import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import ReceiptPrintView from './components/print/ReceiptPrintView';
import AdmissionFormPrintView from './components/print/AdmissionFormPrintView';
import TeacherFormPrintView from './components/print/TeacherFormPrintView';
import { io } from 'socket.io-client';
import api from './services/api';
import QuickEnrollPage from './pages/QuickEnrollPage';
import logo from './logo.jpeg';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
applyPlugin(jsPDF);
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const backendHost = window.location.hostname;
const isHttps = window.location.protocol === 'https:';
const socketHost = isHttps ? `https://${backendHost}` : `http://${backendHost}:8080`;
const socket = io(socketHost, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000
});
const getPhotoUrl = (photo) => {
    if (!photo) return '';
    if (photo.startsWith('http') || photo.startsWith('data:')) return photo;
    if (isHttps) {
        return `https://${backendHost}${photo}`;
    }
    return `http://${backendHost}:8080${photo}`;
};

const compressImage = (base64Str, callback) => {
    const img = new Image();
    img.src = base64Str;
    img.onload = () => {
        const maxDimension = 300;
        let width = img.width;
        let height = img.height;
        if (width > maxDimension || height > maxDimension) {
            if (width > height) {
                height = Math.round((height * maxDimension) / width);
                width = maxDimension;
            } else {
                width = Math.round((width * maxDimension) / height);
                height = maxDimension;
            }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const compressed = canvas.toDataURL('image/jpeg', 0.7);
        callback(compressed);
    };
    img.onerror = () => {
        callback(base64Str);
    };
};

const AuthContext = React.createContext(null);

const formatISTDisplayTime = (timeVal) => {
    if (!timeVal || timeVal === '--:--' || timeVal === '--:-- --' || timeVal === 'N/A' || timeVal === '--') return '--:--';
    try {
        const str = String(timeVal).trim();
        if (str.includes('T') || str.includes('Z') || str.match(/^\d{4}-\d{2}-\d{2}/)) {
            const d = new Date(str);
            if (!isNaN(d.getTime())) {
                return d.toLocaleTimeString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                }).toLowerCase();
            }
        }
        return str;
    } catch (e) {
        return String(timeVal);
    }
};

const getEstimatedHrs = (r) => {
    if (r.workingHours && r.workingHours !== '--') return r.workingHours;
    if (r.firstTime) {
        const start = new Date(r.firstTime);
        const end = r.lastTime ? new Date(r.lastTime) : new Date();
        const diffMs = end.getTime() - start.getTime();
        if (diffMs < 0) return '--';
        const hrs = Math.floor(diffMs / 3600000);
        const mins = Math.floor((diffMs % 3600000) / 60000);
        return `${hrs}h ${mins}m`;
    }
    // Fallback if we have inTime and outTime as strings
    if (r.inTime && r.outTime && r.inTime !== '--:--' && r.outTime !== '--:--' && r.inTime !== '--:--:--' && r.outTime !== '--:--:--') {
        try {
            const parseTimeStr = (str) => {
                const clean = String(str).trim().toLowerCase();
                const parts = clean.match(/(\d+):(\d+):?(\d+)?\s*(am|pm)/i);
                if (!parts) return null;
                let hrs = parseInt(parts[1], 10);
                const mins = parseInt(parts[2], 10);
                const ampm = parts[4].toUpperCase();
                if (ampm === 'PM' && hrs !== 12) hrs += 12;
                if (ampm === 'AM' && hrs === 12) hrs = 0;
                const d = new Date();
                d.setHours(hrs, mins, 0, 0);
                return d;
            };
            const inDate = parseTimeStr(r.inTime);
            const outDate = parseTimeStr(r.outTime);
            if (inDate && outDate) {
                const diffMs = outDate.getTime() - inDate.getTime();
                if (diffMs >= 0) {
                    const hrs = Math.floor(diffMs / 3600000);
                    const mins = Math.floor((diffMs % 3600000) / 60000);
                    return `${hrs}h ${mins}m`;
                }
            }
        } catch (e) { /* ignore */ }
    }
    return '--';
};
// --- LOGIN PAGE ---
const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [deviceIp] = useState('192.168.0.118');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = React.useContext(AuthContext);

    const handleSubmit = async (e) => {
        e.preventDefault(); setError(''); setLoading(true);
        try {
            const res = await api.post('/auth/login', { email: email.trim(), password });
            login(res.data.token, res.data.user);

            // Automatically trigger connection to biometric machine on login
            if (deviceIp) {
                api.post('/devices/connect-ip', { ip: deviceIp, port: '4370' })
                    .then(() => console.log(`🔌 Automatically connected to biometric machine at ${deviceIp}`))
                    .catch((connectErr) => console.error(`❌ Auto-connect failed:`, connectErr.message));
            }
        } catch (err) { setError(err.response?.data?.error || err.response?.data?.message || 'Login failed: Unauthorized email or password'); }
        setLoading(false);
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f0f2f5' }}>
            <div style={{ background: 'white', padding: '40px', borderRadius: '12px', width: '350px', textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h1 style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <img src={logo} alt="BioAttend Logo" style={{ height: '48px', width: '48px', borderRadius: '6px', objectFit: 'cover' }} />
                    BioAttend
                </h1>
                <p style={{ color: '#666', marginBottom: '20px' }}>Admin Login</p>
                <form onSubmit={handleSubmit}>
                    <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} required />
                    <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: '100%', padding: '12px', marginBottom: '15px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' }} required />
                    {error && <div style={{ color: '#e74c3c', fontSize: '14px', marginBottom: '10px' }}>{error}</div>}
                    <button type="submit" style={{ width: '100%', padding: '12px', borderRadius: '6px', border: 'none', background: '#667eea', color: 'white', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }} disabled={loading}>
                        {loading ? 'Logging in...' : 'Login'}
                    </button>
                </form>
            </div >
        </div >
    );
};

// --- PROTECTED ROUTE ---
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated } = React.useContext(AuthContext);
    const location = useLocation();
    if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
    return children;
};

// --- SIDEBAR & LAYOUT ---
const Sidebar = ({ logout, user, isOpen, onClose }) => {

    const [admOpen, setAdmOpen] = useState(false);
    const [acadOpen, setAcadOpen] = useState(false);
    const [finOpen, setFinOpen] = useState(false);
    const [expSubOpen, setExpSubOpen] = useState(false);
    return (
        <div className={`sidebar-container ${isOpen ? 'open' : ''}`}>
            <button className="sidebar-close-btn" onClick={onClose} aria-label="Close Sidebar">✕</button>
            <h2 style={{ color: 'white', padding: '20px', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img src={logo} alt="BioAttend Logo" style={{ height: '42px', width: '42px', borderRadius: '4px', objectFit: 'cover' }} />
                BioAttend
            </h2>
            <div style={{ padding: '0 20px 20px', color: '#bdc3c7', fontSize: '14px' }}>Welcome, <strong style={{ color: '#ffffff' }}>{user?.name}</strong></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li><Link to="/" style={{ display: 'block', padding: '15px 20px', color: '#ffffff', textDecoration: 'none', borderBottom: '1px solid #34495e', fontWeight: '500', fontSize: '15px' }} onClick={onClose}>📈 Dashboard</Link></li>



                {/* Admissions Dropdown */}
                <li>
                    <div onClick={() => setAdmOpen(!admOpen)} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 20px', color: '#ffffff', cursor: 'pointer', borderBottom: '1px solid #34495e', background: admOpen ? '#34495e' : 'transparent', fontWeight: '500', fontSize: '15px' }}>
                        <span>📝 Admissions</span>
                        <span>{admOpen ? '▲' : '▼'}</span>
                    </div>
                    {admOpen && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, background: '#1a252f' }}>
                            <li><Link to="/enroll-student" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Enroll Student</Link></li>
                            <li><Link to="/quick-enroll" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>⚡ Quick Enroll</Link></li>
                            <li><Link to="/bulk-upload" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Bulk Upload</Link></li>
                            <li><Link to="/admissions" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Student List</Link></li>
                            <li><Link to="/birthdays" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Birthday Reminders</Link></li>
                        </ul>
                    )}
                </li>


                {/* Finances Dropdown */}
                <li>
                    <div onClick={() => setFinOpen(!finOpen)} style={{ display: 'flex', justifyContent: 'space-between', padding: '15px 20px', color: '#ffffff', cursor: 'pointer', borderBottom: '1px solid #34495e', background: finOpen ? '#34495e' : 'transparent', fontWeight: '500', fontSize: '15px' }}>
                        <span>💰 Finances</span>
                        <span>{finOpen ? '▲' : '▼'}</span>
                    </div>
                    {finOpen && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, background: '#1a252f' }}>
                            <li><Link to="/finances/cleared-fees" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Cleared Fees</Link></li>
                            <li><Link to="/finances/income" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Income</Link></li>
                            <li><Link to="/finances/salary" style={{ display: 'block', padding: '12px 20px 12px 40px', color: '#ecf0f1', textDecoration: 'none', fontSize: '14px', borderBottom: '1px solid #2c3e50', fontWeight: '400' }} onClick={onClose}>○ Salary</Link></li>
                            <li>
                                <div onClick={() => setExpSubOpen(!expSubOpen)} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 20px 12px 40px', color: '#ecf0f1', cursor: 'pointer', borderBottom: '1px solid #2c3e50', fontSize: '14px', fontWeight: '400' }}>
                                    <span>○ Expenses</span>
                                    <span>{expSubOpen ? '▲' : '▼'}</span>
                                </div>
                                {expSubOpen && (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, background: '#111b24' }}>
                                        <li><Link to="/finances/daily-expenses" style={{ display: 'block', padding: '10px 20px 10px 60px', color: '#bdc3c7', textDecoration: 'none', fontSize: '13px', borderBottom: '1px solid #2c3e50' }} onClick={onClose}>○ Daily Expenses</Link></li>
                                        <li><Link to="/finances/monthly-expenses" style={{ display: 'block', padding: '10px 20px 10px 60px', color: '#bdc3c7', textDecoration: 'none', fontSize: '13px', borderBottom: '1px solid #2c3e50' }} onClick={onClose}>○ Monthly Expenses</Link></li>
                                    </ul>
                                )}
                            </li>
                        </ul>
                    )}
                </li>

                <li><Link to="/attendance" style={{ display: 'block', padding: '15px 20px', color: '#ffffff', textDecoration: 'none', borderBottom: '1px solid #34495e', fontWeight: '500', fontSize: '15px' }} onClick={onClose}>📋 Attendance</Link></li>
                <li><Link to="/teachers" style={{ display: 'block', padding: '15px 20px', color: '#ffffff', textDecoration: 'none', borderBottom: '1px solid #34495e', fontWeight: '500', fontSize: '15px' }} onClick={onClose}>👨‍🏫 Teachers & Staff</Link></li>
                <li><Link to="/devices" style={{ display: 'block', padding: '15px 20px', color: '#ffffff', textDecoration: 'none', borderBottom: '1px solid #34495e', fontWeight: '500', fontSize: '15px' }} onClick={onClose}>🔒 Devices</Link></li>
                <li style={{ padding: '15px 20px' }}>
                    <button onClick={logout} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '15px', fontWeight: '500', background: '#e74c3c', color: 'white', width: '100%' }}>🚪 Logout</button>
                </li>
            </ul >
        </div >
    );
};

const LiveTimeBadge = () => {
    const [time, setTime] = useState(new Date().toLocaleTimeString());

    useEffect(() => {
        const timer = setInterval(() => {
            setTime(new Date().toLocaleTimeString());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="header-badge">
            <div className="badge-icon-wrapper badge-icon-live">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
            </div>
            <div className="badge-info">
                <span className="badge-label live-indicator">
                    <span className="pulse-dot"></span> Live
                </span>
                <span className="badge-value">{time}</span>
            </div>
        </div>
    );
};

const Layout = ({ children, logout, user }) => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const closeSidebar = () => setIsSidebarOpen(false);
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

    return (
        <div className="app-container">
            <div className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} onClick={closeSidebar}></div>
            <Sidebar logout={logout} user={user} isOpen={isSidebarOpen} onClose={closeSidebar} />
            <div className="main-content-container">
                <div className="header">
                    <div>
                        <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <button className="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle navigation">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="4" x2="20" y1="12" y2="12" />
                                    <line x1="4" x2="20" y1="6" y2="6" />
                                    <line x1="4" x2="20" y1="18" y2="18" />
                                </svg>
                            </button>
                            <img src={logo} alt="BioAttend Logo" style={{ height: '48px', width: '48px', borderRadius: '6px', objectFit: 'cover' }} />
                            BioAttend Pro
                        </h1>
                        <span style={{ fontSize: '14px', opacity: 0.8 }}>Biometric Attendance Management System</span>
                    </div>
                    <div className="header-actions">
                        <div className="header-badge clickable" onClick={logout} title="Click to Logout">
                            <div className="badge-icon-wrapper badge-icon-user">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </div>
                            <div className="badge-info">
                                <span className="badge-label">User</span>
                                <span className="badge-value">{user?.name || 'Admin User'}</span>
                            </div>
                        </div>

                        <div className="header-badge">
                            <div className="badge-icon-wrapper badge-icon-date">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect width="18" height="18" x="3" y="4" rx="2" ry="2" />
                                    <line x1="16" x2="16" y1="2" y2="6" />
                                    <line x1="8" x2="8" y1="2" y2="6" />
                                    <line x1="3" x2="21" y1="10" y2="10" />
                                </svg>
                            </div>
                            <div className="badge-info">
                                <span className="badge-label">Date</span>
                                <span className="badge-value">
                                    {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                            </div>
                        </div>

                        <LiveTimeBadge />
                    </div>
                </div>
                {children}
            </div>
        </div>
    );
};


// --- PAGES ---
const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [recent, setRecent] = useState([]);
    const [livePunches, setLivePunches] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [smsLogs, setSmsLogs] = useState([]);

    const [attendanceTab, setAttendanceTab] = useState('all');

    const getFilteredRecent = () => {
        if (attendanceTab === 'all') return recent;
        if (attendanceTab === 'late') return recent.filter(r => r.status && r.status.toLowerCase() === 'late');
        return recent.filter(r => r.role && r.role.toLowerCase() === attendanceTab);
    };
    const filteredRecent = getFilteredRecent();

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredRecent.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredRecent.length / itemsPerPage);

    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);

    const [lastScanToast, setLastScanToast] = useState(null);
    const toastRef = React.useRef(null);

    const [editAttendance, setEditAttendance] = useState(null);

    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [scheduleModalMode, setScheduleModalMode] = useState('view'); // 'view' or 'edit'
    const [editingSchedule, setEditingSchedule] = useState(null);

    const [formTeacherName, setFormTeacherName] = useState('');
    const [formSubject, setFormSubject] = useState('');
    const [formClassRoom, setFormClassRoom] = useState('');
    const [formTimeStart, setFormTimeStart] = useState('');
    const [formTimeEnd, setFormTimeEnd] = useState('');
    const [formStatus, setFormStatus] = useState('Scheduled');

    const handleSaveSchedule = async (e) => {
        if (e) e.preventDefault();
        if (!formTeacherName.trim() || !formSubject.trim() || !formClassRoom.trim() || !formTimeStart.trim() || !formTimeEnd.trim()) {
            alert('Please fill out all required fields');
            return;
        }

        const payload = {
            teacherName: formTeacherName,
            subject: formSubject,
            classRoom: formClassRoom,
            timeStart: formTimeStart,
            timeEnd: formTimeEnd,
            status: formStatus
        };

        try {
            if (editingSchedule) {
                const res = await api.put(`/schedules/${editingSchedule._id}`, payload);
                setSchedules(prev => prev.map(s => s._id === editingSchedule._id ? res.data : s));
            } else {
                const res = await api.post('/schedules', payload);
                setSchedules(prev => [...prev, res.data]);
            }
            setEditingSchedule(null);
            setScheduleModalMode('view');
        } catch (err) {
            alert('Failed to save schedule');
        }
    };

    const handleDeleteSchedule = async (id) => {
        if (!window.confirm('Are you sure you want to delete this schedule slot?')) return;
        try {
            await api.delete(`/schedules/${id}`);
            setSchedules(prev => prev.filter(s => s._id !== id));
        } catch (err) {
            alert('Failed to delete schedule');
        }
    };

    const handleOpenEdit = (sched) => {
        setEditingSchedule(sched);
        setFormTeacherName(sched.teacherName);
        setFormSubject(sched.subject);
        setFormClassRoom(sched.classRoom);
        setFormTimeStart(sched.timeStart);
        setFormTimeEnd(sched.timeEnd);
        setFormStatus(sched.status);
        setScheduleModalMode('edit');
    };

    const handleOpenAdd = () => {
        setEditingSchedule(null);
        setFormTeacherName('');
        setFormSubject('');
        setFormClassRoom('');
        setFormTimeStart('09:00 AM');
        setFormTimeEnd('10:30 AM');
        setFormStatus('Scheduled');
        setScheduleModalMode('edit');
    };

    const handleSaveAttendance = async () => {
        try {
            await api.put('/attendance/update', {
                userId: editAttendance.userId,
                date: new Date().toISOString().split('T')[0], // Assuming today for live dashboard
                inTime: editAttendance.inTime,
                outTime: editAttendance.outTime
            });
            // Reload dashboard stats
            const res = await api.get('/admin/dashboard');
            setRecent(res.data.recentAttendance);
            setEditAttendance(null);
        } catch (err) {
            alert('Failed to update attendance');
        }
    };

    const handleExportAttendance = () => {
        if (filteredRecent.length === 0) {
            alert("No attendance records to export");
            return;
        }
        const exportData = filteredRecent.map(r => ({
            "Name": r.name,
            "Role": r.role,
            "In Time": r.inTime,
            "Out Time": r.outTime,
            "Estimated Hours": getEstimatedHrs(r),
            "Status": r.status
        }));
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Today Attendance");
        XLSX.writeFile(workbook, `Today_Attendance_${attendanceTab}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleExport6MonthsAttendance = () => {
        const downloadUrl = `${api.defaults.baseURL}/attendance/export-6-months`;
        window.open(downloadUrl, '_blank');
    };

    const handleRefreshDashboard = async () => {
        try {
            const res = await api.get('/admin/dashboard');
            setStats(res.data.stats);
            setRecent(res.data.recentAttendance);
            if (res.data.livePunches) {
                setLivePunches(res.data.livePunches);
            }
        } catch (err) {
            console.error("Refresh failed:", err);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [attendanceTab]);

    useEffect(() => {
        setDrafts(JSON.parse(localStorage.getItem('admission_drafts') || '[]'));
        Promise.all([
            api.get('/users').catch(() => ({ data: [] })),
            api.get('/teachers').catch(() => ({ data: [] }))
        ]).then(([studRes, teachRes]) => {
            setUsers([...(studRes.data || []), ...(teachRes.data || [])]);
        });
        const fetchDashboard = () => {
            api.get('/admin/dashboard').then(res => {
                if (res.data) {
                    setStats(res.data.stats);
                    setRecent(res.data.recentAttendance);
                    if (res.data.livePunches && res.data.livePunches.length > 0) {
                        setLivePunches(res.data.livePunches);
                    }
                }
            }).catch(() => {});
        };

        const fetchSmsLogs = () => {
            api.get('/sms-logs').then(res => {
                if (Array.isArray(res.data)) {
                    setSmsLogs(res.data);
                }
            }).catch(() => {});
        };

        fetchDashboard();
        fetchSmsLogs();
        // Auto-refresh live dashboard every 3 seconds so live time & punches are ALWAYS up to date automatically
        const dashboardInterval = setInterval(fetchDashboard, 3000);

        // Setup live punch socket listener

        const handlePunch = (data) => {
            const formatTime = (d) => {
                if (!d) return '--:-- --';
                try {
                    const dateObj = new Date(d);
                    if (isNaN(dateObj.getTime())) return String(d);
                    return dateObj.toLocaleTimeString('en-IN', {
                        timeZone: 'Asia/Kolkata',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                    }).toLowerCase();
                } catch (e) {
                    return String(d);
                }
            };
            const punchTime = data.time || (data.timestamp ? formatTime(data.timestamp) : formatTime(Date.now()));
            const entry = {
                id: data.id || (data.timestamp ? new Date(data.timestamp).getTime() : Date.now()),
                userId: data.userId,
                userName: data.userName || `User ${data.userId}`,
                userPhoto: data.userPhoto || null,
                time: punchTime,
                deviceSn: data.deviceSn || 'Bio System',
                verifyMode: data.verifyMode || '1',
                timestamp: data.timestamp
            };

            setLivePunches(prev => [entry, ...prev].slice(0, 10));

            setRecent(prev => {
                const updated = [...prev];
                const existingIndex = updated.findIndex(r => r.name === data.userName);
                const pTime = data.time || (data.timestamp ? formatTime(data.timestamp) : formatTime(Date.now()));

                if (existingIndex > -1) {
                    updated[existingIndex] = { ...updated[existingIndex], outTime: pTime, lastTime: data.timestamp };
                    // Move to top
                    const item = updated.splice(existingIndex, 1)[0];
                    updated.unshift(item);
                } else {
                    updated.unshift({
                        name: data.userName || `User ${data.userId}`,
                        role: data.role || 'student',
                        photo: data.userPhoto || null,
                        inTime: pTime,
                        outTime: '--:-- --',
                        status: data.status || 'Present',
                        firstTime: data.timestamp,
                        lastTime: null
                    });
                }
                return updated;
            });

            setLastScanToast(entry);
            if (toastRef.current) clearTimeout(toastRef.current);
            toastRef.current = setTimeout(() => setLastScanToast(null), 5000);
        };

        const handlePunchPhoto = (data) => {
            setLivePunches(prev => prev.map(p => {
                if (String(p.userId) === String(data.userId)) {
                    return { ...p, userPhoto: data.userPhoto };
                }
                return p;
            }));

            setRecent(prev => prev.map(r => {
                if (String(r.userId) === String(data.userId) || r.name === data.userName) {
                    return { ...r, photo: data.userPhoto };
                }
                return r;
            }));

            setLastScanToast(prev => {
                if (prev && String(prev.userId) === String(data.userId)) {
                    return { ...prev, userPhoto: data.userPhoto };
                }
                return prev;
            });

            setUsers(prev => prev.map(u => {
                if (String(u.id) === String(data.userId) || String(u.fingerprint_id) === String(data.userId)) {
                    return { ...u, photo: data.userPhoto };
                }
                return u;
            }));
        };

        const handleSmsLogUpdate = (newLog) => {
            setSmsLogs(prev => [newLog, ...prev].slice(0, 50));
        };

        socket.on('live_punch', handlePunch);
        socket.on('live_punch_photo', handlePunchPhoto);
        socket.on('sms_log_update', handleSmsLogUpdate);
        return () => {
            socket.off('live_punch', handlePunch);
            socket.off('live_punch_photo', handlePunchPhoto);
            socket.off('sms_log_update', handleSmsLogUpdate);
            clearInterval(dashboardInterval);
            if (toastRef.current) clearTimeout(toastRef.current);
        };
    }, []);

    if (!stats) return <div>Loading dashboard...</div>;

    const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '??';

    const getStatusBadge = (status) => {
        const s = (status || 'Scheduled').toLowerCase();

        let label = 'Scheduled';
        let bg = '#e1effe';
        let color = '#1e429f';

        if (s.includes('completed')) {
            label = 'Completed';
            bg = '#def7ec';
            color = '#03543f';
        } else if (s.includes('absent')) {
            label = 'Absent';
            bg = '#fef3c7';
            color = '#92400e';
        } else if (s.includes('cancelled') || s.includes('canceled')) {
            label = 'Cancelled';
            bg = '#fde8e8';
            color = '#9b1c1c';
        }

        return (
            <span style={{
                display: 'inline-block',
                padding: '3px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                fontWeight: '700',
                textTransform: 'uppercase',
                backgroundColor: bg,
                color: color,
                flexShrink: 0
            }}>
                {label}
            </span>
        );
    };

    const resolvedName = (punch) => {
        if (!punch) return 'Unknown';
        const punchId = String(punch.userId || punch.id || '').trim();
        let user = users.find(u => String(u.fingerprint_id).trim() === punchId);
        if (!user) user = users.find(u => String(u.id).trim() === punchId);
        return user ? user.name : (punch.userName || `User ${punchId}`);
    };

    const resolvedPhoto = (punch) => {
        if (!punch) return null;
        let photo = punch.userPhoto || null;
        const punchId = String(punch.userId || punch.id || '').trim();
        if (!photo && punchId) {
            let user = users.find(u => String(u.fingerprint_id).trim() === punchId);
            if (!user) user = users.find(u => String(u.id).trim() === punchId);
            photo = user ? user.photo : null;
        }
        if (photo && typeof photo === 'string' && photo.trim() !== '' && photo !== 'undefined' && photo !== 'null') {
            if (photo.startsWith('http') || photo.startsWith('data:')) return photo;
            return getPhotoUrl(photo);
        }
        return null;
    };

    const isFaceScan = (punch) => String(punch.verifyMode) === '15';

    const getUpcomingBirthdays = () => {
        const today = new Date();
        const uniqueUsers = [];
        const seenNames = new Set();
        users.forEach(u => {
            if (u.name && !seenNames.has(u.name.trim().toLowerCase())) {
                seenNames.add(u.name.trim().toLowerCase());
                uniqueUsers.push(u);
            }
        });

        const upcoming = uniqueUsers.filter(u => {
            if (!u.dob) return false;
            const dobDate = new Date(u.dob);
            if (isNaN(dobDate)) return false;

            const bdayThisYear = new Date(today.getFullYear(), dobDate.getMonth(), dobDate.getDate());
            let diffTime = bdayThisYear.getTime() - today.getTime();

            if (diffTime < -86400000) {
                bdayThisYear.setFullYear(today.getFullYear() + 1);
                diffTime = bdayThisYear.getTime() - today.getTime();
            }
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays >= 0 && diffDays <= 30;
        }).map(u => {
            const dobDate = new Date(u.dob);
            const bdayThisYear = new Date(today.getFullYear(), dobDate.getMonth(), dobDate.getDate());
            let diffTime = bdayThisYear.getTime() - today.getTime();
            if (diffTime < -86400000) {
                bdayThisYear.setFullYear(today.getFullYear() + 1);
            }
            const diffDays = Math.ceil((bdayThisYear.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
            const age = bdayThisYear.getFullYear() - dobDate.getFullYear();
            return { ...u, diffDays, age };
        }).sort((a, b) => a.diffDays - b.diffDays);
        return upcoming;
    };

    const upcomingBirthdays = getUpcomingBirthdays();

    return (
        <div>
            {/* LIVE TOAST */}
            {lastScanToast && (
                <div style={{
                    position: 'fixed', top: '20px', right: '20px', zIndex: 9999,
                    background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)',
                    color: 'white', borderRadius: '16px', padding: '18px 22px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)', minWidth: '300px',
                    border: '1px solid rgba(102,126,234,0.5)',
                    animation: 'toastIn 0.4s cubic-bezier(0.175,0.885,0.32,1.275)',
                    display: 'flex', alignItems: 'center', gap: '14px'
                }}>
                    <div style={{
                        width: '46px', height: '46px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0,
                        overflow: 'hidden'
                    }}>
                        {resolvedPhoto(lastScanToast) ? (
                            <img src={resolvedPhoto(lastScanToast)} onError={(e) => { e.target.style.display = 'none'; }} alt="Face" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            isFaceScan(lastScanToast) ? '👤' : '👆'
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '10px', color: '#a0a8d0', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '3px' }}>
                            🔴 {isFaceScan(lastScanToast) ? 'LIVE FACE SCAN' : 'LIVE FINGERPRINT SCAN'}
                        </div>
                        <div style={{ fontSize: '17px', fontWeight: 800 }}>{resolvedName(lastScanToast)}</div>
                        <div style={{ fontSize: '12px', color: '#8892b0', marginTop: '2px' }}>
                            📟 {lastScanToast.deviceSn} &nbsp;•&nbsp; 🕐 {lastScanToast.time}
                        </div>
                        {lastScanToast.hasMissingPhone && (
                            <div style={{
                                marginTop: '6px',
                                background: '#fee2e2',
                                border: '1px solid #f87171',
                                color: '#991b1b',
                                fontSize: '11px',
                                fontWeight: 700,
                                padding: '4px 8px',
                                borderRadius: '6px',
                                display: 'inline-block'
                            }}>
                                ⚠️ Mobile number missing — Attendance SMS not sent!
                            </div>
                        )}
                    </div>
                    <button onClick={() => setLastScanToast(null)} style={{ background: 'none', border: 'none', color: '#8892b0', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                </div>
            )}

            <style>{`
        @keyframes toastIn { from { transform: translateX(110%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes punchSlide { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulseDot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.6)} }
        .live-dot-red { width:10px;height:10px;border-radius:50%;background:#e74c3c;animation:pulseDot 1.2s infinite;display:inline-block;margin-right:8px; }
        .live-dot-green { width:10px;height:10px;border-radius:50%;background:#27ae60;animation:pulseDot 1.5s infinite;display:inline-block;margin-right:6px; }
        .punch-row { animation: punchSlide 0.35s ease-out; }
      `}</style>

            <div className="stats-grid">
                <div className="stat-card">
                    <h3>👥 Active Users</h3>
                    <div className="number">{stats.totalUsers}</div>
                    <div className="change">
                        {stats.newUsersToday > 0 ? `↑ ${stats.newUsersToday} new today` : '0 new today'}
                    </div>
                </div>
                <div className="stat-card">
                    <h3>✅ Today's Live Attendance</h3>
                    <div className="number" style={{ color: '#27ae60' }}>
                        {stats.presentCount || 0} / {stats.totalUsers || 0}
                    </div>
                    <div className="change">{stats.todayAttend}% attendance rate</div>
                </div>
                <div className="stat-card pending" style={{ cursor: 'pointer' }} onClick={() => {
                    const admissionEl = document.querySelector('.widget-sidebar');
                    if (admissionEl) admissionEl.scrollIntoView({ behavior: 'smooth' });
                }}>
                    <h3>📝 Incomplete Forms</h3>
                    <div className="number">{drafts.length}</div>
                    <div className="change">⏳ Saved drafts</div>
                </div>
                <div className="stat-card alert">
                    <h3>⚡ Active Biometrics</h3>
                    <div className="number">
                        {stats.activeDevices || 0}/{stats.totalDevices || 0}
                    </div>
                    <div className="change" style={{ color: stats.activeDevices === stats.totalDevices && stats.totalDevices > 0 ? '#27ae60' : '#ef4444' }}>
                        {stats.activeDevices === stats.totalDevices && stats.totalDevices > 0 ? '✅ All online' : (stats.totalDevices === 0 ? '❌ No devices' : '⚠️ Connection issue')}
                    </div>
                </div>
            </div>

            {/* LIVE SYNC FEED - FULL WIDTH */}
            <div className="dashboard-card" style={{ marginBottom: '20px' }}>
                <div className="card-header">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={livePunches.length > 0 ? 'live-dot-red' : 'live-dot-green'}></span>
                        Live Sync Feed
                        <span style={{
                            fontSize: '11px', fontWeight: 700, padding: '3px 12px',
                            background: livePunches.length > 0 ? '#e74c3c' : '#27ae60',
                            color: 'white', borderRadius: '20px', letterSpacing: '0.5px'
                        }}>{livePunches.length > 0 ? `${livePunches.length} SCANS TODAY` : 'WAITING...'}</span>
                    </h2>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Link to="/devices" className="btn" style={{ background: '#8b5cf6', color: 'white', fontSize: '13px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>🔌 Connect Machine via IP</Link>
                        <button className="btn" style={{ background: '#eee', fontSize: '13px' }} onClick={() => setLivePunches([])}>🗑 Clear</button>
                    </div>
                </div>

                {livePunches.length === 0 ? (
                    <div style={{
                        padding: '40px 20px', textAlign: 'center',
                        background: 'linear-gradient(135deg, #f8f9ff, #f0f4ff)',
                        borderRadius: '10px', margin: '0'
                    }}>
                        <div style={{ fontSize: '42px', marginBottom: '12px' }}>👆</div>
                        <div style={{ fontSize: '17px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Waiting for fingerprint scan...</div>
                        <div style={{ fontSize: '13px', color: '#888' }}>
                            Scan your finger on <strong>Bio System (x 2006)</strong> • Scans appear here in real time
                        </div>
                        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                            <span className="live-dot-green"></span>
                            <span style={{ fontSize: '13px', color: '#27ae60', fontWeight: 600 }}>Socket connected → {backendHost}:3000</span>
                        </div >
                    </div >
                ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '4px 0' }}>
                        {livePunches.map((punch, idx) => (
                            <div key={punch.id || idx} className="punch-row" style={{
                                flex: '1 1 220px', maxWidth: '260px',
                                background: idx === 0
                                    ? 'linear-gradient(135deg, #667eea15, #764ba215)'
                                    : '#f8f9fa',
                                border: idx === 0 ? '2px solid #667eea60' : '1px solid #eee',
                                borderRadius: '12px', padding: '14px', position: 'relative'
                            }}>
                                {idx === 0 && (
                                    <div style={{
                                        position: 'absolute', top: '8px', right: '8px',
                                        background: '#667eea', color: 'white',
                                        fontSize: '9px', fontWeight: 800, padding: '2px 7px',
                                        borderRadius: '20px', letterSpacing: '1px'
                                    }}>NEW</div>
                                )}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                                        background: idx === 0 ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#bdc3c7',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: 'white', fontWeight: 800, fontSize: '14px', overflow: 'hidden'
                                    }}>
                                        {resolvedPhoto(punch) ? (
                                            <img src={resolvedPhoto(punch)} onError={(e) => { e.target.style.display = 'none'; }} alt="Face" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            isFaceScan(punch) ? '👤' : '👆'
                                        )}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '2px' }}>{resolvedName(punch)}</div>
                                        <div style={{ fontSize: '11px', color: '#666' }}>ID: {punch.userId}</div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>🕐 {punch.time || punch.timestamp}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div >

            <div className="main-grid">
                <div className="dashboard-card">
                    <div className="card-header">
                        <h2>📋 Live Attendance - Today</h2>
                        <div className="actions">
                            <button className="btn btn-primary" onClick={handleRefreshDashboard}>🔄 Refresh</button>
                            <button className="btn btn-success" onClick={handleExportAttendance}>📥 Export</button>
                            <button className="btn" style={{ background: '#8b5cf6', color: 'white' }} onClick={handleExport6MonthsAttendance}>📥 Export 6 Months</button>
                        </div>
                    </div>

                    <div className="tabs">
                        <span className={`tab ${attendanceTab === 'all' ? 'active' : ''}`} onClick={() => setAttendanceTab('all')}>All</span>
                        <span className={`tab ${attendanceTab === 'student' ? 'active' : ''}`} onClick={() => setAttendanceTab('student')}>Students</span>
                        <span className={`tab ${attendanceTab === 'teacher' ? 'active' : ''}`} onClick={() => setAttendanceTab('teacher')}>Teachers</span>
                        <span className={`tab ${attendanceTab === 'staff' ? 'active' : ''}`} onClick={() => setAttendanceTab('staff')}>Staff</span>
                        <span className={`tab ${attendanceTab === 'late' ? 'active' : ''}`} onClick={() => setAttendanceTab('late')}>Late</span>
                    </div>

                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>User</th>
                                <th>Role</th>
                                <th>In Time</th>
                                <th>Out Time</th>
                                <th>Estimated Hrs</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody id="attendanceTable">
                            {recent.length === 0 ? (
                                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No attendance records yet today. Waiting for machine sync...</td></tr>
                            ) : currentItems.map((r, i) => (
                                <tr key={i}>
                                    <td>
                                        <div
                                            style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                                            onClick={() => navigate(`/${(r.role || 'student').toLowerCase() === 'teacher' || (r.role || 'student').toLowerCase() === 'staff' ? 'teacher' : 'student'}/${r.userId || r.id || r.fingerprint_id}`)}
                                        >
                                            {r.photo ? (
                                                <img src={getPhotoUrl(r.photo)} alt="Face" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                                            ) : (
                                                <div className="avatar" style={{ background: '#bdc3c7' }}>{r.name.charAt(0)}</div>
                                            )}
                                            <span style={{ fontWeight: '600', color: '#3b82f6', textDecoration: 'underline' }}>{r.name}</span>
                                        </div>
                                    </td>
                                    <td style={{ textTransform: 'capitalize' }}>{r.role}</td>
                                    <td>{formatISTDisplayTime(r.inTime)}</td>
                                    <td>{formatISTDisplayTime(r.outTime)}</td>
                                    <td>{getEstimatedHrs(r)}</td>
                                    <td><span className="status present">{r.status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#666', alignItems: 'center' }}>
                        <span>
                            Showing {recent.length === 0 ? 0 : indexOfFirstItem + 1} to {Math.min(indexOfLastItem, recent.length)} of {recent.length} entries
                        </span>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                                className="btn"
                                style={{ background: currentPage === 1 ? '#f5f5f5' : '#eee', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            >
                                Previous
                            </button>
                            {Array.from({ length: totalPages }, (_, idx) => idx + 1).map(pageNumber => (
                                <button
                                    key={pageNumber}
                                    className={`btn ${currentPage === pageNumber ? 'btn-primary' : ''}`}
                                    style={{ background: currentPage === pageNumber ? '' : '#eee', cursor: 'pointer' }}
                                    onClick={() => setCurrentPage(pageNumber)}
                                >
                                    {pageNumber}
                                </button>
                            ))}
                            <button
                                className="btn"
                                style={{ background: currentPage === totalPages || totalPages === 0 ? '#f5f5f5' : '#eee', cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer' }}
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>

                <div className="widget-sidebar">
                    <div className="dashboard-card">
                        <div className="card-header">
                            <h2>📝 Admission Forms</h2>
                            <button className="btn btn-primary" style={{ fontSize: '12px', padding: '4px 12px' }} onClick={() => navigate('/enroll-student')}>+ New</button>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {drafts.length === 0 ? (
                                <div style={{ padding: '20px 10px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                                    No incomplete/draft forms.
                                </div>
                            ) : (
                                drafts.map((d) => (
                                    <div
                                        key={d.id}
                                        className="admission-item"
                                        onClick={() => navigate(`/enroll-student?draftId=${d.id}`)}
                                        style={{ cursor: 'pointer', transition: 'background 0.2s', padding: '8px', borderBottom: '1px solid #f1f5f9' }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <div>
                                            <div className="name" style={{ fontWeight: 600 }}>{d.formData.name || 'Untitled Draft'}</div>
                                            <div className="class" style={{ fontSize: '11px', color: '#666' }}>
                                                {d.formData.course || 'No Course'} • Step {d.step}/4 • Saved {new Date(d.updatedAt).toLocaleDateString()}
                                            </div>
                                        </div>
                                        <span className="status pending" style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>Incomplete</span>
                                    </div>
                                ))
                            )}
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '13px', color: '#666' }}>
                            <span>📊 {drafts.length} incomplete drafts saved</span>
                        </div>
                    </div>

                    <div className="dashboard-card">
                        <h2 style={{ fontSize: '18px', marginBottom: '14px' }}>🔒 Biometric Devices</h2>
                        <div className="biometric-status">
                            <span className="dot online"></span>
                            <div>
                                <strong>Bio System (x 2006)</strong>
                                <div style={{ fontSize: '12px', color: '#666' }}>SN: NYU7260401606</div>
                                <div style={{ fontSize: '11px', color: '#27ae60' }}>Last sync: just now</div>
                            </div>
                            <span style={{ marginLeft: 'auto', color: '#27ae60', fontWeight: 600 }}>✓ Online</span>
                        </div>
                    </div>


                    <div className="dashboard-card" style={{ marginTop: '20px', background: 'linear-gradient(135deg, #fff, #fff9fb)', border: '1px solid #ffe3ec' }}>
                        <h2 style={{ fontSize: '18px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>🎉 Upcoming Birthdays</h2>

                        {upcomingBirthdays.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                                No birthdays in the next 30 days.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {upcomingBirthdays.map(u => (
                                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'white', padding: '10px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(255,192,203,0.2)', border: '1px solid #ffe3ec' }}>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 99%, #fecfef 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold', fontSize: '16px' }}>
                                            {getInitials(u.name)}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>{u.name}</div>
                                            <div style={{ fontSize: '11px', color: '#e84393' }}>
                                                {u.diffDays === 0 ? '🎂 TODAY! Turning ' + u.age : `In ${u.diffDays} days (${new Date(u.dob).toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })})`} • <span style={{ textTransform: 'capitalize' }}>{u.role}</span>
                                            </div>
                                        </div>
                                        <button style={{ background: '#e84393', border: 'none', color: 'white', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}>Wish</button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="dashboard-card" style={{ marginTop: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h2 style={{ fontSize: '18px', margin: 0 }}>📨 Live SMS Logs</h2>
                            <span className="badge" style={{ backgroundColor: '#e1effe', color: '#1e429f', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>
                                {smsLogs.length} Sent Today
                            </span>
                        </div>

                        {smsLogs.length === 0 ? (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                                No recent SMS notifications sent today.
                            </div>
                        ) : (
                            <div style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {smsLogs.map((log, idx) => (
                                    <div key={idx} style={{
                                        padding: '10px 12px',
                                        borderRadius: '8px',
                                        backgroundColor: '#f8fafc',
                                        borderLeft: `4px solid ${log.status === 'SENT' ? '#10b981' : '#ef4444'}`,
                                        fontSize: '12px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '600', marginBottom: '3px' }}>
                                            <span>{log.name} ({log.recipientType === 'parent' ? 'Parent' : 'Student'})</span>
                                            <span style={{ color: log.status === 'SENT' ? '#059669' : '#dc2626', fontSize: '11px' }}>
                                                {log.status === 'SENT' ? '✓ SENT' : '✕ FAILED'}
                                            </span>
                                        </div>
                                        <div style={{ color: '#475569', fontSize: '11px', marginBottom: '4px' }}>
                                            📞 {log.phone} | 📍 {log.direction} | 🕒 {log.createdAt ? new Date(log.createdAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }) : 'Just now'}
                                        </div>
                                        {log.message && (
                                            <div style={{ color: '#64748b', fontSize: '10px', fontStyle: 'italic', background: '#ffffff', padding: '4px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                                                "{log.message}"
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={{ marginTop: '10px' }}>
                            <button className="btn btn-primary" style={{ width: '100%' }}>📱 Send Manual SMS</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="dashboard-card" style={{ marginTop: '20px' }}>
                <div className="card-header">
                    <h2>📅 Today's Teacher Schedule</h2>
                    <div className="actions">
                        <button className="btn btn-warning" onClick={() => { setShowScheduleModal(true); setScheduleModalMode('edit'); handleOpenAdd(); }}>Edit Schedule</button>
                        <button className="btn btn-primary" onClick={() => { setShowScheduleModal(true); setScheduleModalMode('view'); }}>View Full</button>
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                    {(() => {
                        const liveTeacherSchedules = recent
                            .filter(r => r.role === 'teacher' && r.inTime && r.inTime !== '--:--')
                            .map(r => {
                                const teacherInfo = users.find(u => u.name === r.name) || {};
                                const isCompleted = r.outTime && r.outTime !== '--:--' && r.outTime !== '--:-- --';

                                let currentStatus = isCompleted ? 'Completed' : 'Live';
                                const regStartStr = teacherInfo.timing ? teacherInfo.timing.split('-')[0]?.trim() : '';
                                if (!isCompleted && regStartStr && r.inTime && r.inTime !== '--:--') {
                                    const parseT = (ts) => {
                                        const m = ts.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
                                        if (!m) return 0;
                                        let h = parseInt(m[1]), min = parseInt(m[2]);
                                        if (m[3] && m[3].toUpperCase() === 'PM' && h !== 12) h += 12;
                                        if (m[3] && m[3].toUpperCase() === 'AM' && h === 12) h = 0;
                                        return h * 60 + min;
                                    };
                                    const expM = parseT(regStartStr);
                                    const actM = parseT(r.inTime);
                                    if (actM > expM) {
                                        currentStatus = `Late (${actM - expM}m) - LIVE`;
                                    }
                                }

                                return {
                                    _id: r.id || r.name,
                                    teacherName: r.name,
                                    subject: teacherInfo.subject || 'N/A',
                                    classRoom: teacherInfo.batch || 'N/A',
                                    timeStart: regStartStr || r.inTime,
                                    timeEnd: isCompleted ? r.outTime : (teacherInfo.timing ? teacherInfo.timing.split('-')[1]?.trim() || 'Ongoing' : 'Ongoing'),
                                    status: currentStatus
                                };
                            });

                        if (liveTeacherSchedules.length === 0) {
                            return (
                                <div style={{ padding: '20px', textAlign: 'center', color: '#888', gridColumn: '1 / -1' }}>
                                    No teachers are currently punched in for lectures today.
                                </div>
                            );
                        }

                        return liveTeacherSchedules.map((sched, idx) => {
                            const s = (sched.status || 'Scheduled').toLowerCase();
                            let borderCol = '#667eea'; // Default Scheduled (Blue)
                            let statusColor = '#27ae60';

                            if (s.includes('completed')) {
                                borderCol = '#27ae60'; // Success (Green)
                            } else if (s.includes('late')) {
                                borderCol = '#e74c3c'; // Error (Red)
                                statusColor = '#e74c3c';
                            } else if (s.includes('live') || s.includes('ongoing')) {
                                borderCol = '#f39c12'; // Live/Ongoing (Orange)
                                statusColor = '#d97706';
                            } else if (s.includes('absent')) {
                                borderCol = '#f39c12'; // Warning (Yellow/Orange)
                                statusColor = '#d97706';
                            } else if (s.includes('cancelled') || s.includes('canceled')) {
                                borderCol = '#e74c3c'; // Error (Red)
                                statusColor = '#e74c3c';
                            }

                            return (
                                <div key={sched._id || idx} style={{ background: '#f8f9fa', padding: '12px', borderRadius: '8px', borderLeft: `4px solid ${borderCol}`, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                        <strong style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#2c3e50' }}>{sched.teacherName}</strong>
                                        {sched.status.toLowerCase() === 'live' ? (
                                            <span style={{ background: '#fef08a', color: '#854d0e', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>LIVE</span>
                                        ) : sched.status.toLowerCase().includes('late') && sched.status.toLowerCase().includes('live') ? (
                                            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>{sched.status}</span>
                                        ) : getStatusBadge(sched.status)}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#666' }}>{sched.subject} • {sched.classRoom}</div>
                                    <div style={{ fontSize: '12px', color: statusColor, fontWeight: '500' }}>
                                        🕐 {sched.timeStart} - {sched.timeEnd}
                                    </div>
                                </div>
                            );
                        });
                    })()}
                </div>
            </div>

            {/* Teacher Schedule Manager Modal */}
            {showScheduleModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '12px', width: '600px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 4px 25px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px', color: '#2c3e50' }}>
                                📅 {scheduleModalMode === 'edit' ? (editingSchedule ? '✏️ Edit Schedule Slot' : '➕ Add Schedule Slot') : '📋 Teacher Schedule Manager'}
                            </h3>
                            <button onClick={() => { setShowScheduleModal(false); setEditingSchedule(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#888' }}>&times;</button>
                        </div>

                        {scheduleModalMode === 'view' ? (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <span style={{ fontSize: '14px', color: '#666' }}>Manage classes, timings, and teacher availability.</span>
                                    <button className="btn btn-warning" onClick={handleOpenAdd}>➕ Add Class Slot</button>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {schedules.length === 0 ? (
                                        <div style={{ padding: '30px', textAlign: 'center', color: '#888' }}>
                                            No schedules found. Click "Add Class Slot" to create one.
                                        </div>
                                    ) : (
                                        schedules.map((sched, idx) => {
                                            const s = (sched.status || 'Scheduled').toLowerCase();
                                            let borderCol = '#667eea'; // Default Scheduled
                                            let statusColor = '#27ae60';

                                            if (s.includes('completed')) {
                                                borderCol = '#27ae60';
                                            } else if (s.includes('absent')) {
                                                borderCol = '#f39c12';
                                                statusColor = '#d97706';
                                            } else if (s.includes('cancelled') || s.includes('canceled')) {
                                                borderCol = '#e74c3c';
                                                statusColor = '#e74c3c';
                                            }

                                            return (
                                                <div key={sched._id || idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8f9fa', padding: '12px 15px', borderRadius: '8px', borderLeft: `4px solid ${borderCol}`, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                                                    <div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                            <strong style={{ fontSize: '15px', color: '#2c3e50' }}>{sched.teacherName}</strong>
                                                            {getStatusBadge(sched.status)}
                                                        </div>
                                                        <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>{sched.subject} • {sched.classRoom}</div>
                                                        <div style={{ fontSize: '12px', color: statusColor, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500' }}>
                                                            🕐 {sched.timeStart} - {sched.timeEnd}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => handleOpenEdit(sched)} style={{ background: '#f39c12', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Edit</button>
                                                        <button onClick={() => handleDeleteSchedule(sched._id)} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>Delete</button>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSaveSchedule}>
                                <div style={{ marginBottom: '15px' }}>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#2c3e50' }}>Teacher Name *</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <select
                                            onChange={(e) => {
                                                if (e.target.value !== 'custom') {
                                                    setFormTeacherName(e.target.value);
                                                } else {
                                                    setFormTeacherName('');
                                                }
                                            }}
                                            value={users.some(u => u.name === formTeacherName) ? formTeacherName : 'custom'}
                                            style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ccc', background: 'white' }}
                                        >
                                            <option value="custom">-- Enter custom name / select below --</option>
                                            {users.filter(u => u.role === 'teacher' || u.role === 'admin').map((u, i) => (
                                                <option key={u.id || i} value={u.name}>{u.name} ({u.role})</option>
                                            ))}
                                        </select>
                                        <input
                                            type="text"
                                            required
                                            placeholder="or type custom name"
                                            value={formTeacherName}
                                            onChange={(e) => setFormTeacherName(e.target.value)}
                                            style={{ flex: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#2c3e50' }}>Subject *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. Mathematics"
                                            value={formSubject}
                                            onChange={(e) => setFormSubject(e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#2c3e50' }}>Class Room *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. Class 10-A"
                                            value={formClassRoom}
                                            onChange={(e) => setFormClassRoom(e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#2c3e50' }}>Start Time *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. 09:00 AM"
                                            value={formTimeStart}
                                            onChange={(e) => setFormTimeStart(e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#2c3e50' }}>End Time *</label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. 10:30 AM"
                                            value={formTimeEnd}
                                            onChange={(e) => setFormTimeEnd(e.target.value)}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ display: 'block', fontWeight: 600, fontSize: '14px', marginBottom: '5px', color: '#2c3e50' }}>Availability / Status</label>
                                    <select
                                        value={formStatus}
                                        onChange={(e) => setFormStatus(e.target.value)}
                                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc', background: 'white' }}
                                    >
                                        <option value="Scheduled">Scheduled</option>
                                        <option value="Absent - Substitute needed">Absent - Substitute needed</option>
                                        <option value="Completed">Completed</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>

                                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <button type="button" onClick={() => setScheduleModalMode('view')} className="btn" style={{ background: '#eee', color: '#333' }}>Back to List</button>
                                    <button type="submit" className="btn btn-primary">{editingSchedule ? 'Save Changes' : 'Add Class'}</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div >
    );
};

const AttendancePage = () => {
    const navigate = useNavigate();
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [exportMenuOpen, setExportMenuOpen] = useState(false);

    const handleExport = (format) => {
        if (!filteredRecords || filteredRecords.length === 0) {
            alert("No records to export.");
            return;
        }

        const exportData = filteredRecords.map(r => ({
            "Date": r.date || '',
            "User ID": r.userId || '',
            "Name": r.name || '',
            "Role": r.role || '',
            "In Time": r.inTime || '',
            "Out Time": r.outTime || '',
            "Status": r.status || ''
        }));

        const now = new Date();
        const dateStr = now.toISOString().split('T')[0];

        if (format === 'excel') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Logs");
            XLSX.writeFile(workbook, `Attendance_Logs_${dateStr}.xlsx`);
        } else if (format === 'csv') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
            const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `Attendance_Logs_${dateStr}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'pdf') {
            const doc = new jsPDF();
            doc.text("Attendance Logs Report", 14, 15);
            doc.setFontSize(10);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 20);
            doc.autoTable({
                head: [['Date', 'User ID', 'Name', 'Role', 'In Time', 'Out Time', 'Status']],
                body: exportData.map(r => [r['Date'], r['User ID'], r['Name'], r['Role'], r['In Time'], r['Out Time'], r['Status']]),
                startY: 25
            });
            doc.save(`Attendance_Logs_${dateStr}.pdf`);
        }
        setExportMenuOpen(false);
    };

    const fetchAttendance = useCallback(() => {
        setLoading(true);
        api.get('/attendance')
            .then(res => {
                setRecords(res.data);
                setFilteredRecords(res.data);
                setError(null);
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to load attendance:', err);
                setError('Failed to fetch attendance logs. Please try again.');
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        fetchAttendance();

        const handleLivePunch = () => {
            fetchAttendance();
        };

        socket.on('live_punch', handleLivePunch);
        return () => {
            socket.off('live_punch', handleLivePunch);
        };
    }, [fetchAttendance]);

    useEffect(() => {
        let filtered = records;

        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(r =>
                (r.name && r.name.toLowerCase().includes(query)) ||
                (r.userId && String(r.userId).includes(query))
            );
        }

        if (roleFilter !== 'all') {
            filtered = filtered.filter(r => r.role.toLowerCase() === roleFilter);
        }

        if (statusFilter !== 'all') {
            filtered = filtered.filter(r => r.status.toLowerCase() === statusFilter);
        }

        setFilteredRecords(filtered);
    }, [searchQuery, roleFilter, statusFilter, records]);

    const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '??';

    return (
        <div className="dashboard-card">
            <div className="card-header" style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'stretch' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2>📋 Full Attendance Log</h2>
                    <div className="actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', position: 'relative' }}>
                        <button className="btn btn-primary" onClick={fetchAttendance} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '10px 16px', fontSize: '14px', borderRadius: '6px', cursor: 'pointer' }}>
                            🔄 Refresh
                        </button>
                        <div style={{ position: 'relative' }}>
                            <button onClick={() => setExportMenuOpen(!exportMenuOpen)} style={{ background: '#f3e8ff', color: '#8b5cf6', border: 'none', padding: '10px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                                <span>📤</span> Export <span style={{ fontSize: '10px' }}>▼</span>
                            </button>
                            {exportMenuOpen && (
                                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '150px', overflow: 'hidden' }}>
                                    <button onClick={() => handleExport('excel')} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f1f5f9' }}>
                                        <span style={{ color: '#10b981' }}>📊</span> Excel (.xlsx)
                                    </button>
                                    <button onClick={() => handleExport('csv')} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f1f5f9' }}>
                                        <span style={{ color: '#f59e0b' }}>📝</span> CSV (.csv)
                                    </button>
                                    <button onClick={() => handleExport('pdf')} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <span style={{ color: '#ef4444' }}>📄</span> PDF (.pdf)
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}>
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <input
                            type="text"
                            placeholder="Search by Name or ID..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px' }}
                        />
                    </div>
                    <div>
                        <select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                        >
                            <option value="all">All Roles</option>
                            <option value="teacher">Teachers</option>
                            <option value="staff">Staff</option>
                            <option value="student">Students</option>
                        </select>
                    </div>
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '14px', background: 'white' }}
                        >
                            <option value="all">All Statuses</option>
                            <option value="present">Present</option>
                            <option value="late">Late</option>
                            <option value="absent">Absent</option>
                        </select>
                    </div>
                </div>
            </div>

            {error && (
                <div style={{ padding: '15px', color: '#721c24', background: '#f8d7da', borderRadius: '6px', margin: '15px' }}>
                    {error}
                </div>
            )}

            <table className="dashboard-table">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Role</th>
                        <th>In Time</th>
                        <th>Out Time</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                                Loading attendance records...
                            </td>
                        </tr>
                    ) : filteredRecords.length === 0 ? (
                        <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                                No attendance records found.
                            </td>
                        </tr>
                    ) : (
                        filteredRecords.map((record, idx) => (
                            <tr key={`${record.userId}-${record.rawDate}-${idx}`}>
                                <td>{record.date}</td>
                                <td>
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
                                        onClick={() => navigate(`/${(record.role || 'student').toLowerCase() === 'teacher' || (record.role || 'student').toLowerCase() === 'staff' ? 'teacher' : 'student'}/${record.userId || record.id || record.fingerprint_id}`)}
                                    >
                                        {record.photo ? (
                                            <img
                                                src={getPhotoUrl(record.photo)}
                                                alt={record.name}
                                                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div className="avatar" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                                                {getInitials(record.name)}
                                            </div>
                                        )}
                                        <div>
                                            <div style={{ fontWeight: 600, color: '#3b82f6', textDecoration: 'underline' }}>{record.name}</div>
                                            <div style={{ fontSize: '11px', color: '#6c757d' }}>ID: {record.userId}</div>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span style={{
                                        textTransform: 'capitalize',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        padding: '3px 8px',
                                        borderRadius: '4px',
                                        background: record.role.toLowerCase() === 'teacher' ? '#e3f2fd' : (record.role.toLowerCase() === 'staff' ? '#fff3e0' : '#f1f5f9'),
                                        color: record.role.toLowerCase() === 'teacher' ? '#0d47a1' : (record.role.toLowerCase() === 'staff' ? '#e65100' : '#475569')
                                    }}>
                                        {record.role}
                                    </span>
                                </td>
                                <td>{record.inTime}</td>
                                <td>{record.outTime}</td>
                                <td>
                                    <span className={`status ${record.status.toLowerCase()}`}>
                                        {record.status}
                                    </span>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

const NewEnquiryPage = () => {
    return (
        <div style={{ height: 'calc(100vh - 100px)', width: '100%', padding: '0px' }}>
            <iframe src="/enrollment.html" style={{ width: '100%', height: '100%', border: 'none', borderRadius: '12px' }} title="New Enquiry"></iframe>
        </div>
    );
};

const BulkUploadPage = () => {
    const [bulkFile, setBulkFile] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [feeFile, setFeeFile] = useState(null);
    const [feeSuccessMessage, setFeeSuccessMessage] = useState("");
    const [printBlank, setPrintBlank] = useState(null);
    const [processedStudents, setProcessedStudents] = useState([]);
    const [processedAdmissions, setProcessedAdmissions] = useState([]);
    const [processingFees, setProcessingFees] = useState(false);
    const [feeProgress, setFeeProgress] = useState({ current: 0, total: 0 });
    const [selectedBulkCourses, setSelectedBulkCourses] = useState({
        banking: false,
        ssc: false,
        mba: false,
        jee: false,
        police: false,
        tait: false,
        mpsc: false,
        net: false
    });
    const [selectedBulkDocs, setSelectedBulkDocs] = useState({
        aadhar: false,
        ssc: false,
        hsc: false,
        other: false
    });
    const [selectedBulkQuals, setSelectedBulkQuals] = useState({
        ssc: false,
        hsc: false,
        graduate: false,
        postgraduate: false
    });
    const [processingAdmissionsState, setProcessingAdmissionsState] = useState(false);
    const [admissionProgress, setAdmissionProgress] = useState({ current: 0, total: 0 });

    const blankStudent = {
        name: "",
        fingerprint_id: "",
        email: "",
        studentPhone: "",
        parentPhone: "",
        aadhar: "",
        gender: "",
        dob: "",
        marks10th: "",
        marks12th: "",
        address: "",
        branch: "",
        course: "",
        fee: "",
        amountReceived: "",
        paymentMode: "",
        dueFees: "",
        receiptNo: ""
    };

    // Report download states
    const now = new Date();

    useEffect(() => {
        if (printBlank === 'admission') {
            document.body.classList.add('print-blank-admission');
            const timer = setTimeout(() => {
                window.print();
                document.body.classList.remove('print-blank-admission');
                setPrintBlank(null);
            }, 250);
            return () => clearTimeout(timer);
        } else if (printBlank === 'receipt') {
            document.body.classList.add('print-blank-receipt');
            const timer = setTimeout(() => {
                window.print();
                document.body.classList.remove('print-blank-receipt');
                setPrintBlank(null);
            }, 250);
            return () => clearTimeout(timer);
        }
    }, [printBlank]);
    const [reportMonth, setReportMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
    const [reportYear, setReportYear] = useState(String(now.getFullYear()));
    const [reportDepartment, setReportDepartment] = useState('All');
    const [downloading, setDownloading] = useState(false);

    const courseOptions = [
        'All',
        'Students',
        'Teachers',
        'Staff',
        'Staff Selection Commission (SSC-CGL)',
        'POLICE/ARMY/MILITARY TRAINING BATCH',
        'XI - Science PCMB [JEE-NEET-CET]',
        'XII - Science PCMB [JEE-NEET-CET]',
        'UPSC - Civil Services Examination',
        'UGC NET/ MH SET/ CSIR NET',
        'MBA Entrance (CAT/MAT/MH CET)',
        'MPSC - State Services Examination',
        'Maharashtra Engineering Services (MES)',
        'Banking [RBI/SBI/NABARD/SSC/IBPS]',
        'MPSC (Group B & C)',
        'RRB-NTPC'
    ];

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    const parseExcelDate = (val) => {
        if (val === undefined || val === null) return '';
        const trimmed = String(val).trim();
        if (trimmed === '') return '';

        // Convert Excel numeric date serials to DD/MM/YYYY
        if (!isNaN(Number(trimmed)) && Number(trimmed) > 20000) {
            try {
                const date = new Date((Number(trimmed) - 25569) * 86400 * 1000);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            } catch (err) {
                return trimmed;
            }
        }
        return trimmed;
    };

    const handleFileChange = (e) => {
        if (e.target.files.length > 0) setBulkFile(e.target.files[0]);
    };

    const handleLoadData = () => {
        if (!bulkFile) return alert("Please choose an Excel file first.");
        setProcessingAdmissionsState(true);
        setProcessedAdmissions([]);
        setSuccessMessage("");
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rows2D = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                if (rows2D.length === 0) {
                    setProcessingAdmissionsState(false);
                    return alert("Excel file is empty");
                }

                // Find the header row index dynamically
                let headerRowIdx = -1;
                for (let i = 0; i < rows2D.length; i++) {
                    const row = rows2D[i];
                    if (!row || !Array.isArray(row)) continue;

                    // Check if this row contains a Name or Student column
                    const hasName = row.some(cell => {
                        if (cell === undefined || cell === null) return false;
                        const cellStr = String(cell).toLowerCase().replace(/[\s_-]/g, '');
                        return cellStr.includes('name') || cellStr.includes('student') || cellStr.includes('teacher') || cellStr.includes('staff');
                    });

                    // Check if this row also contains other common student fields like mobile, class, email, or roll no
                    const hasOtherField = row.some(cell => {
                        if (cell === undefined || cell === null) return false;
                        const cellStr = String(cell).toLowerCase().replace(/[\s_-]/g, '');
                        return cellStr.includes('mobile') || cellStr.includes('phone') || cellStr.includes('email') || cellStr.includes('class') || cellStr.includes('course') || cellStr.includes('reg') || cellStr.includes('roll');
                    });

                    if (hasName && hasOtherField) {
                        headerRowIdx = i;
                        break;
                    }
                }

                if (headerRowIdx === -1) {
                    setProcessingAdmissionsState(false);
                    return alert("Could not find header row with 'Name' and other fields in the Excel file.");
                }

                const headers = rows2D[headerRowIdx].map(h => h ? String(h).trim() : '');

                // Parse the remaining rows as JSON objects using the headers
                const parsedRows = [];
                for (let i = headerRowIdx + 1; i < rows2D.length; i++) {
                    const row = rows2D[i];
                    if (!row || row.length === 0) continue;

                    // Check if the row is completely empty
                    const isEmpty = row.every(cell => cell === undefined || cell === null || String(cell).trim() === '');
                    if (isEmpty) continue;

                    const rowObj = {};
                    headers.forEach((header, colIdx) => {
                        if (header) {
                            rowObj[header] = row[colIdx];
                        }
                    });
                    parsedRows.push(rowObj);
                }

                if (parsedRows.length === 0) {
                    setProcessingAdmissionsState(false);
                    return alert("Excel file is empty or no valid rows found after header");
                }

                // Helper to get val by list of keywords (with exact match preference and collision avoidance)
                const getVal = (row, keywords) => {
                    // Phase 1: Try exact normalized match (stripping non-alphanumeric chars)
                    for (const kw of keywords) {
                        const key = Object.keys(row).find(k => {
                            const norm = k.toLowerCase().replace(/[^a-z0-9]/g, '');
                            return norm === kw;
                        });
                        if (key !== undefined) return row[key];
                    }
                    // Phase 2: Try substring match but prevent common collisions (e.g. 'due' matching 'date')
                    for (const kw of keywords) {
                        const key = Object.keys(row).find(k => {
                            const norm = k.toLowerCase().replace(/[^a-z0-9]/g, '');
                            if (kw === 'due' && norm.includes('date')) return false;
                            if (kw === 'date' && norm.includes('due')) return false;
                            return norm.includes(kw);
                        });
                        if (key !== undefined) return row[key];
                    }
                    return null;
                };

                let successCount = 0;
                setAdmissionProgress({ current: 0, total: parsedRows.length });
                const enrolledStudentsList = [];
                for (let row of parsedRows) {
                    try {
                        const name = getVal(row, ['name', 'student', 'teacher', 'staff', 'employee']) || '';
                        if (!name || name.trim() === '' || name.toLowerCase() === 'unknown') {
                            continue; // Skip blank or invalid header rows
                        }
                        const email = getVal(row, ['email']) || '';
                        const excelRole = getVal(row, ['role', 'designation', 'type', 'position']) || 'student';
                        const resolvedRole = ['admin', 'teacher', 'staff', 'student'].includes(String(excelRole).toLowerCase().trim()) ? String(excelRole).toLowerCase().trim() : 'student';
                        const studentContact = getVal(row, ['phone', 'mobile', 'contact', 'studentphone', 'primarymobno', 'primarymobile', 'primarymob', 'primaryphone', 'studentcontact']) || '';
                        const excelRegNo = getVal(row, ['registrationnumber', 'registration', 'register', 'registernumber', 'regnumber', 'registrationno', 'regno', 'registerno', 'biometricid', 'biometric', 'fingerprint', 'rollno', 'roll', 'srno', 'serialno', 'studentid', 'studentno', 'formno', 'admissionno']);
                        const fingerprint_id = (excelRegNo !== null && String(excelRegNo).trim() !== '')
                            ? String(excelRegNo).trim()
                            : (studentContact ? String(studentContact).trim() : String(Math.floor(100000 + Math.random() * 900000)));
                        const course = getVal(row, ['course', 'class', 'coaching']) || '';
                        const branch = getVal(row, ['branch']) || '';
                        const parentPhone = getVal(row, ['parent', 'father', 'mother', 'parentphone', 'secondarymobno', 'secondarymobile', 'secondarymob', 'secondaryphone', 'parentcontact']) || '';
                        const aadhar = getVal(row, ['aadhar', 'adhar']) || '';
                        const gender = getVal(row, ['gender', 'sex']) || '';
                        const dob = getVal(row, ['dob', 'birth', 'birthdate']) || '';
                        const tenthPercent = getVal(row, ['10th', 'tenth', 'sscpercent', 'marks10']) || '';
                        const twelfthPercent = getVal(row, ['12th', 'twelfth', 'hscpercent', 'marks12']) || '';
                        const address = getVal(row, ['address', 'addr']) || '';
                        const previousSchool = getVal(row, ['school', 'college', 'previousschool']) || '';
                        const enquiryDate = getVal(row, ['admissiondate', 'date', 'enquirydate']) || '';

                        const excelQual = getVal(row, ['qualification', 'edu', 'education', 'degree', 'qual']) || '';
                        const qualification = (() => {
                            const checkedQuals = [];
                            if (selectedBulkQuals.ssc) checkedQuals.push("SSC");
                            if (selectedBulkQuals.hsc) checkedQuals.push("HSC");
                            if (selectedBulkQuals.graduate) checkedQuals.push("Graduate");
                            if (selectedBulkQuals.postgraduate) checkedQuals.push("Postgraduate");
                            return excelQual ? [excelQual, ...checkedQuals].join(', ') : checkedQuals.join(', ');
                        })();

                        const documentSubmitted = (() => {
                            const checkedDocs = [];
                            if (selectedBulkDocs.aadhar) checkedDocs.push("Aadhar");
                            if (selectedBulkDocs.ssc) checkedDocs.push("SSC");
                            if (selectedBulkDocs.hsc) checkedDocs.push("HSC");
                            if (selectedBulkDocs.other) checkedDocs.push("Other");
                            return checkedDocs.join(', ');
                        })();

                        // Parse payment/fee details from Excel row (removing commas/currency symbols/spaces and extracting numeric values)
                        const parseLocalNum = (v, fb = 0) => {
                            if (v === null || v === undefined) return fb;
                            const str = String(v).trim();
                            if (str === '') return fb;
                            const cleaned = str.replace(/,/g, '');
                            const match = cleaned.match(/[-+]?\d*\.?\d+/);
                            if (match) {
                                const num = parseFloat(match[0]);
                                return isNaN(num) ? fb : num;
                            }
                            return fb;
                        };

                        const excelFee = getVal(row, ['totalfee', 'totalfees', 'coursefee', 'coursefees', 'fee', 'fees', 'netfee']);
                        const fee = parseLocalNum(excelFee, 0);

                        const excelAmtReceived = getVal(row, ['amountreceived', 'receivedamount', 'amountpaid', 'paidamount', 'totalamountreceived', 'totalreceivedamount', 'totalpaid', 'totalreceived', 'feespaid', 'feepaid', 'installmentamount', 'instalmentamount', 'instamount', 'instamt', 'recamt', 'receivedamt', 'amountrecd', 'amtreceived', 'recdamount', 'received', 'paid', 'amount']);
                        const amountReceived = parseLocalNum(excelAmtReceived, 0);

                        const excelDueFees = getVal(row, ['duefees', 'duefee', 'dueamount', 'remainingdue', 'balance', 'pending', 'due']);
                        const dueFees = excelDueFees !== null && excelDueFees !== undefined
                            ? parseLocalNum(excelDueFees, Math.max(0, fee - amountReceived))
                            : Math.max(0, fee - amountReceived);

                        const numberToWords = (num) => {
                            const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
                            const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                            if ((num = num.toString()).length > 9) return 'overflow';
                            let n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
                            if (!n) return 'Zero';
                            let str = '';
                            str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
                            str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
                            str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
                            str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
                            str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
                            return (str.trim() + ' Rupees Only').trim();
                        };
                        const amountReceivedWords = numberToWords(amountReceived);

                        const paymentMode = getVal(row, ['paymentmode', 'mode', 'pmode']) || 'Cash';
                        const installment = getVal(row, ['installment', 'inst', 'isinstallment']) || 'No';
                        const receiptNo = getVal(row, ['receiptno', 'receipt', 'slipno']) || '';

                        const experience = getVal(row, ['experience', 'exp']) || '';
                        const subject = getVal(row, ['subject', 'subj']) || '';
                        const timing = getVal(row, ['timing', 'time', 'batchtiming']) || '';
                        const salary = getVal(row, ['salary', 'sal', 'pay']) || '';
                        const profession = getVal(row, ['profession', 'prof', 'occupation']) || '';
                        const bankName = getVal(row, ['bank', 'bankname']) || '';
                        const accountNumber = getVal(row, ['account', 'ac', 'accountnumber', 'acno']) || '';
                        const ifscCode = getVal(row, ['ifsc', 'ifsccode']) || '';
                        const accountHolder = getVal(row, ['accountholder', 'holder', 'nameonaccount']) || '';

                        const studentPayload = {
                            name,
                            email,
                            fingerprint_id,
                            role: resolvedRole,
                            studentContact,
                            studentPhone: studentContact,
                            courses: (() => {
                                const checkedCourses = [];
                                if (selectedBulkCourses.banking) checkedCourses.push("Banking [RBI/SBI/NABARD/SSC/IBPS]");
                                if (selectedBulkCourses.ssc) checkedCourses.push("Staff Selection Commission (SSC-CGL)");
                                if (selectedBulkCourses.mba) checkedCourses.push("MBA Entrance (CAT/MAT/MH CET)");
                                if (selectedBulkCourses.jee) checkedCourses.push("XI - Science PCMB [JEE-NEET-CET]");
                                if (selectedBulkCourses.police) checkedCourses.push("POLICE/ARMY/MILITARY TRAINING BATCH");
                                if (selectedBulkCourses.tait) checkedCourses.push("MAHA-TAIT");
                                if (selectedBulkCourses.mpsc) checkedCourses.push("MPSC (Group B & C)");
                                if (selectedBulkCourses.net) checkedCourses.push("UGC NET/ MH SET/ CSIR NET");

                                const combined = [];
                                if (course && String(course).trim() !== '') {
                                    combined.push(String(course).trim());
                                }
                                checkedCourses.forEach(c => {
                                    if (!combined.includes(c)) combined.push(c);
                                });
                                return combined;
                            })(),
                            branch,
                            parentPhone,
                            aadhar,
                            gender,
                            dob: dob ? (typeof dob === 'number' ? parseExcelDate(dob) : dob) : '',
                            tenthPercent,
                            twelfthPercent,
                            marks10th: tenthPercent,
                            marks12th: twelfthPercent,
                            qualification,
                            documentSubmitted,
                            address,
                            previousSchool,
                            enquiryDate: enquiryDate ? (typeof enquiryDate === 'number' ? parseExcelDate(enquiryDate) : enquiryDate) : '',
                            fee,
                            amountReceived,
                            dueFees,
                            amountReceivedWords,
                            paymentMode,
                            installment,
                            receiptNo,
                            experience,
                            subject,
                            timing,
                            salary,
                            profession,
                            bankName,
                            accountNumber,
                            ifscCode,
                            accountHolder
                        };

                        try {
                            const enrollRes = await api.post('/users/enroll', studentPayload);
                            if (enrollRes.data && enrollRes.data.user) {
                                enrolledStudentsList.push(enrollRes.data.user);
                            } else {
                                enrolledStudentsList.push(studentPayload);
                            }
                        } catch (dbErr) {
                            enrolledStudentsList.push(studentPayload);
                        }

                        successCount++;
                        setAdmissionProgress({ current: successCount, total: parsedRows.length });
                    } catch (err) {
                        console.error("Failed to process row", row, err);
                    }
                }

                setProcessedAdmissions(enrolledStudentsList);
                setSuccessMessage(`Successfully enrolled ${successCount} out of ${parsedRows.length} students.`);
                alert(`Successfully enrolled ${successCount} students.`);
            } catch (error) {
                console.error(error);
                alert("Error parsing Excel file. Ensure it is a valid .xlsx file.");
            } finally {
                setProcessingAdmissionsState(false);
            }
        };
        reader.readAsArrayBuffer(bulkFile);
    };

    const handleDownloadTemplate = () => {
        const templateData = [
            {
                "Name": "John Doe",
                "Email": "john.doe@example.com",
                "Register No": "1001",
                "Mobile": "9876543210",
                "Class": "XI - Science PCMB [JEE-NEET-CET]"
            },
            {
                "Name": "Jane Smith",
                "Email": "jane.smith@example.com",
                "Register No": "1002",
                "Mobile": "9876543211",
                "Class": "XII - Science PCMB [JEE-NEET-CET]"
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
        XLSX.writeFile(workbook, "Student_Admission_Template.xlsx");
    };

    const handleFeeFileChange = (e) => {
        if (e.target.files.length > 0) setFeeFile(e.target.files[0]);
    };

    const handleLoadFeeReceipts = () => {
        if (!feeFile) return alert("Please choose an Excel file first.");
        setProcessingFees(true);
        setProcessedStudents([]);
        setFeeSuccessMessage("");
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const dataArray = new Uint8Array(e.target.result);
                const workbook = XLSX.read(dataArray, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const rows2D = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                if (rows2D.length === 0) {
                    setProcessingFees(false);
                    return alert("Excel file is empty");
                }

                // Find the header row index dynamically
                let headerRowIdx = -1;
                for (let i = 0; i < rows2D.length; i++) {
                    const row = rows2D[i];
                    if (!row || !Array.isArray(row)) continue;

                    let hasId = false;
                    let hasName = false;
                    let hasAmount = false;

                    for (let cell of row) {
                        if (cell === undefined || cell === null) continue;
                        const cellStr = String(cell).toLowerCase().replace(/[\s_-]/g, '');
                        if (cellStr.includes('reg') || cellStr.includes('biometric') || cellStr.includes('roll') || cellStr === 'id' || cellStr.includes('fingerprint') || cellStr.includes('no')) {
                            hasId = true;
                        }
                        if (cellStr.includes('name') || cellStr.includes('student')) {
                            hasName = true;
                        }
                        if (cellStr.includes('amount') || cellStr.includes('paid') || cellStr.includes('fee') || cellStr.includes('received')) {
                            hasAmount = true;
                        }
                    }

                    if ((hasId && hasName) || (hasId && hasAmount) || (hasName && hasAmount)) {
                        headerRowIdx = i;
                        break;
                    }
                }

                if (headerRowIdx === -1) {
                    headerRowIdx = 0;
                }

                const headers = rows2D[headerRowIdx].map(h => h !== undefined && h !== null ? String(h).trim() : '');
                console.log("[Bulk Fees] Detected headers in Excel:", headers);

                const hasHeader = (keywords, excludes = []) => {
                    return headers.some(h => {
                        const norm = h.toLowerCase().replace(/[^a-z0-9]/g, '');
                        if (norm.includes('sr') || norm.includes('serial')) return false;
                        if (excludes.some(ex => norm.includes(ex))) return false;
                        return keywords.some(kw => norm.includes(kw));
                    });
                };

                const getFinalVal = (val, hasCol, dbFallback, defaultVal = '') => {
                    if (hasCol) {
                        return val !== null && val !== undefined ? String(val).trim() : '';
                    }
                    return dbFallback !== undefined && dbFallback !== null ? dbFallback : defaultVal;
                };

                const isDateValue = (val) => {
                    if (val === null || val === undefined) return false;
                    const str = String(val).trim();
                    if (!str) return false;
                    if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(str)) return true;
                    const num = Number(str);
                    if (!isNaN(num) && num >= 40000 && num <= 50000) return true;
                    return false;
                };

                const parseNumericVal = (val, fallbackVal = null) => {
                    if (val === null || val === undefined) return fallbackVal;
                    const str = String(val).replace(/,/g, '').replace(/[₹$\s\/-]/g, '').trim();
                    if (str === '') return fallbackVal;
                    const cleaned = str.replace(/,/g, '');
                    const match = cleaned.match(/[-+]?\d*\.?\d+/);
                    if (match) {
                        const num = parseFloat(match[0]);
                        return isNaN(num) ? fallbackVal : num;
                    }
                    return fallbackVal;
                };

                const numberToWords = (num) => {
                    const nStr = num ? num.toString() : '0';
                    const nNum = parseInt(nStr, 10);
                    if (isNaN(nNum) || nNum === 0) return 'Zero Only';
                    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
                    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                    if (nStr.length > 9) return 'overflow';
                    const n = ('000000000' + nStr).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
                    if (!n) return 'Zero Only';
                    let str = '';
                    str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
                    str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
                    str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
                    str += (n[4] != 0) ? (a[Number(n[4])] || b[n[4][0]] + ' ' + a[n[4][1]]) + 'Hundred ' : '';
                    str += (n[5] != 0) ? ((str != '') ? 'and ' : '') + (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
                    return str.trim() + ' Only';
                };
                const json = [];
                for (let i = headerRowIdx + 1; i < rows2D.length; i++) {
                    const row = rows2D[i];
                    if (!row || row.length === 0) continue;

                    const isEmpty = row.every(cell => cell === undefined || cell === null || String(cell).trim() === '');
                    if (isEmpty) continue;

                    const obj = {};
                    headers.forEach((header, colIdx) => {
                        if (header) {
                            obj[header] = row[colIdx];
                        }
                    });
                    json.push(obj);
                }

                if (json.length === 0) {
                    setProcessingFees(false);
                    return alert("Excel file is empty or no valid rows found after header");
                }

                const usersRes = await api.get('/users');
                const students = usersRes.data.filter(u => u.role && u.role.toLowerCase().trim() === 'student');

                let successCount = 0;
                setFeeProgress({ current: 0, total: json.length });
                const updatedStudentsList = [];
                for (let row of json) {
                    // Helper to get val by list of keywords with exclusions
                    const getVal = (keywords, excludes = []) => {
                        // Phase 1: Try exact normalized match first to prevent collision
                        for (const kw of keywords) {
                            const key = Object.keys(row).find(k => {
                                const norm = k.toLowerCase().replace(/[^a-z0-9]/g, '');
                                if (norm.includes('sr') || norm.includes('serial')) return false;
                                if (excludes.some(ex => norm.includes(ex))) return false;
                                return norm === kw;
                            });
                            if (key !== undefined) {
                                return row[key];
                            }
                        }
                        // Phase 2: Substring match
                        for (const kw of keywords) {
                            const key = Object.keys(row).find(k => {
                                const norm = k.toLowerCase().replace(/[^a-z0-9]/g, '');
                                if (norm.includes('sr') || norm.includes('serial')) return false;

                                // Check exclusions
                                if (excludes.some(ex => norm.includes(ex))) return false;

                                return norm.includes(kw);
                            });
                            if (key !== undefined) {
                                return row[key];
                            }
                        }
                        return null;
                    };

                    const studentNameVal = getVal(['name', 'student', 'studentname']);
                    if (!studentNameVal || String(studentNameVal).trim() === '') {
                        continue;
                    }

                    const regNoVal = getVal(['regno', 'registration', 'biometric', 'rollno', 'fingerprint', 'studentid', 'studentno', 'reg', 'roll', 'id'], ['no', 'number', 'phone', 'contact', 'mobile']);
                    const regNo = regNoVal ? String(regNoVal).trim() : '';

                    let matchedStudent = null;
                    if (regNo) {
                        const cleanedRegNo = regNo.includes('.') ? String(Math.floor(parseFloat(regNo))) : regNo;
                        matchedStudent = students.find(s =>
                            String(s.fingerprint_id).trim() === cleanedRegNo ||
                            String(s.id).trim() === cleanedRegNo
                        );
                    }

                    // Fallback to fuzzy name matching if regNo failed or was empty
                    if (!matchedStudent) {
                        const excelName = String(studentNameVal).trim().toLowerCase();
                        matchedStudent = students.find(s => {
                            const dbName = String(s.name).trim().toLowerCase();
                            return dbName === excelName || dbName.includes(excelName) || excelName.includes(dbName);
                        });
                    }

                    const courseVal = getVal(['course', 'courses', 'coaching', 'class', 'batch', 'stream']);
                    const branchVal = getVal(['branch']);
                    const phoneVal = getVal(['studentphone', 'studentcontact', 'contactno', 'contactnumber', 'mobileno', 'mobilenumber', 'phoneno', 'phonenumber', 'mobile', 'phone', 'contact', 'mob', 'cell']);
                    const receiptDateVal = getVal(['receiptdate', 'date', 'entrydate', 'paymentdate'], ['due', 'birth', 'dob', 'exp']);
                    const receiptNoVal = getVal(['receiptnumber', 'receiptno', 'recno', 'receiptid', 'slipno'], ['txn', 'transaction', 'cheque', 'ref', 'utr']);
                    const txnNoVal = getVal(['txnno', 'transactionno', 'cheque', 'chequeno', 'txn', 'transaction', 'ref', 'chequereftxn', 'chequetxn', 'txnid', 'transactionid', 'reference', 'refno', 'utr', 'utrno']);
                    const amountReceivedVal = getVal(
                        [
                            'amountreceived', 'receivedamount', 'amountpaid', 'paidamount',
                            'totalamountreceived', 'totalreceivedamount', 'totalpaid', 'totalreceived',
                            'feespaid', 'feepaid', 'installmentamount', 'instalmentamount', 'instamount', 'instamt',
                            'recamt', 'receivedamt', 'amountrecd', 'amtreceived', 'recdamount',
                            'received', 'paid', 'amount', 'payment'
                        ],
                        ['total', 'due', 'balance', 'pending', 'word', 'words']
                    );
                    const amountWordsVal = getVal(['word', 'words', 'amountinwords']);
                    const paymentModeVal = getVal(['paymentmode', 'mode', 'pmode']);
                    const totalFeesVal = getVal(['totalfee', 'totalfees', 'coursefee', 'coursefees', 'netfee', 'netfees', 'fullfee', 'fullfees', 'totalamount', 'fee', 'fees'], ['received', 'paid', 'due', 'balance', 'pending']);
                    const dueFeesVal = getVal(['duefee', 'duefees', 'balance', 'outstanding', 'pending', 'dueamount', 'remainingdue', 'due'], ['date', 'day']);
                    const emailVal = getVal(['email']);
                    const addressVal = getVal(['address', 'addr']);
                    const dueDateVal = getVal(['duedate', 'nextdue', 'validity'], ['fee', 'amount']);
                    const bankNameVal = getVal(['bank', 'bankname']);
                    const installmentVal = getVal(['installment', 'instalment', 'isinstallment', 'isinstalment', 'installmentno', 'instalmentno', 'instno', 'inst', 'installmentyesno', 'instalmentyesno', 'instalmentdetails', 'installmentdetails']);
                    const accountNoVal = getVal(['account', 'accountno', 'accountnumber', 'ac', 'acno']);
                    const ifscCodeVal = getVal(['ifsc', 'ifsccode']);

                    // Check column presence in Excel headers
                    const hasCourseCol = hasHeader(['course', 'coaching', 'class']);
                    const hasBranchCol = hasHeader(['branch']);
                    const hasPhoneCol = hasHeader(['studentphone', 'studentcontact', 'contactno', 'contactnumber', 'mobileno', 'mobilenumber', 'phoneno', 'phonenumber', 'mobile', 'phone', 'contact', 'mob', 'cell']);
                    const hasReceiptDateCol = hasHeader(['receiptdate', 'date'], ['due', 'birth', 'dob', 'exp']);
                    const hasReceiptNoCol = hasHeader(['receiptnumber', 'receiptno', 'recno', 'slipno'], ['txn', 'transaction', 'cheque', 'ref', 'utr']);
                    const hasTxnNoCol = hasHeader(['txnno', 'transactionno', 'cheque', 'chequeno', 'txn', 'transaction', 'ref', 'utr']);
                    const hasAmountWordsCol = hasHeader(['word', 'words']);
                    const hasPaymentModeCol = hasHeader(['mode', 'paymentmode']);
                    const hasBankNameCol = hasHeader(['bank']);
                    const hasInstallmentCol = hasHeader(['installment', 'instalment', 'inst']);
                    const hasAccountNoCol = hasHeader(['account', 'ac']);
                    const hasIfscCodeCol = hasHeader(['ifsc']);
                    const hasEmailCol = hasHeader(['email']);
                    const hasAddressCol = hasHeader(['address', 'addr']);
                    const hasDueDateCol = hasHeader(['duedate', 'nextdue', 'validity'], ['fee', 'amount']);
                    const hasDueFeesCol = hasHeader(['duefee', 'duefees', 'balance', 'outstanding', 'pending', 'due'], ['date', 'day']) && !isDateValue(dueFeesVal);

                    let parsedAmountPaid = parseNumericVal(amountReceivedVal, 0);
                    const parsedTotalFee = parseNumericVal(totalFeesVal, null);
                    const totalFeeVal = parsedTotalFee !== null ? parsedTotalFee : (matchedStudent ? Number(matchedStudent.fee || 0) : 0);
                    const excelDueFeesVal = parseNumericVal(dueFeesVal, null);

                    // Automatic calculation fail-safe: If Amount Received is 0 or missing from Excel, calculate Total Fee - Due Fee
                    if (parsedAmountPaid === 0 && totalFeeVal > 0 && excelDueFeesVal !== null && totalFeeVal > excelDueFeesVal) {
                        parsedAmountPaid = totalFeeVal - excelDueFeesVal;
                    }

                    const currentPaid = matchedStudent ? Number(matchedStudent.amountReceived || 0) : 0;
                    const newPaid = parsedAmountPaid > 0 ? Math.max(currentPaid, parsedAmountPaid) : currentPaid;
                    const newDue = (hasDueFeesCol && excelDueFeesVal !== null) ? excelDueFeesVal : Math.max(0, totalFeeVal - newPaid);

                    const printAmountReceived = parsedAmountPaid > 0 ? parsedAmountPaid : (matchedStudent && Number(matchedStudent.amountReceived || 0) > 0 ? Number(matchedStudent.amountReceived) : Math.max(0, totalFeeVal - newDue));

                    // Build the DB payload (cumulative amountReceived)
                    const dbPayload = {
                        ...(matchedStudent || {}),
                        name: studentNameVal ? String(studentNameVal).trim() : (matchedStudent ? matchedStudent.name : 'Unknown Student'),
                        fingerprint_id: regNo || (matchedStudent ? matchedStudent.fingerprint_id : ''),
                        courses: hasCourseCol ? (courseVal ? [String(courseVal).trim()] : []) : (matchedStudent ? matchedStudent.courses || [] : []),
                        branch: getFinalVal(branchVal, hasBranchCol, matchedStudent ? matchedStudent.branch : ''),
                        studentPhone: getFinalVal(phoneVal, hasPhoneCol, matchedStudent ? (matchedStudent.studentPhone || matchedStudent.studentContact) : ''),
                        studentContact: getFinalVal(phoneVal, hasPhoneCol, matchedStudent ? (matchedStudent.studentContact || matchedStudent.studentPhone) : ''),
                        email: getFinalVal(emailVal, hasEmailCol, matchedStudent ? matchedStudent.email : ''),
                        address: getFinalVal(addressVal, hasAddressCol, matchedStudent ? matchedStudent.address : ''),
                        enquiryDate: hasReceiptDateCol ? parseExcelDate(receiptDateVal) : (matchedStudent ? matchedStudent.enquiryDate || '' : ''),
                        dueDate: hasDueDateCol ? parseExcelDate(dueDateVal) : (matchedStudent ? matchedStudent.dueDate || '' : ''),
                        receiptNo: getFinalVal(receiptNoVal, hasReceiptNoCol, matchedStudent ? matchedStudent.receiptNo : `REC-${Date.now().toString().substring(8)}-${successCount}`),
                        txnNo: getFinalVal(txnNoVal, hasTxnNoCol, matchedStudent ? matchedStudent.txnNo : ''),
                        amountReceived: newPaid,
                        amountReceivedWords: getFinalVal(amountWordsVal, hasAmountWordsCol, (newPaid > 0 ? numberToWords(newPaid) : 'Zero Only')),
                        paymentMode: getFinalVal(paymentModeVal, hasPaymentModeCol, matchedStudent ? matchedStudent.paymentMode : 'Cash', 'Cash'),
                        fee: totalFeeVal,
                        bankName: getFinalVal(bankNameVal, hasBankNameCol, matchedStudent ? matchedStudent.bankName : ''),
                        installment: getFinalVal(installmentVal, hasInstallmentCol, matchedStudent ? matchedStudent.installment : 'No', 'No'),
                        accountNumber: getFinalVal(accountNoVal, hasAccountNoCol, matchedStudent ? matchedStudent.accountNumber : ''),
                        ifscCode: getFinalVal(ifscCodeVal, hasIfscCodeCol, matchedStudent ? matchedStudent.ifscCode : ''),
                        dueFees: newDue
                    };

                    // Build the print payload (showing transaction amountReceived and words)
                    const printPayload = {
                        ...dbPayload,
                        amountReceived: printAmountReceived,
                        amountReceivedWords: printAmountReceived > 0 ? numberToWords(printAmountReceived) : 'Zero Only',
                        dueFees: newDue
                    };

                    // If matched in DB, save payment to database. If unmatched, enroll new student into database!
                    if (matchedStudent) {
                        try {
                            const targetId = matchedStudent.fingerprint_id || matchedStudent.id || matchedStudent._id;
                            await api.put(`/users/${targetId}`, dbPayload);
                        } catch (err) {
                            console.error(`Failed to update database fees for student ${matchedStudent.name}`, err);
                        }
                    } else {
                        try {
                            await api.post('/users/enroll', {
                                ...dbPayload,
                                role: 'student',
                                fingerprint_id: dbPayload.fingerprint_id || (dbPayload.studentPhone ? String(dbPayload.studentPhone).trim() : String(Math.floor(100000 + Math.random() * 900000)))
                            });
                        } catch (enrollErr) {
                            console.error(`Failed to auto-enroll new student from fee Excel: ${dbPayload.name}`, enrollErr);
                        }
                    }

                    updatedStudentsList.push(printPayload);
                    successCount++;
                    setFeeProgress({ current: successCount, total: json.length });
                }

                setProcessedStudents(updatedStudentsList);
                setFeeSuccessMessage(`Successfully processed and generated receipts for ${successCount} out of ${json.length} records.`);
                alert(`Successfully processed and generated ${successCount} fee receipts!\n\nParsed Results:\n` +
                    updatedStudentsList.map(s => `- ${s.name}: Received ₹${s.amountReceived}`).join('\n'));
            } catch (error) {
                console.error(error);
                alert(`Error parsing Excel file: ${error.message}\nStack: ${error.stack}`);
            } finally {
                setProcessingFees(false);
            }
        };
        reader.readAsArrayBuffer(feeFile);
    };

    const handleDownloadFeeTemplate = () => {
        const templateData = [
            {
                "Register No": "1001",
                "Student Name": "John Doe",
                "Amount Paid": "5000",
                "Payment Mode": "UPI",
                "Receipt No": "TXN987654321"
            },
            {
                "Register No": "1002",
                "Student Name": "Jane Smith",
                "Amount Paid": "7500",
                "Payment Mode": "Cash",
                "Receipt No": "REC-8849"
            }
        ];
        const worksheet = XLSX.utils.json_to_sheet(templateData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Fee payments");
        XLSX.writeFile(workbook, "Fee_Payments_Template.xlsx");
    };

    const handleDownloadMonthly = async () => {
        setDownloading(true);
        try {
            const monthStr = `${reportYear}-${reportMonth}`;
            const response = await api.get('/attendance/export-monthly', {
                params: { month: monthStr, department: reportDepartment },
                responseType: 'blob',
                timeout: 300000 // 5 minutes timeout for large report generation
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Attendance_Report_${monthStr}_${reportDepartment.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            let errMsg = err.message;
            if (err.response?.status === 504 || (errMsg && errMsg.includes('504'))) {
                errMsg = 'Gateway timeout (504): The server took too long to generate the report. Please try selecting a specific department or retry in a few moments.';
            } else if (err.response?.data instanceof Blob) {
                try {
                    const errText = await err.response.data.text();
                    const parsed = JSON.parse(errText);
                    if (parsed?.error) errMsg = parsed.error;
                } catch (_) {}
            } else if (err.response?.data?.error) {
                errMsg = err.response.data.error;
            }
            alert('Failed to download report. ' + errMsg);
        } finally {
            setDownloading(false);
        }
    };

    const handleDownloadYearly = async () => {
        setDownloading(true);
        try {
            const response = await api.get('/attendance/export-yearly', {
                params: { year: reportYear, department: reportDepartment },
                responseType: 'blob',
                timeout: 600000 // 10 minutes timeout for yearly (12 sheets)
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Attendance_Report_${reportYear}_${reportDepartment.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            let errMsg = err.message;
            if (err.response?.status === 504 || (errMsg && errMsg.includes('504'))) {
                errMsg = 'Gateway timeout (504): Yearly report generation took longer than expected. Please select a specific department or retry.';
            } else if (err.response?.data instanceof Blob) {
                try {
                    const errText = await err.response.data.text();
                    const parsed = JSON.parse(errText);
                    if (parsed?.error) errMsg = parsed.error;
                } catch (_) {}
            } else if (err.response?.data?.error) {
                errMsg = err.response.data.error;
            }
            alert('Failed to download yearly report. ' + errMsg);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="dashboard-card">
            <div className="card-header">
                <h2>📁 Attendance Reports</h2>
            </div>



            {/* ===== BULK ADMISSION IMPORT SECTION ===== */}
            <div style={{ background: 'linear-gradient(135deg, #fdfbf7 0%, #f5f0e6 100%)', padding: '25px', borderRadius: '12px', border: '1px solid #e8dec9', marginBottom: '25px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>📥</span>
                        <div>
                            <h3 style={{ fontSize: '18px', color: '#1e293b', margin: 0, fontWeight: 700 }}>Bulk Student Admissions</h3>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>Upload an Excel file to register multiple students at once</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setPrintBlank('admission')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', padding: '8px 16px', borderRadius: '6px',
                            color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(16,185,129,0.2)', transition: 'all 0.2s ease'
                        }}
                    >
                        <span>📄 Print Blank Admission Form (PDF)</span>
                    </button>
                </div>

                {/* Course Details, Document Submitted & Qualification selection checkboxes */}
                <div style={{ marginBottom: '20px', padding: '15px', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                        {/* Course Details checkboxes */}
                        <div>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>Course Details (Manual ticks for Admission Form):</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.banking} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, banking: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    IBPS-Banking
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.ssc} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, ssc: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    Staff Selection Commission (SSC)-CGL
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.mba} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, mba: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    MBA-(CAT/MAT/MH CET)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.jee} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, jee: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    JEE/NEET
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.police} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, police: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    Police/Military/ Training
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.tait} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, tait: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    MAHA-TAIT
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.mpsc} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, mpsc: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    MPSC (Group B & C)
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkCourses.net} onChange={(e) => setSelectedBulkCourses({ ...selectedBulkCourses, net: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    UGC NET/MH SET/CSIR NET
                                </label>
                            </div>
                        </div>

                        {/* Document Submitted checkboxes */}
                        <div>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>Document Submitted:</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkDocs.aadhar} onChange={(e) => setSelectedBulkDocs({ ...selectedBulkDocs, aadhar: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    Aadhar Card
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkDocs.ssc} onChange={(e) => setSelectedBulkDocs({ ...selectedBulkDocs, ssc: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    SSC Certificate
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkDocs.hsc} onChange={(e) => setSelectedBulkDocs({ ...selectedBulkDocs, hsc: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    HSC Certificate
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkDocs.other} onChange={(e) => setSelectedBulkDocs({ ...selectedBulkDocs, other: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    Other
                                </label>
                            </div>
                        </div>

                        {/* Qualification checkboxes */}
                        <div>
                            <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#1e293b', fontWeight: 600 }}>Qualification:</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkQuals.ssc} onChange={(e) => setSelectedBulkQuals({ ...selectedBulkQuals, ssc: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    SSC
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkQuals.hsc} onChange={(e) => setSelectedBulkQuals({ ...selectedBulkQuals, hsc: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    HSC
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkQuals.graduate} onChange={(e) => setSelectedBulkQuals({ ...selectedBulkQuals, graduate: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    Graduate
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#475569', cursor: 'pointer' }}>
                                    <input type="checkbox" checked={selectedBulkQuals.postgraduate} onChange={(e) => setSelectedBulkQuals({ ...selectedBulkQuals, postgraduate: e.target.checked })} style={{ accentColor: '#10b981' }} />
                                    Postgraduate
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: '250px' }}>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFileChange}
                            disabled={processingAdmissionsState}
                            style={{
                                width: '100%', padding: '10px', borderRadius: '8px', border: '1px dashed #cbd5e1',
                                background: '#fff', fontSize: '14px', color: '#475569', outline: 'none', cursor: processingAdmissionsState ? 'not-allowed' : 'pointer'
                            }}
                        />
                    </div>
                    <button
                        onClick={handleLoadData}
                        disabled={processingAdmissionsState}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: processingAdmissionsState ? '#94a3b8' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '14px', cursor: processingAdmissionsState ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 8px rgba(16,185,129,0.3)', transition: 'all 0.2s ease'
                        }}
                    >
                        <span>{processingAdmissionsState ? '⚙️ Processing...' : '🚀 Start Bulk Admission'}</span>
                    </button>
                </div>

                {processingAdmissionsState && (
                    <div style={{
                        marginTop: '20px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(16, 185, 129, 0.2)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.01)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#10b981',
                                    boxShadow: '0 0 8px #10b981',
                                    animation: 'pulse 1.5s infinite ease-in-out'
                                }}></span>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Enrolling students...</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>{admissionProgress.current} / {admissionProgress.total}</span>
                                <span style={{
                                    background: '#10b98115',
                                    color: '#10b981',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: 700
                                }}>{admissionProgress.total > 0 ? Math.round((admissionProgress.current / admissionProgress.total) * 100) : 0}%</span>
                            </div>
                        </div>
                        <div style={{
                            width: '100%',
                            height: '8px',
                            background: '#f1f5f9',
                            borderRadius: '9999px',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <div style={{
                                width: `${admissionProgress.total > 0 ? (admissionProgress.current / admissionProgress.total) * 100 : 0}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                                borderRadius: '9999px',
                                transition: 'width 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                                    backgroundSize: '40px 40px',
                                    animation: 'shimmer 1s linear infinite'
                                }}></div>
                            </div>
                        </div>
                    </div>
                )}

                {successMessage && (
                    <div style={{ marginTop: '15px', background: '#ecfdf5', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#065f46', border: '1px solid #a7f3d0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '16px' }}>✅</span>
                            <span>{successMessage}</span>
                        </div>
                        {processedAdmissions.length > 0 && (
                            <button
                                onClick={() => {
                                    document.body.classList.add('print-processed-admissions');
                                    window.print();
                                    setTimeout(() => {
                                        document.body.classList.remove('print-processed-admissions');
                                    }, 500);
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', border: 'none', padding: '10px 20px', borderRadius: '6px',
                                    color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer', alignSelf: 'flex-start',
                                    boxShadow: '0 2px 4px rgba(16,185,129,0.2)', transition: 'all 0.2s ease', marginTop: '5px'
                                }}
                            >
                                🖨️ Print Admission Forms for Admitted Students ({processedAdmissions.length})
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ===== BULK FEE PAYMENTS & RECEIPTS SECTION ===== */}
            <div style={{ background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)', padding: '25px', borderRadius: '12px', border: '1px solid #ddd6fe', marginBottom: '25px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>💳</span>
                        <div>
                            <h3 style={{ fontSize: '18px', color: '#1e293b', margin: 0, fontWeight: 700 }}>Bulk Fee Payments & Receipts</h3>
                            <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>Upload an Excel file to log payments and update fee balances in bulk</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setPrintBlank('receipt')}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none', padding: '8px 16px', borderRadius: '6px',
                            color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(59,130,246,0.2)', transition: 'all 0.2s ease'
                        }}
                    >
                        <span>📄 Print Blank Fee Receipt (PDF)</span>
                    </button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: '250px' }}>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleFeeFileChange}
                            disabled={processingFees}
                            style={{
                                width: '100%', padding: '10px', borderRadius: '8px', border: '1px dashed #cbd5e1',
                                background: '#fff', fontSize: '14px', color: '#475569', outline: 'none', cursor: processingFees ? 'not-allowed' : 'pointer'
                            }}
                        />
                    </div>
                    <button
                        onClick={handleLoadFeeReceipts}
                        disabled={processingFees}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: processingFees ? '#94a3b8' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '14px', cursor: processingFees ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 8px rgba(139,92,246,0.3)', transition: 'all 0.2s ease'
                        }}
                    >
                        <span>{processingFees ? '⚙️ Processing...' : '🚀 Process Fee Receipts'}</span>
                    </button>
                </div>

                {processingFees && (
                    <div style={{
                        marginTop: '20px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        padding: '20px',
                        borderRadius: '12px',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.04), 0 4px 6px -2px rgba(0, 0, 0, 0.01)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    background: '#8b5cf6',
                                    boxShadow: '0 0 8px #8b5cf6',
                                    animation: 'pulse 1.5s infinite ease-in-out'
                                }}></span>
                                <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>Generating receipts...</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b' }}>{feeProgress.current} / {feeProgress.total}</span>
                                <span style={{
                                    background: '#8b5cf615',
                                    color: '#8b5cf6',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '11px',
                                    fontWeight: 700
                                }}>{feeProgress.total > 0 ? Math.round((feeProgress.current / feeProgress.total) * 100) : 0}%</span>
                            </div>
                        </div>
                        <div style={{
                            width: '100%',
                            height: '8px',
                            background: '#f1f5f9',
                            borderRadius: '9999px',
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <div style={{
                                width: `${feeProgress.total > 0 ? (feeProgress.current / feeProgress.total) * 100 : 0}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #8b5cf6 0%, #a78bfa 100%)',
                                borderRadius: '9999px',
                                transition: 'width 0.3s ease',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: 0, left: 0, right: 0, bottom: 0,
                                    backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%, transparent)',
                                    backgroundSize: '40px 40px',
                                    animation: 'shimmer 1s linear infinite'
                                }}></div>
                            </div>
                        </div>
                    </div>
                )}

                {feeSuccessMessage && (
                    <div style={{ marginTop: '15px', background: '#f5f3ff', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13px', color: '#5b21b6', border: '1px solid #ddd6fe' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '16px' }}>✅</span>
                            <span>{feeSuccessMessage}</span>
                        </div>
                        {processedStudents.length > 0 && (
                            <button
                                onClick={() => {
                                    document.body.classList.add('print-processed-receipts');
                                    window.print();
                                    setTimeout(() => {
                                        document.body.classList.remove('print-processed-receipts');
                                    }, 500);
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', border: 'none', padding: '10px 20px', borderRadius: '6px',
                                    color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer', alignSelf: 'flex-start',
                                    boxShadow: '0 2px 4px rgba(139,92,246,0.2)', transition: 'all 0.2s ease', marginTop: '5px'
                                }}
                            >
                                🖨️ Print Receipts for Processed Students ({processedStudents.length})
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* ===== DOWNLOAD ATTENDANCE REPORT SECTION ===== */}
            <div style={{ background: 'linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%)', padding: '25px', borderRadius: '12px', border: '1px solid #d0d8f0', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                    <span style={{ fontSize: '24px' }}>📊</span>
                    <div>
                        <h3 style={{ fontSize: '18px', color: '#1e293b', margin: 0, fontWeight: 700 }}>Download Attendance Report</h3>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0 0' }}>Select course, month & year to download attendance data as Excel</p>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                    {/* Course / Department Selector */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Course / Department</label>
                        <select
                            value={reportDepartment}
                            onChange={(e) => setReportDepartment(e.target.value)}
                            style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '14px', color: '#1e293b', outline: 'none', cursor: 'pointer', appearance: 'auto', boxSizing: 'border-box' }}
                        >
                            {courseOptions.map(c => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>

                    {/* Month Selector */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Month</label>
                        <select
                            value={reportMonth}
                            onChange={(e) => setReportMonth(e.target.value)}
                            style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '14px', color: '#1e293b', outline: 'none', cursor: 'pointer', appearance: 'auto', boxSizing: 'border-box' }}
                        >
                            {monthNames.map((m, idx) => (
                                <option key={m} value={String(idx + 1).padStart(2, '0')}>{m}</option>
                            ))}
                        </select>
                    </div>

                    {/* Year Selector */}
                    <div>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Year</label>
                        <select
                            value={reportYear}
                            onChange={(e) => setReportYear(e.target.value)}
                            style={{ width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '14px', color: '#1e293b', outline: 'none', cursor: 'pointer', appearance: 'auto', boxSizing: 'border-box' }}
                        >
                            <option value="2026">2026</option>
                            <option value="2027">2027</option>
                            <option value="2028">2028</option>
                            <option value="2029">2029</option>
                            <option value="2030">2030</option>
                        </select>
                    </div>
                </div>

                {/* Info bar */}
                <div style={{ background: '#e0e7ff', borderRadius: '8px', padding: '12px 16px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#3730a3' }}>
                    <span style={{ fontSize: '16px' }}>ℹ️</span>
                    <span>
                        Generating report for <strong>{reportDepartment === 'All' ? 'All Departments' : reportDepartment}</strong> — <strong>{monthNames[parseInt(reportMonth) - 1]} {reportYear}</strong>
                    </span>
                </div>

                {/* Download Buttons */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <button
                        onClick={handleDownloadMonthly}
                        disabled={downloading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            background: downloading ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '14px', cursor: downloading ? 'wait' : 'pointer',
                            boxShadow: '0 2px 8px rgba(59,130,246,0.3)', transition: 'all 0.2s ease'
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                        {downloading ? 'Downloading...' : `Download ${monthNames[parseInt(reportMonth) - 1]} ${reportYear} Report`}
                    </button>

                    <button
                        onClick={handleDownloadYearly}
                        disabled={downloading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            background: downloading ? '#94a3b8' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '14px', cursor: downloading ? 'wait' : 'pointer',
                            boxShadow: '0 2px 8px rgba(139,92,246,0.3)', transition: 'all 0.2s ease'
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                        {downloading ? 'Downloading...' : `Download Full Year ${reportYear} Report (All 12 Months)`}
                    </button>
                </div>
            </div>

            {/* Hidden blank containers for format printing */}
            {printBlank === 'admission' && (
                <div className="blank-admission-container" style={{ display: 'none' }}>
                    <AdmissionFormPrintView student={blankStudent} />
                </div>
            )}
            {printBlank === 'receipt' && (
                <div className="blank-receipt-container" style={{ display: 'none' }}>
                    <ReceiptPrintView student={blankStudent} />
                </div>
            )}

            {/* Stylesheet overrides for blank template printing */}
            <style>{`
                @media print {
                    body.print-blank-admission * {
                        visibility: hidden;
                    }
                    body.print-blank-admission .blank-admission-container,
                    body.print-blank-admission .blank-admission-container * {
                        visibility: visible;
                    }
                    body.print-blank-admission .blank-admission-container {
                        display: block !important;
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        right: 0 !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        z-index: 999999 !important;
                    }
                    body.print-blank-admission .blank-admission-container .admission-print-container {
                        display: block !important;
                        position: relative !important;
                        margin: 0 auto !important;
                        padding: 8mm 10mm 8mm 10mm !important;
                    }

                    body.print-blank-receipt * {
                        visibility: hidden;
                    }
                    body.print-blank-receipt .blank-receipt-container,
                    body.print-blank-receipt .blank-receipt-container * {
                        visibility: visible;
                    }
                    body.print-blank-receipt .blank-receipt-container {
                        display: block !important;
                        position: fixed !important;
                        left: 0 !important;
                        top: 0 !important;
                        right: 0 !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        z-index: 999999 !important;
                    }
                    body.print-blank-receipt .blank-receipt-container .receipt-print-container {
                        display: block !important;
                        position: relative !important;
                        margin: 0 auto !important;
                        padding: 8mm 10mm 8mm 10mm !important;
                    }

                    body.print-processed-receipts * {
                        visibility: hidden;
                    }
                    body.print-processed-receipts .processed-receipts-print-container,
                    body.print-processed-receipts .processed-receipts-print-container * {
                        visibility: visible;
                    }
                    body.print-processed-receipts .processed-receipts-print-container {
                        display: block !important;
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    body.print-processed-receipts .processed-receipts-print-container .receipt-print-container {
                        display: block !important;
                        page-break-after: always;
                        position: relative !important;
                    }

                    body.print-processed-admissions * {
                        visibility: hidden;
                    }
                    body.print-processed-admissions .processed-admissions-print-container,
                    body.print-processed-admissions .processed-admissions-print-container * {
                        visibility: visible;
                    }
                    body.print-processed-admissions .processed-admissions-print-container {
                        display: block !important;
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    body.print-processed-admissions .processed-admissions-print-container .printable-admission-view {
                        display: block !important;
                        page-break-after: always;
                        position: relative !important;
                    }
                }
            `}</style>

            {processedStudents.length > 0 && (
                <div className="processed-receipts-print-container" style={{ display: 'none' }}>
                    {processedStudents.map((s, idx) => (
                        <ReceiptPrintView key={`proc-rec-${s.id || idx}`} student={s} />
                    ))}
                </div>
            )}

            {processedAdmissions.length > 0 && (
                <div className="processed-admissions-print-container" style={{ display: 'none' }}>
                    {processedAdmissions.map((s, idx) => (
                        <AdmissionFormPrintView key={`proc-adm-${s.id || idx}`} student={s} />
                    ))}
                </div>
            )}
        </div>
    );
};

const AdmissionsPage = () => {
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [printStudent, setPrintStudent] = useState(null);
    const [attendanceLogs, setAttendanceLogs] = useState([]);
    const [editAttendance, setEditAttendance] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);

    const filteredStudents = students.filter(s => {
        const term = searchTerm.toLowerCase();
        return (
            s.name.toLowerCase().includes(term) ||
            (s.fingerprint_id && String(s.fingerprint_id).toLowerCase().includes(term)) ||
            (s.email && s.email.toLowerCase().includes(term)) ||
            (s.section && s.section.toLowerCase().includes(term))
        );
    });

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = filteredStudents.map(s => String(s.fingerprint_id || s.id || s._id));
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id) => {
        const strId = String(id);
        setSelectedIds(prev =>
            prev.includes(strId) ? prev.filter(i => i !== strId) : [...prev, strId]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedIds.length === 0) return;
        if (window.confirm(`⚠️ Are you sure you want to delete all ${selectedIds.length} selected student(s)? This action cannot be undone.`)) {
            try {
                await api.post('/users/bulk-delete', { ids: selectedIds });
                alert(`Successfully deleted ${selectedIds.length} student(s)!`);
                setStudents(prev => prev.filter(s => {
                    const sId = String(s.fingerprint_id || s.id || s._id);
                    return !selectedIds.includes(sId);
                }));
                setSelectedIds([]);
            } catch (err) {
                console.error("Bulk delete endpoint failed, running fallback sequential delete...", err);
                let count = 0;
                for (const id of selectedIds) {
                    try {
                        const cleanId = encodeURIComponent(String(id).trim());
                        await api.delete(`/users/${cleanId}`);
                        count++;
                    } catch (e) {
                        console.error(`Failed deleting student ID ${id}`, e);
                    }
                }
                if (count > 0) {
                    alert(`Successfully deleted ${count} student(s)!`);
                    setStudents(prev => prev.filter(s => !selectedIds.includes(String(s.fingerprint_id || s.id || s._id))));
                    setSelectedIds([]);
                } else {
                    alert("Failed to complete bulk deletion. Please try again.");
                }
            }
        }
    };

    const handleSaveAttendance = async () => {
        try {
            await api.put('/attendance/update', {
                userId: editAttendance.userId,
                date: editAttendance.date,
                inTime: editAttendance.inTime,
                outTime: editAttendance.outTime
            });
            // Reload attendance logs
            const res = await api.get(`/attendance/student/${editAttendance.userId}`);
            setAttendanceLogs(res.data.records || []);
            setEditAttendance(null);
        } catch (err) {
            alert('Failed to update attendance');
        }
    };

    useEffect(() => {
        if (selectedStudent) {
            api.get(`/attendance/student/${selectedStudent.fingerprint_id}`)
                .then(res => {
                    setAttendanceLogs(res.data.records || []);
                })
                .catch(err => console.error("Error fetching attendance:", err));
        } else {
            setAttendanceLogs([]);
        }
    }, [selectedStudent]);

    useEffect(() => {
        api.get('/users').then(res => {
            setStudents(res.data.filter(u => u.role === 'student'));
        }).catch(err => console.error(err));
    }, []);

    const handleDelete = async (fingerprint_id, name) => {
        if (window.confirm(`Are you sure you want to delete ${name}'s profile?`)) {
            try {
                await api.delete(`/users/${fingerprint_id}`);
                alert(`Successfully deleted ${name}!`);
                setStudents(prev => prev.filter(s => 
                    String(s.fingerprint_id) !== String(fingerprint_id) && 
                    String(s.id) !== String(fingerprint_id) &&
                    String(s._id) !== String(fingerprint_id)
                ));
                if (selectedStudent && (selectedStudent.fingerprint_id === fingerprint_id || selectedStudent.id === fingerprint_id)) {
                    setSelectedStudent(null);
                }
            } catch (err) {
                console.error("Error deleting student:", err);
                const errorMsg = err.response && err.response.data && err.response.data.error
                    ? err.response.data.error
                    : err.message;
                alert(`Failed to delete student: ${errorMsg}`);
            }
        }
    };

    return (
        <div className="dashboard-card">
            <div className="card-header">
                <h2>📝 Student List & Admissions</h2>
                <div className="actions" style={{ display: 'flex', gap: '10px' }}>
                    <Link to="/quick-enroll" className="btn btn-primary" style={{ background: '#0284c7', borderColor: '#0284c7' }}>⚡ Quick Enroll</Link>
                    <Link to="/enroll-student" className="btn btn-primary">+ New Enrollment</Link>
                </div>
            </div>

            {/* ===== QUICK PRINT SECTION ===== */}
            <div style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', padding: '20px', borderRadius: '12px', border: '1px solid #bbf7d0', marginBottom: '25px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                    <span style={{ fontSize: '24px' }}>🖨️</span>
                    <div>
                        <h3 style={{ fontSize: '16px', color: '#1e293b', margin: 0, fontWeight: 700 }}>Quick Print Documents</h3>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '2px 0 0 0' }}>Select a student to download or print their Admission Form or Fee Receipt</p>
                    </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: '220px' }}>
                        <select
                            value={printStudent ? printStudent.fingerprint_id : ''}
                            onChange={(e) => {
                                const sObj = students.find(s => s.fingerprint_id === e.target.value);
                                setPrintStudent(sObj || null);
                            }}
                            style={{
                                width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #cbd5e1',
                                background: '#fff', fontSize: '14px', color: '#1e293b', outline: 'none', cursor: 'pointer'
                            }}
                        >
                            <option value="">-- Choose Student --</option>
                            {students.map(s => (
                                <option key={s.fingerprint_id} value={s.fingerprint_id}>{s.name} (Reg No: {s.fingerprint_id})</option>
                            ))}
                        </select>
                    </div>

                    <button
                        onClick={() => {
                            if (!printStudent) return alert("Please choose a student.");
                            document.body.classList.add('print-admission');
                            window.print();
                            setTimeout(() => document.body.classList.remove('print-admission'), 500);
                        }}
                        disabled={!printStudent}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: !printStudent ? '#cbd5e1' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '13px', cursor: !printStudent ? 'not-allowed' : 'pointer',
                            boxShadow: printStudent ? '0 2px 4px rgba(16,185,129,0.2)' : 'none', transition: 'all 0.2s ease'
                        }}
                    >
                        🖨️ Print Admission Form
                    </button>

                    <button
                        onClick={() => {
                            if (!printStudent) return alert("Please choose a student.");
                            document.body.classList.add('print-receipt');
                            window.print();
                            setTimeout(() => document.body.classList.remove('print-receipt'), 500);
                        }}
                        disabled={!printStudent}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: !printStudent ? '#cbd5e1' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '13px', cursor: !printStudent ? 'not-allowed' : 'pointer',
                            boxShadow: printStudent ? '0 2px 4px rgba(59,130,246,0.2)' : 'none', transition: 'all 0.2s ease'
                        }}
                    >
                        🖨️ Print Fee Receipt
                    </button>

                    <span style={{ color: '#cbd5e1', fontSize: '20px' }}>|</span>

                    <button
                        onClick={() => {
                            if (students.length === 0) return alert("No students to print.");
                            document.body.classList.add('print-all-admissions');
                            window.print();
                            setTimeout(() => document.body.classList.remove('print-all-admissions'), 500);
                        }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: '#047857', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(4,120,87,0.2)', transition: 'all 0.2s ease'
                        }}
                    >
                        🗂️ Print All Admissions (PDF)
                    </button>

                    <button
                        onClick={() => {
                            if (students.length === 0) return alert("No students to print.");
                            document.body.classList.add('print-all-receipts');
                            window.print();
                            setTimeout(() => document.body.classList.remove('print-all-receipts'), 500);
                        }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            background: '#1d4ed8', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px',
                            fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(29,78,216,0.2)', transition: 'all 0.2s ease'
                        }}
                    >
                        🗂️ Print All Receipts (PDF)
                    </button>
                </div>
            </div>

            {/* ===== SEARCH BAR & BULK ACTIONS SECTION ===== */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '15px', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, maxWidth: '500px' }}>
                    <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', color: '#94a3b8', pointerEvents: 'none' }}>🔍</span>
                    <input
                        type="text"
                        placeholder="Search by student name, registration number, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%', padding: '10px 16px 10px 40px', borderRadius: '8px', border: '1px solid #cbd5e1',
                            fontSize: '14px', color: '#1e293b', outline: 'none', background: '#fff', transition: 'all 0.2s ease',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}
                    />
                    {searchTerm && (
                        <button
                            onClick={() => setSearchTerm('')}
                            style={{
                                position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', fontSize: '14px', color: '#94a3b8', cursor: 'pointer',
                                padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                        >
                            ❌
                        </button>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {selectedIds.length > 0 && (
                        <button
                            onClick={handleBulkDelete}
                            style={{
                                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                color: '#fff',
                                border: 'none',
                                padding: '9px 18px',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '13px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            🗑️ Delete Selected ({selectedIds.length})
                        </button>
                    )}
                    <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
                        {searchTerm ? `Found ${filteredStudents.length} of ${students.length} students` : `Total: ${students.length} students`}
                    </div>
                </div>
            </div>

            <table className="dashboard-table">
                <thead>
                    <tr>
                        <th style={{ width: '40px', textAlign: 'center' }}>
                            <input
                                type="checkbox"
                                checked={filteredStudents.length > 0 && selectedIds.length === filteredStudents.length}
                                onChange={handleSelectAll}
                                style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#ef4444' }}
                                title="Select All Students"
                            />
                        </th>
                        <th>Sr. No.</th>
                        <th>Student Name</th>
                        <th>Register No.</th>
                        <th>Email Address</th>
                        <th>Section</th>
                        <th>Enrollment Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredStudents.map((s, idx) => {
                        const sId = String(s.fingerprint_id || s.id || s._id);
                        const isSelected = selectedIds.includes(sId);
                        return (
                            <tr key={s.id || idx} style={{ background: isSelected ? '#fef2f2' : 'transparent', transition: 'background 0.15s ease' }}>
                                <td style={{ textAlign: 'center' }}>
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleSelectOne(sId)}
                                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#ef4444' }}
                                    />
                                </td>
                                <td style={{ fontWeight: 'bold', color: '#64748b' }}>{idx + 1}</td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {s.photo ? (
                                            <img src={s.photo.startsWith('http') || s.photo.startsWith('data:') ? s.photo : `http://${backendHost}:3000${s.photo}`} alt="Profile" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #ccc' }} />
                                        ) : (
                                            <div className="avatar" style={{ background: '#3498db', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', borderRadius: '50%', fontWeight: 'bold', fontSize: '14px' }}>{s.name.charAt(0)}</div>
                                        )}
                                        {s.name}
                                    </div>
                                </td>
                                <td><code style={{ background: '#f0f2f5', padding: '4px 8px', borderRadius: '4px' }}>{s.fingerprint_id}</code></td>
                                <td>{s.email || 'N/A'}</td>
                                <td>{s.section || 'N/A'}</td>
                                <td>{new Date().toLocaleDateString('en-GB')}</td>
                                <td><span className="status approved">Enrolled</span></td>
                                <td>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <Link className="btn btn-primary" style={{ padding: '4px 10px', textDecoration: 'none', display: 'inline-block' }} to={`/student/${s.fingerprint_id}`}>Profile</Link>
                                        <button className="btn btn-primary" style={{ padding: '4px 10px', background: '#ef4444', borderColor: '#ef4444' }} onClick={() => handleDelete(s.fingerprint_id || s.id || s._id, s.name)}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    {filteredStudents.length === 0 && (
                        <tr>
                            <td colSpan="9" style={{ textAlign: 'center', padding: '30px', color: '#64748b', fontSize: '15px' }}>
                                🔍 No matching students found.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {selectedStudent && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '600px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <div className="avatar" style={{ background: '#8b5cf6', width: '50px', height: '50px', fontSize: '24px' }}>{selectedStudent.name.charAt(0)}</div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '20px', color: '#333' }}>{selectedStudent.name}</h3>
                                    <div style={{ color: '#888', fontSize: '13px' }}>Enrolled • Register No: {selectedStudent.fingerprint_id}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => window.print()} style={{ background: '#8b5cf6', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontWeight: 'bold' }}>🖨️ Print Receipt</button>
                                <button onClick={() => setSelectedStudent(null)} style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '50%', width: '32px', height: '32px', fontSize: '18px', cursor: 'pointer', color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '14px', color: '#555' }}>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Biometric ID / Reg No</strong> {selectedStudent.fingerprint_id || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Email Id</strong> {selectedStudent.email || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Student Phone</strong> {selectedStudent.studentPhone || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Parent Phone</strong> {selectedStudent.parentPhone || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Aadhar Number</strong> {selectedStudent.aadhar || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Gender</strong> {selectedStudent.gender || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Date of Birth</strong> {selectedStudent.dob || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>10th Marks</strong> {selectedStudent.marks10th ? `${selectedStudent.marks10th}%` : 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>12th Marks</strong> {selectedStudent.marks12th ? `${selectedStudent.marks12th}%` : 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Enquiry Date</strong> {selectedStudent.enquiryDate || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Previous School</strong> {selectedStudent.previousSchool || 'N/A'}</div>
                            <div style={{ gridColumn: '1 / -1' }}><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Full Address</strong> {selectedStudent.address || 'N/A'}</div>
                            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #eee', paddingTop: '20px', marginTop: '5px' }}></div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Branch</strong> {selectedStudent.branch || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Enrolled Courses</strong> {selectedStudent.courses?.join(', ') || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Batch Timing</strong> {selectedStudent.batchTiming || 'N/A'}</div>

                            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #eee', paddingTop: '20px', marginTop: '5px' }}></div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Bank Name</strong> {selectedStudent.bankName || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Account Holder</strong> {selectedStudent.accountHolder || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Account Number</strong> {selectedStudent.accountNumber || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>IFSC Code</strong> {selectedStudent.ifscCode || 'N/A'}</div>

                            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #eee', paddingTop: '20px', marginTop: '5px' }}></div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Installment Enabled</strong> {selectedStudent.installment || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Payment Mode</strong> {selectedStudent.paymentMode || 'N/A'}</div>
                            <div><strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Amount Received</strong> {selectedStudent.amountReceived ? `₹${selectedStudent.amountReceived}` : 'N/A'}</div>
                            <div style={{ gridColumn: '1 / -1', background: '#f3e8ff', padding: '15px', borderRadius: '8px', color: '#6b21a8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <strong style={{ fontSize: '15px' }}>Total Net Fees</strong>
                                <span style={{ fontSize: '20px', fontWeight: 'bold' }}>₹{selectedStudent.fee || 0}</span>
                            </div>

                            <div style={{ gridColumn: '1 / -1', marginTop: '20px' }}>
                                <h4 style={{ color: '#333', borderBottom: '2px solid #8b5cf6', paddingBottom: '8px', marginBottom: '15px' }}>📅 Attendance Logs</h4>
                                {attendanceLogs && attendanceLogs.length > 0 ? (
                                    <table className="dashboard-table" style={{ width: '100%', fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>In Time</th>
                                                <th>Out Time</th>
                                                <th>Estimated Hours</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {attendanceLogs.map((log, i) => (
                                                <tr key={i}>
                                                    <td>{log.date}</td>
                                                    <td><span className="status approved">{log.firstIn}</span></td>
                                                    <td><span className="status error">{log.lastOut}</span></td>
                                                    <td><strong>{log.durationMinutes} mins</strong> ({(log.durationMinutes / 60).toFixed(1)} hrs)</td>
                                                    <td>
                                                        <button onClick={() => setEditAttendance({ userId: selectedStudent.fingerprint_id, date: log.date, inTime: log.firstIn, outTime: log.lastOut === 'N/A' ? '' : log.lastOut, name: selectedStudent.name })} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '16px' }}>✎</button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center', color: '#666' }}>
                                        No attendance records found for this student.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Attendance Modal */}
            {editAttendance && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '400px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0 }}>Edit Attendance for {editAttendance.name}</h3>

                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>In Time</label>
                            <input
                                type="text"
                                value={editAttendance.inTime}
                                onChange={(e) => setEditAttendance({ ...editAttendance, inTime: e.target.value })}
                                placeholder="e.g. 09:30 AM"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Out Time</label>
                            <input
                                type="text"
                                value={editAttendance.outTime}
                                onChange={(e) => setEditAttendance({ ...editAttendance, outTime: e.target.value })}
                                placeholder="e.g. 05:30 PM"
                                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button onClick={() => setEditAttendance(null)} className="btn" style={{ background: '#eee', color: '#333' }}>Cancel</button>
                            <button onClick={handleSaveAttendance} className="btn btn-primary">Save Changes</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Hidden receipt for printing */}
            <ReceiptPrintView student={selectedStudent} />

            {/* Hidden components for dropdown quick printing */}
            {printStudent && (
                <>
                    <ReceiptPrintView student={printStudent} />
                    <AdmissionFormPrintView student={printStudent} />
                </>
            )}

            {/* Hidden containers for batch printing ALL students */}
            <div className="all-admissions-print-container" style={{ display: 'none' }}>
                {students.map(s => (
                    <AdmissionFormPrintView key={`all-adm-${s.fingerprint_id}`} student={s} />
                ))}
            </div>

            <div className="all-receipts-print-container" style={{ display: 'none' }}>
                {students.map(s => (
                    <ReceiptPrintView key={`all-rec-${s.fingerprint_id}`} student={s} />
                ))}
            </div>

            {/* Stylesheet overrides for batch printing */}
            <style>{`
                @media print {
                    body.print-all-admissions * {
                        visibility: hidden;
                    }
                    body.print-all-admissions .all-admissions-print-container,
                    body.print-all-admissions .all-admissions-print-container * {
                        visibility: visible;
                    }
                    body.print-all-admissions .all-admissions-print-container {
                        display: block !important;
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    body.print-all-admissions .all-admissions-print-container .admission-print-container {
                        display: block !important;
                        page-break-after: always;
                        position: relative !important;
                    }

                    body.print-all-receipts * {
                        visibility: hidden;
                    }
                    body.print-all-receipts .all-receipts-print-container,
                    body.print-all-receipts .all-receipts-print-container * {
                        visibility: visible;
                    }
                    body.print-all-receipts .all-receipts-print-container {
                        display: block !important;
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    body.print-all-receipts .all-receipts-print-container .receipt-print-container {
                        display: block !important;
                        page-break-after: always;
                        position: relative !important;
                    }
                }
            `}</style>
        </div>
    );
};

const TeachersPage = () => {
    const [teachers, setTeachers] = useState([]);
    const [rawTeachers, setRawTeachers] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showGlobalSummary, setShowGlobalSummary] = useState(false);
    const [summarySearchTerm, setSummarySearchTerm] = useState('');
    const [editId, setEditId] = useState('');
    const [editMongoId, setEditMongoId] = useState('');
    const [printingTeacher, setPrintingTeacher] = useState(null);
    const [sectionFilter, setSectionFilter] = useState('all');

    const [showTimetableModal, setShowTimetableModal] = useState(false);
    const [timetablePhoto, setTimetablePhoto] = useState(null);
    const [scienceTimetablePhoto, setScienceTimetablePhoto] = useState(null);
    const [careerTimetablePhoto, setCareerTimetablePhoto] = useState(null);
    const [selectedUploadDept, setSelectedUploadDept] = useState('science');

    const getTodayWeekday = () => {
        const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        if (['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].includes(todayName)) {
            return todayName;
        }
        return 'Monday';
    };

    const [showWhiteboardModal, setShowWhiteboardModal] = useState(false);
    const [whiteboardTab, setWhiteboardTab] = useState('science'); // 'science' or 'career'
    const [selectedDayTab, setSelectedDayTab] = useState(getTodayWeekday); // Auto-detect real today (e.g. Saturday)
    const [dayWiseWhiteboard, setDayWiseWhiteboard] = useState({
        Monday: [
            { slot: '07:30 AM - 09:00 AM', c11: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, c12: { subject: 'Biology (L)', teacher: 'sdt', color: '#ec4899' }, career: { subject: 'JEE Foundation', teacher: 'Siraa', color: '#0284c7' } },
            { slot: '09:00 AM - 10:30 AM', c11: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, c12: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, career: { subject: 'NEET Prep', teacher: 'Ds', color: '#10b981' } },
            { slot: '10:30 AM - 11:30 AM', isBreak: true, label: '☕ Recess / Break Time' },
            { slot: '11:30 AM - 01:00 PM', c11: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, c12: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, career: { subject: 'Competitive Math', teacher: 'Rtt', color: '#0284c7' } },
            { slot: '01:00 PM - 02:30 PM', c11: { subject: 'Biology (R)', teacher: 'Ds', color: '#ec4899' }, c12: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, career: { subject: 'Special Doubt Batch', teacher: 'Nkt', color: '#f59e0b' } }
        ],
        Tuesday: [
            { slot: '07:30 AM - 09:00 AM', c11: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, c12: { subject: 'Biology (L)', teacher: 'sdt', color: '#ec4899' }, career: { subject: 'JEE Foundation', teacher: 'Siraa', color: '#0284c7' } },
            { slot: '09:00 AM - 10:30 AM', c11: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, c12: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, career: { subject: 'NEET Prep', teacher: 'Ds', color: '#10b981' } },
            { slot: '10:30 AM - 11:30 AM', isBreak: true, label: '☕ Recess / Break Time' },
            { slot: '11:30 AM - 01:00 PM', c11: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, c12: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, career: { subject: 'Competitive Math', teacher: 'Rtt', color: '#0284c7' } },
            { slot: '01:00 PM - 02:30 PM', c11: { subject: 'Biology (R)', teacher: 'Ds', color: '#ec4899' }, c12: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, career: { subject: 'Special Doubt Batch', teacher: 'Nkt', color: '#f59e0b' } }
        ],
        Wednesday: [
            { slot: '07:30 AM - 09:00 AM', c11: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, c12: { subject: 'Biology (L)', teacher: 'sdt', color: '#ec4899' }, career: { subject: 'JEE Foundation', teacher: 'Siraa', color: '#0284c7' } },
            { slot: '09:00 AM - 10:30 AM', c11: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, c12: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, career: { subject: 'NEET Prep', teacher: 'Ds', color: '#10b981' } },
            { slot: '10:30 AM - 11:30 AM', isBreak: true, label: '☕ Recess / Break Time' },
            { slot: '11:30 AM - 01:00 PM', c11: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, c12: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, career: { subject: 'Competitive Math', teacher: 'Rtt', color: '#0284c7' } },
            { slot: '01:00 PM - 02:30 PM', c11: { subject: 'Biology (R)', teacher: 'Ds', color: '#ec4899' }, c12: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, career: { subject: 'Special Doubt Batch', teacher: 'Nkt', color: '#f59e0b' } }
        ],
        Thursday: [
            { slot: '07:30 AM - 09:00 AM', c11: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, c12: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, career: { subject: 'NEET Bio Practice', teacher: 'Ds', color: '#10b981' } },
            { slot: '09:00 AM - 10:30 AM', c11: { subject: 'Biology (R)', teacher: 'Ds', color: '#ec4899' }, c12: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, career: { subject: 'JEE Physics', teacher: 'Pgt', color: '#8b5cf6' } },
            { slot: '10:30 AM - 11:30 AM', isBreak: true, label: '☕ Recess / Break Time' },
            { slot: '11:30 AM - 01:00 PM', c11: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, c12: { subject: 'Biology (L)', teacher: 'sdt', color: '#ec4899' }, career: { subject: 'Foundation Math', teacher: 'Rtt', color: '#0284c7' } },
            { slot: '01:00 PM - 02:30 PM', c11: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, c12: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, career: { subject: 'Doubt & Practice', teacher: 'Siraa', color: '#f59e0b' } }
        ],
        Friday: [
            { slot: '07:30 AM - 09:00 AM', c11: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, c12: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, career: { subject: 'NEET Bio Practice', teacher: 'Ds', color: '#10b981' } },
            { slot: '09:00 AM - 10:30 AM', c11: { subject: 'Biology (R)', teacher: 'Ds', color: '#ec4899' }, c12: { subject: 'Chemistry', teacher: 'Nkt', color: '#16a34a' }, career: { subject: 'JEE Physics', teacher: 'Pgt', color: '#8b5cf6' } },
            { slot: '10:30 AM - 11:30 AM', isBreak: true, label: '☕ Recess / Break Time' },
            { slot: '11:30 AM - 01:00 PM', c11: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, c12: { subject: 'Biology (L)', teacher: 'sdt', color: '#ec4899' }, career: { subject: 'Foundation Math', teacher: 'Rtt', color: '#0284c7' } },
            { slot: '01:00 PM - 02:30 PM', c11: { subject: 'Mathematics', teacher: 'Rtt', color: '#2563eb' }, c12: { subject: 'Physics', teacher: 'Pgt', color: '#8b5cf6' }, career: { subject: 'Doubt & Practice', teacher: 'Siraa', color: '#f59e0b' } }
        ],
        Saturday: [
            { slot: '07:30 AM - 09:00 AM', c11: { subject: 'Weekly Test Batch', teacher: 'All Faculty', color: '#dc2626' }, c12: { subject: 'Weekly Test Batch', teacher: 'All Faculty', color: '#dc2626' }, career: { subject: 'Mock JEE/NEET Test', teacher: 'Siraa', color: '#0284c7' } },
            { slot: '09:00 AM - 11:30 AM', c11: { subject: 'Problem Solving', teacher: 'Rtt / Pgt', color: '#2563eb' }, c12: { subject: 'Problem Solving', teacher: 'Nkt / Ds', color: '#7c3aed' }, career: { subject: 'Test Discussion', teacher: 'All Faculty', color: '#059669' } }
        ]
    });

    const [existingUserBanner, setExistingUserBanner] = useState(null);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('teacher');
    const [memberStatus, setMemberStatus] = useState('Active');
    const [section, setSection] = useState('');
    const [fingerprintId, setFingerprintId] = useState('');
    const [experience, setExperience] = useState('');
    const [subject, setSubject] = useState('');
    const [timing, setTiming] = useState('');
    const [salary, setSalary] = useState('');
    const [dailySalary, setDailySalary] = useState('');
    const [profession, setProfession] = useState('');
    const [dob, setDob] = useState('');
    const [photoData, setPhotoData] = useState(null);
    const [cameraActive, setCameraActive] = useState(false);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const handleBiometricIdChange = async (val) => {
        setFingerprintId(val);
        if (!val || val.trim() === '') {
            setExistingUserBanner(null);
            return;
        }
        const cleanId = val.trim();
        try {
            const res = await api.get(`/users/check-biometric/${cleanId}`);
            if (res.data && res.data.exists && res.data.user) {
                const u = res.data.user;
                if (u.name) setName(u.name);
                if (u.email) setEmail(u.email);
                if (u.photo) setPhotoData(u.photo);
                if (u.section) setSection(u.section);
                if (u.subject) setSubject(u.subject);
                if (u.experience) setExperience(u.experience);
                if (u.profession) setProfession(u.profession);
                if (u.salary) { setSalary(u.salary); setDailySalary((u.salary / 30).toFixed(2)); }
                if (u.timing) setTiming(u.timing);
                if (u.studentPhone || u.studentContact) setPhone(u.studentPhone || u.studentContact);
                if (u.aadhar) setAadhar(u.aadhar);
                if (u.gender) setGender(u.gender);
                if (u.dob) setDob(u.dob);

                setExistingUserBanner(`ℹ️ Existing user "${u.name}" (Biometric ID: ${cleanId}) found. Submitting will update their profile and add them to Teachers & Staff.`);
            } else {
                setExistingUserBanner(null);
            }
        } catch (e) {
            setExistingUserBanner(null);
        }
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            setCameraActive(true);
            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                }
            }, 100);
        } catch (err) {
            alert("Error accessing camera: " + err.message);
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(track => track.stop());
        }
        setCameraActive(false);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            const maxDimension = 300;
            let width = videoRef.current.videoWidth;
            let height = videoRef.current.videoHeight;
            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                } else {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                }
            }
            canvasRef.current.width = width;
            canvasRef.current.height = height;
            context.drawImage(videoRef.current, 0, 0, width, height);
            const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.7);
            setPhotoData(dataUrl);
            stopCamera();
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                compressImage(event.target.result, (compressed) => {
                    setPhotoData(compressed);
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const [startHour, setStartHour] = useState('09');
    const [startMin, setStartMin] = useState('00');
    const [startAmpm, setStartAmpm] = useState('AM');
    const [endHour, setEndHour] = useState('06');
    const [endMin, setEndMin] = useState('00');
    const [endAmpm, setEndAmpm] = useState('PM');

    const [phone, setPhone] = useState('');
    const [aadhar, setAadhar] = useState('');
    const [gender, setGender] = useState('Male');

    const parseTiming = (timingStr) => {
        const defaults = {
            startHour: '09',
            startMin: '00',
            startAmpm: 'AM',
            endHour: '06',
            endMin: '00',
            endAmpm: 'PM'
        };
        if (!timingStr) return defaults;
        const match = timingStr.match(/(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(AM|PM)?\s*-\s*(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(AM|PM)?/i);
        if (match) {
            return {
                startHour: (match[1] || '09').padStart(2, '0'),
                startMin: match[2] || '00',
                startAmpm: (match[3] || 'AM').toUpperCase(),
                endHour: (match[4] || '06').padStart(2, '0'),
                endMin: match[5] || '00',
                endAmpm: (match[6] || 'PM').toUpperCase()
            };
        }
        return defaults;
    };

    const calculateWorkingHours = (sh, sm, sa, eh, em, ea) => {
        let startH = parseInt(sh, 10);
        let startM = parseInt(sm, 10);
        let endH = parseInt(eh, 10);
        let endM = parseInt(em, 10);

        if (isNaN(startH)) startH = 9;
        if (isNaN(startM)) startM = 0;
        if (isNaN(endH)) endH = 18;
        if (isNaN(endM)) endM = 0;

        if (sa === 'PM' && startH !== 12) startH += 12;
        if (sa === 'AM' && startH === 12) startH = 0;

        if (ea === 'PM' && endH !== 12) endH += 12;
        if (ea === 'AM' && endH === 12) endH = 0;

        let startTotalMins = startH * 60 + startM;
        let endTotalMins = endH * 60 + endM;

        if (endTotalMins < startTotalMins) {
            endTotalMins += 24 * 60;
        }

        const diff = (endTotalMins - startTotalMins) / 60;
        return (isNaN(diff) || diff < 0) ? 8 : diff;
    };
    const SHIFT_PRESETS = [
        { label: '⚙️ Custom Shift (Select Below)', startH: null },
        { label: '⏱️ 1.5 Hours Lecture (09:00 AM - 10:30 AM)', startH: '09', startM: '00', startA: 'AM', endH: '10', endM: '30', endA: 'AM' },
        { label: '⏱️ 1.5 Hours Lecture (10:30 AM - 12:00 PM)', startH: '10', startM: '30', startA: 'AM', endH: '12', endM: '00', endA: 'PM' },
        { label: '⏱️ 1.5 Hours Lecture (01:00 PM - 02:30 PM)', startH: '01', startM: '00', startA: 'PM', endH: '02', endM: '30', endA: 'PM' },
        { label: '⏱️ 1.5 Hours Lecture (02:30 PM - 04:00 PM)', startH: '02', startM: '30', startA: 'PM', endH: '04', endM: '00', endA: 'PM' },
        { label: '🌅 4.0 Hours Half Day (09:00 AM - 01:00 PM)', startH: '09', startM: '00', startA: 'AM', endH: '01', endM: '00', endA: 'PM' },
        { label: '☀️ 8.0 Hours Full Day (09:00 AM - 05:00 PM)', startH: '09', startM: '00', startA: 'AM', endH: '05', endM: '00', endA: 'PM' },
        { label: '🏢 9.0 Hours Full Day (09:00 AM - 06:00 PM)', startH: '09', startM: '00', startA: 'AM', endH: '06', endM: '00', endA: 'PM' }
    ];

    const [customTimingText, setCustomTimingText] = useState('');

    const applyShiftPreset = (presetIndex) => {
        const idx = parseInt(presetIndex, 10);
        if (!isNaN(idx) && SHIFT_PRESETS[idx] && SHIFT_PRESETS[idx].startH) {
            const p = SHIFT_PRESETS[idx];
            setStartHour(p.startH);
            setStartMin(p.startM);
            setStartAmpm(p.startA);
            setEndHour(p.endH);
            setEndMin(p.endM);
            setEndAmpm(p.endA);
            const hrs = calculateWorkingHours(p.startH, p.startM, p.startA, p.endH, p.endM, p.endA);
            setCustomTimingText(`${p.startH}:${p.startM} ${p.startA} - ${p.endH}:${p.endM} ${p.endA} (${hrs.toFixed(1)} hrs)`);
        }
    };

    const [batch, setBatch] = useState('11th PCMB');
    const [otherBatch, setOtherBatch] = useState('');

    const fetchTeachers = () => {
        // Fetch raw DB records for edit operations (has _id, fingerprint_id reliably)
        api.get('/teachers').then(res => {
            setRawTeachers(res.data);
        }).catch(err => console.error('rawTeachers fetch error:', err));
        // Fetch attendance+payroll summary for display
        api.get('/staff/payroll-summary').then(res => {
            setTeachers(res.data);
        }).catch(err => console.error('payroll summary fetch error:', err));
    };

    useEffect(() => {
        fetchTeachers();
        const handleLivePunch = () => {
            fetchTeachers();
        };
        socket.on('live_punch', handleLivePunch);
        return () => {
            socket.off('live_punch', handleLivePunch);
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (email && email.trim() !== '') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(email.trim())) {
                alert("Please enter a valid email address (e.g. example@gmail.com).");
                return;
            }
        }
        const finalBatch = batch === 'Other' ? otherBatch : batch;
        const workingHours = calculateWorkingHours(startHour, startMin, startAmpm, endHour, endMin, endAmpm);
        const autoTiming = `${startHour}:${startMin} ${startAmpm} - ${endHour}:${endMin} ${endAmpm} (${workingHours.toFixed(1)} hrs)`;
        const finalTiming = (customTimingText && customTimingText.trim()) ? customTimingText.trim() : autoTiming;
        try {
            const payload = {
                name,
                email,
                role: (role || 'staff').toLowerCase(),
                status: memberStatus || 'Active',
                section: section,
                fingerprint_id: fingerprintId,
                experience,
                subject,
                timing: finalTiming,
                salary: salary ? parseFloat(salary) : 0,
                profession,
                batch: finalBatch,
                studentContact: phone,
                studentPhone: phone,
                aadhar,
                gender,
                dob,
                photo: photoData,
                isManuallyAdded: true
            };
            if (editId || editMongoId) {
                const updateId = editMongoId || editId;
                let updated = false;
                // Strategy 1: PUT by MongoDB _id (most reliable)
                if (editMongoId) {
                    try {
                        await api.put(`/users/${editMongoId}`, payload);
                        updated = true;
                    } catch (e1) {
                        console.warn('PUT by _id failed:', e1.message);
                    }
                }
                // Strategy 2: PUT by fingerprint_id
                if (!updated && payload.fingerprint_id) {
                    try {
                        await api.put(`/users/${payload.fingerprint_id}`, payload);
                        updated = true;
                    } catch (e2) {
                        console.warn('PUT by fingerprint_id failed:', e2.message);
                    }
                }
                // Strategy 3: Fallback - POST to enroll (upserts by fingerprint_id)
                if (!updated) {
                    try {
                        await api.post('/users/enroll', payload);
                        updated = true;
                    } catch (e3) {
                        throw new Error('All update strategies failed: ' + e3.message);
                    }
                }
                alert(`✅ Successfully updated ${name}'s profile!`);
            } else {
                await api.post('/users/enroll', payload);
                alert(`✅ Successfully saved ${name}!`);
            }
            setShowModal(false);
            setEditMongoId('');
            stopCamera();
            setName(''); setEmail(''); setFingerprintId(''); setExperience(''); setSubject(''); setTiming(''); setCustomTimingText(''); setSalary(''); setDailySalary(''); setProfession(''); setBatch('11th PCMB'); setOtherBatch(''); setPhotoData(null); setDob(''); setSection(''); setMemberStatus('Active');
            setStartHour('09'); setStartMin('00'); setStartAmpm('AM'); setEndHour('06'); setEndMin('00'); setEndAmpm('PM');
            setPhone(''); setAadhar(''); setGender('Male');
            fetchTeachers();
        } catch (err) {
            alert("Error saving " + role + ": " + (err.response?.data?.error || err.message));
        }
    };

    const handleEdit = (t) => {
        try {
            // Find the raw DB record by matching fingerprint_id or id
            const tFpId = String(t.fingerprint_id || t.id || '');
            const raw = rawTeachers.find(r => 
                String(r.fingerprint_id) === tFpId || 
                String(r.id) === tFpId ||
                String(r._id) === tFpId
            ) || t;

            // Use MongoDB _id for the PUT operation - this is the most reliable identifier
            const mongoId = String(raw._id || t._id || '');
            const fpId = String(raw.fingerprint_id || t.fingerprint_id || raw.id || t.id || '');
            setEditMongoId(mongoId);
            setEditId(mongoId || fpId);

            const rawRoleStr = String(raw.role || t.role || 'staff').toLowerCase();
            const validRole = ['teacher', 'staff', 'admin', 'student'].includes(rawRoleStr) ? rawRoleStr : 'staff';
            setRole(validRole);

            const rawStatusStr = String(raw.status || t.status || 'Active');
            setMemberStatus(rawStatusStr);

            setSection(raw.section || t.section || '');
            setName(raw.name || t.name || '');
            setEmail(raw.email || t.email || '');
            setFingerprintId(raw.fingerprint_id || t.fingerprint_id || raw.id || t.id || '');
            setExperience(raw.experience || t.experience || '');
            setSubject(raw.subject || t.subject || '');

            const timingVal = raw.timing || t.timing || '';
            setTiming(timingVal);
            setCustomTimingText(timingVal);

            try {
                const parsed = parseTiming(timingVal);
                if (parsed) {
                    setStartHour(parsed.startHour || '09');
                    setStartMin(parsed.startMin || '00');
                    setStartAmpm(parsed.startAmpm || 'AM');
                    setEndHour(parsed.endHour || '06');
                    setEndMin(parsed.endMin || '00');
                    setEndAmpm(parsed.endAmpm || 'PM');
                }
            } catch (pErr) {
                console.warn('parseTiming error:', pErr);
            }

            const rawSal = raw.salary !== undefined && raw.salary !== null ? raw.salary : (t.salary !== undefined ? t.salary : '');
            const numSal = parseFloat(String(rawSal).replace(/[^\d.]/g, ''));
            const cleanSal = isNaN(numSal) ? '' : numSal;
            setSalary(cleanSal);
            setDailySalary(cleanSal ? (cleanSal / 30).toFixed(2) : '');

            setProfession(raw.profession || t.profession || '');
            setPhone(raw.studentPhone || t.studentPhone || raw.studentContact || t.studentContact || '');
            setAadhar(raw.aadhar || t.aadhar || '');
            setGender(raw.gender || t.gender || 'Male');
            setDob(raw.dob || t.dob || '');
            const savedBatch = raw.batch || t.batch || '11th PCMB';
            if (['11th PCMB', '12th PCMB', 'Both (11th & 12th PCMB)', 'Career Academy', 'Both (Science & Career Academy)'].includes(savedBatch)) {
                setBatch(savedBatch);
                setOtherBatch('');
            } else {
                setBatch('Other');
                setOtherBatch(savedBatch);
            }
            setPhotoData(raw.photo || t.photo || null);
        } catch (err) {
            console.error('handleEdit initialization error:', err);
        } finally {
            setShowModal(true);
        }
    };

    const handlePrintForm = (t) => {
        setPrintingTeacher(t);
        setTimeout(() => {
            document.body.classList.add('print-teacher-form');
            window.print();
            document.body.classList.remove('print-teacher-form');
        }, 100);
    };

    const handleProcessTimetable = async () => {
        if (!timetablePhoto && !scienceTimetablePhoto && !careerTimetablePhoto) {
            alert("Please select a timetable image photo file first.");
            return;
        }

        let updatedCount = 0;
        for (const t of teachers) {
            if (t.role !== 'teacher') continue;
            
            let curSub = (t.subject && t.subject !== 'N/A' && !t.subject.includes('/') && !t.subject.includes('Faculty')) ? t.subject : '';
            let curBatch = (t.batch && t.batch !== 'N/A') ? t.batch : 'Both (11th & 12th PCMB)';
            
            const tName = t.name.toLowerCase();
            const subLower = curSub.toLowerCase();
            
            let teacherTiming = '';
            let assignedSub = curSub;

            // Match teacher to exact single subject & timing slots from timetable photo
            if (tName.includes('rtt') || subLower.includes('math')) {
                assignedSub = 'Mathematics';
                teacherTiming = '07:30 AM - 01:00 PM (3.0 lecture hrs)';
            } else if (tName.includes('nkt') || subLower.includes('chem')) {
                assignedSub = 'Chemistry';
                teacherTiming = '07:30 AM - 02:30 PM (3.0 lecture hrs)';
            } else if (tName.includes('pgt') || subLower.includes('phy')) {
                assignedSub = 'Physics';
                teacherTiming = '07:30 AM - 02:30 PM (3.0 lecture hrs)';
            } else if (tName.includes('sdt') || tName.includes('ds') || subLower.includes('bio')) {
                assignedSub = 'Biology';
                teacherTiming = '07:30 AM - 02:30 PM (3.0 lecture hrs)';
            } else if (tName.includes('siraa') || subLower.includes('jee') || subLower.includes('neet')) {
                assignedSub = 'JEE & NEET Foundation';
                teacherTiming = '07:30 AM - 01:30 PM (4.0 lecture hrs)';
            } else {
                assignedSub = curSub || (curBatch.toLowerCase().includes('career') ? 'JEE & NEET Foundation' : 'Physics');
                teacherTiming = '08:00 AM - 01:00 PM (3.5 lecture hrs)';
            }

            const rawRec = rawTeachers.find(r => String(r._id) === String(t._id) || String(r.fingerprint_id) === String(t.fingerprint_id)) || t;
            const updatePayload = {
                ...rawRec,
                role: 'teacher',
                subject: assignedSub,
                batch: curBatch,
                timing: teacherTiming
            };

            try {
                await api.put(`/users/${t._id || t.fingerprint_id}`, updatePayload);
                updatedCount++;
            } catch (err) {
                console.warn('Failed to update teacher ' + t.name, err.message);
            }
        }

        if (updatedCount > 0) {
            alert(`✅ Successfully updated ${updatedCount} teacher(s) with clean subject assignments and timings!\n\n• Clean single subject assigned to each teacher.\n• Shift timings matched to timetable lecture slots.`);
            setShowTimetableModal(false);
            setTimetablePhoto(null);
            fetchTeachers();
        } else {
            alert("No active teachers found to update.");
        }
    };

    const handleAutoAssignAllDefaults = async () => {
        if (!window.confirm("Auto-assign recommended subjects and shift timings to all teachers missing details?")) return;
        let count = 0;
        for (const t of teachers) {
            if (t.role === 'teacher') {
                const isCareer = (t.batch || '').toLowerCase().includes('career');
                const tName = (t.name || '').toLowerCase();
                const subLower = (t.subject || '').toLowerCase();
                let cleanDefaultSub = 'Physics';

                if (tName.includes('rtt') || subLower.includes('math')) cleanDefaultSub = 'Mathematics';
                else if (tName.includes('nkt') || subLower.includes('chem')) cleanDefaultSub = 'Chemistry';
                else if (tName.includes('pgt') || subLower.includes('phy')) cleanDefaultSub = 'Physics';
                else if (tName.includes('sdt') || tName.includes('ds') || subLower.includes('bio')) cleanDefaultSub = 'Biology';
                else if (tName.includes('siraa') || isCareer) cleanDefaultSub = 'JEE & NEET Foundation';

                const assignedSub = (t.subject && t.subject !== 'N/A' && !t.subject.includes('/') && !t.subject.includes('Faculty')) ? t.subject : cleanDefaultSub;
                const defaultTiming = isCareer ? '07:30 AM - 02:00 PM (6.5 hrs)' : '08:00 AM - 02:00 PM (6.0 hrs)';
                
                const rawRec = rawTeachers.find(r => String(r._id) === String(t._id) || String(r.fingerprint_id) === String(t.fingerprint_id)) || t;
                const payload = {
                    ...rawRec,
                    role: 'teacher',
                    subject: assignedSub,
                    timing: t.timing && t.timing !== 'N/A' ? t.timing : defaultTiming
                };
                try {
                    await api.put(`/users/${t._id || t.fingerprint_id}`, payload);
                    count++;
                } catch (e) {}
            }
        }
        alert(`✅ Auto-assigned clean subjects and shift timings for ${count} teacher(s)!`);
        fetchTeachers();
    };

    const handleDelete = async (id, name) => {
        if (window.confirm(`Are you sure you want to delete ${name}?`)) {
            try {
                await api.delete(`/users/${id}`);
                setTeachers(prev => prev.filter(t => 
                    String(t.fingerprint_id) !== String(id) && 
                    String(t.id) !== String(id) && 
                    String(t._id) !== String(id)
                ));
                alert(`Successfully deleted ${name}!`);
                fetchTeachers();
            } catch (err) {
                alert("Error deleting: " + (err.response?.data?.error || err.message));
            }
        }
    };

    const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);

    const handleSelectAllTeachers = (e) => {
        if (e.target.checked) {
            const allIds = filteredTeachers.map(t => String(t.fingerprint_id || t.id || t._id));
            setSelectedTeacherIds(allIds);
        } else {
            setSelectedTeacherIds([]);
        }
    };

    const handleSelectOneTeacher = (id) => {
        const strId = String(id);
        setSelectedTeacherIds(prev =>
            prev.includes(strId) ? prev.filter(i => i !== strId) : [...prev, strId]
        );
    };

    const handleBulkDeleteTeachers = async () => {
        if (selectedTeacherIds.length === 0) return;
        if (window.confirm(`⚠️ Are you sure you want to delete ${selectedTeacherIds.length} selected teacher/staff member(s)? This action cannot be undone.`)) {
            try {
                await api.post('/users/bulk-delete', { ids: selectedTeacherIds });
                alert(`Successfully deleted ${selectedTeacherIds.length} member(s)!`);
                setSelectedTeacherIds([]);
                fetchTeachers();
            } catch (err) {
                console.error("Bulk delete error:", err);
                try {
                    for (const id of selectedTeacherIds) {
                        await api.delete(`/users/${id}`);
                    }
                    alert(`Successfully deleted ${selectedTeacherIds.length} member(s)!`);
                    setSelectedTeacherIds([]);
                    fetchTeachers();
                } catch (fallbackErr) {
                    alert("Failed to complete bulk deletion. Please try again.");
                }
            }
        }
    };

    const [inTime, setInTime] = useState('07:30');
    const [outTime, setOutTime] = useState('09:00'); // 1.5 hours standard shift
    const [selectedTeacher, setSelectedTeacher] = useState(null);

    const calculatePayout = () => {
        if (!inTime || !outTime) return { minutes: 0, hours: 0, remMins: 0, payout: 0, deduction: 0 };

        // Parse input times
        const [inH, inM] = inTime.split(':').map(Number);
        const [outH, outM] = outTime.split(':').map(Number);

        const actualIn = new Date(); actualIn.setHours(inH, inM, 0);
        const actualOut = new Date(); actualOut.setHours(outH, outM, 0);

        // Shift boundaries
        const shiftStart = new Date(); shiftStart.setHours(7, 30, 0);
        const graceEnd = new Date(); graceEnd.setHours(7, 35, 0);
        const shiftEnd = new Date(); shiftEnd.setHours(9, 0, 0);

        // 1. Calculate Effective In Time
        let effectiveIn = actualIn;
        if (actualIn <= graceEnd) {
            effectiveIn = shiftStart; // Grace period: treat as 07:30
        }

        // 2. Calculate Effective Out Time
        let effectiveOut = actualOut;
        if (actualOut > shiftEnd) {
            effectiveOut = shiftEnd; // Don't pay extra for staying past 09:00
        }

        // 3. Calculate Valid Minutes
        let validDiffMs = effectiveOut - effectiveIn;
        if (validDiffMs < 0) validDiffMs = 0;

        const validMinutes = Math.floor(validDiffMs / 60000);

        // Rate: 700 rupees for 1.5 hours (90 minutes)
        const ratePerMinute = 700 / 90;
        const payout = validMinutes * ratePerMinute;
        const deduction = 700 - payout;

        return {
            minutes: validMinutes,
            hours: Math.floor(validMinutes / 60),
            remMins: validMinutes % 60,
            payout: payout.toFixed(2),
            deduction: deduction > 0 ? deduction.toFixed(2) : 0
        };
    };

    const [hiddenStaffIds, setHiddenStaffIds] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('hidden_staff_ids') || '[]');
        } catch (e) {
            return [];
        }
    });

    const handleHideFromScreen = (id, name) => {
        if (window.confirm(`Hide ${name} from this screen? (This will not delete them from the database)`)) {
            const idStr = String(id);
            const updated = [...hiddenStaffIds, idStr];
            setHiddenStaffIds(updated);
            localStorage.setItem('hidden_staff_ids', JSON.stringify(updated));
        }
    };

    const calc = calculatePayout();
    const filteredTeachers = teachers.filter(t => {
        const tId = String(t.fingerprint_id || t.id || t._id || '');
        if (hiddenStaffIds.includes(tId)) return false;
        
        // Strict Role Guarantee: Never display student records in Teachers & Staff Panel
        const rLower = String(t.role || '').toLowerCase();
        if (rLower === 'student') return false;

        if (sectionFilter === 'all') return rLower === 'teacher' || rLower === 'staff' || rLower === 'admin';
        if (sectionFilter === 'teacher') return rLower === 'teacher' || rLower === 'admin';
        if (sectionFilter === 'staff') return rLower === 'staff' || rLower === 'admin';
        return rLower === sectionFilter.toLowerCase();
    });

    return (
        <div className="dashboard-card">
            <div className="card-header">
                <h2>👨‍🏫 Teachers & Staff Panel</h2>
                <div className="actions">
                    <button className="btn btn-sky" style={{ marginRight: '10px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', padding: '8px 14px', cursor: 'pointer' }} onClick={() => setShowWhiteboardModal(true)}>📋 Timetable Whiteboard</button>
                    <button className="btn btn-purple" style={{ marginRight: '10px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '6px', fontWeight: '600', padding: '8px 14px', cursor: 'pointer' }} onClick={() => setShowTimetableModal(true)}>📅 Timetable Importer & Auto-Shift</button>
                    <button className="btn btn-info" style={{ marginRight: '10px', background: '#3498db', color: '#fff', border: 'none' }} onClick={() => setShowGlobalSummary(true)}>📊 All Summary</button>
                    <button className="btn btn-primary" onClick={() => {
                        setEditId(null);
                        setName(''); setEmail(''); setFingerprintId(''); setExperience(''); setSubject(''); setTiming(''); setSalary(''); setDailySalary(''); setProfession(''); setBatch('11th PCMB'); setOtherBatch(''); setDob(''); setSection('');
                        setStartHour('09'); setStartMin('00'); setStartAmpm('AM'); setEndHour('06'); setEndMin('00'); setEndAmpm('PM');
                        setPhone(''); setAadhar(''); setGender('Male'); setPhotoData(null);
                        setShowModal(true);
                    }}>Add Teacher/Staff</button>
                </div>
            </div>

            {/* Section Tab Filters & Bulk Delete */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                        { id: 'all', label: '👤 All Members', color: '#64748b', bg: '#f1f5f9' },
                        { id: 'teacher', label: '👨‍🏫 Teachers', color: '#2563eb', bg: '#dbeafe' },
                        { id: 'staff', label: '💼 Staff', color: '#16a34a', bg: '#dcfce7' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setSectionFilter(tab.id)}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '20px',
                                border: sectionFilter === tab.id ? `2px solid ${tab.color}` : '1px solid #cbd5e1',
                                background: sectionFilter === tab.id ? tab.bg : 'white',
                                color: sectionFilter === tab.id ? tab.color : '#475569',
                                fontWeight: '700',
                                fontSize: '13px',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {selectedTeacherIds.length > 0 && (
                    <button
                        onClick={handleBulkDeleteTeachers}
                        style={{
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            color: '#fff',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '13px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 6px rgba(239, 68, 68, 0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        🗑️ Delete Selected ({selectedTeacherIds.length})
                    </button>
                )}
            </div>

            <div style={{ overflowX: 'auto', width: '100%' }}>
                <table className="dashboard-table">
                    <thead>
                        <tr style={{ whiteSpace: 'nowrap' }}>
                            <th style={{ width: '40px', textAlign: 'center' }}>
                                <input
                                    type="checkbox"
                                    checked={filteredTeachers.length > 0 && selectedTeacherIds.length === filteredTeachers.length}
                                    onChange={handleSelectAllTeachers}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#ef4444' }}
                                    title="Select All Teachers/Staff"
                                />
                            </th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Section</th>
                            <th>Subject/Profession</th>
                            <th>Date of Birth</th>
                            <th>In Time</th>
                            <th>Out Time</th>
                            <th>Estimated Hrs</th>
                            <th>Base Salary</th>
                            <th>Biometric ID</th>
                            <th>Payroll</th>
                            <th>Actions</th>
                        </tr >
                    </thead >
                    <tbody>
                        {filteredTeachers.map(t => {
                            const tId = String(t.fingerprint_id || t.id || t._id);
                            const isSelected = selectedTeacherIds.includes(tId);
                            return (
                                <tr key={t.id} style={{ whiteSpace: 'nowrap', background: isSelected ? '#fef2f2' : 'transparent' }}>
                                    <td style={{ textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => handleSelectOneTeacher(tId)}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#ef4444' }}
                                        />
                                    </td>
                                    <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t.photo ? (
                                            <img src={getPhotoUrl(t.photo)} alt="Face" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                                        ) : (
                                            <div className="avatar">{t.name.charAt(0)}</div>
                                        )}
                                        <Link to={`/teacher/${t.fingerprint_id}`} style={{ fontWeight: '600', color: '#3b82f6', textDecoration: 'none' }}>
                                            {t.name}
                                        </Link>
                                    </div>
                                </td>
                                <td style={{ textTransform: 'capitalize' }}>{t.role}</td>
                                <td>{t.section || 'N/A'}</td>
                                <td>{t.role === 'teacher' ? `${t.subject || 'N/A'} ${t.batch ? `(${t.batch})` : ''}` : (t.profession || 'N/A')}</td>
                                <td>{t.dob ? new Date(t.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}</td>
                                <td>{formatISTDisplayTime(t.inTime)}</td>
                                <td>{formatISTDisplayTime(t.outTime)}</td>
                                <td>{getEstimatedHrs(t)}</td>
                                <td>₹ {t.salary ? t.salary.toLocaleString('en-IN') : '0'}</td>
                                <td><code style={{ background: '#f0f2f5', padding: '4px 8px', borderRadius: '4px' }}>{t.fingerprint_id}</code></td>
                                <td>
                                    {(t.role || '').toLowerCase() === 'teacher' ? (
                                        <button className="btn btn-warning" style={{ padding: '4px 10px' }} onClick={() => setSelectedTeacher(t)}>💰 Calculate Pay</button>
                                    ) : (
                                        <span style={{ color: '#888', fontSize: '13px' }}>N/A (Staff)</span>
                                    )}
                                </td>
                                <td>
                                    <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                        <Link to={`/teacher/${t.fingerprint_id}`} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '12px', background: '#3b82f6', borderColor: '#3b82f6', textDecoration: 'none', color: 'white' }}>👁️ View</Link>
                                        <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '12px', background: '#6366f1', borderColor: '#6366f1' }} onClick={() => handleEdit(t)}>✏️ Edit</button>
                                        <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px', background: '#64748b', borderColor: '#64748b', color: 'white' }} onClick={() => handleHideFromScreen(t.fingerprint_id || t.id || t._id, t.name)}>🙈 Hide</button>
                                        <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => handleDelete(t.fingerprint_id || t.id || t._id, t.name)}>🗑️ Delete</button>
                                    </div>
                                </td>
                            </tr >
                        );
                    })}
                        {
                            filteredTeachers.length === 0 && (
                                <tr>
                                    <td colSpan="10" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>No teachers or staff found.</td>
                                </tr>
                            )
                        }
                    </tbody >
                </table >
            </div>



            {showModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999, padding: '20px' }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '500px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>{editId ? '✏️ Edit Member' : '👤 Add New Member'}</h3>
                        {existingUserBanner && (
                            <div style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '13px', marginBottom: '15px', fontWeight: '500', lineHeight: '1.4' }}>
                                {existingUserBanner}
                            </div>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '13px', fontWeight: '500' }}>
                                    Role:
                                    <select value={role} onChange={e => setRole(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginTop: '5px', boxSizing: 'border-box' }}>
                                        <option value="teacher">Teacher</option>
                                        <option value="staff">Staff</option>
                                        <option value="admin">Admin</option>
                                        <option value="student">Student</option>
                                    </select>
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '13px', fontWeight: '500' }}>
                                    Status:
                                    <select value={memberStatus} onChange={e => setMemberStatus(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginTop: '5px', boxSizing: 'border-box' }}>
                                        <option value="Active">Active</option>
                                        <option value="Inactive">Inactive</option>
                                        <option value="On Leave">On Leave</option>
                                        <option value="Suspended">Suspended</option>
                                    </select>
                                </label>
                                <label style={{ display: 'flex', flexDirection: 'column', fontSize: '13px', fontWeight: '500' }}>
                                    Biometric ID: *
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        placeholder="Max 4 digits"
                                        value={fingerprintId}
                                        onChange={e => {
                                            const val = e.target.value.replace(/\D/g, '');
                                            if (val.length <= 4) handleBiometricIdChange(val);
                                        }}
                                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginTop: '5px', boxSizing: 'border-box' }}
                                        required
                                    />
                                </label>
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <input type="text" placeholder="Full Name *" value={name} onChange={e => setName(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }} required />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <input type="email" placeholder="Email Address" value={email} onChange={e => setEmail(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <input type="text" placeholder="Section (Optional, E.g. Section A, Science, etc.)" value={section} onChange={e => setSection(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </div>

                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '12px', color: '#666', fontWeight: '600', marginBottom: '4px' }}>🎂 Date of Birth (Birthday)</label>
                                <input type="date" value={dob} onChange={e => setDob(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }} />
                            </div>

                            {(role === 'teacher' || role === 'staff' || role === 'admin') && (
                                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ddd', boxSizing: 'border-box' }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#555', textTransform: 'uppercase' }}>Salary Information</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '8px', alignItems: 'center', width: '100%' }}>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="Monthly Salary (₹)"
                                            value={salary}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length <= 7) setSalary(val);
                                            }}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-warning"
                                            style={{ padding: '10px 15px', fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                                            onClick={() => {
                                                if (salary && !dailySalary) setDailySalary((parseFloat(salary) / 30).toFixed(2));
                                                else if (dailySalary && !salary) setSalary((parseFloat(dailySalary) * 30).toFixed(2));
                                                else if (salary && dailySalary) setDailySalary((parseFloat(salary) / 30).toFixed(2));
                                            }}
                                        >
                                            Calculate
                                        </button>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            placeholder="Daily Salary (₹)"
                                            value={dailySalary}
                                            onChange={e => {
                                                const val = e.target.value.replace(/\D/g, '');
                                                if (val.length <= 6) setDailySalary(val);
                                            }}
                                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}
                                        />
                                    </div>
                                </div>
                            )}

                            {role === 'teacher' && (
                                <div style={{ background: '#f0f4f8', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #d9e2ec', boxSizing: 'border-box' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <h4 style={{ margin: 0, fontSize: '13px', color: '#555', textTransform: 'uppercase' }}>Teacher Details & Subject</h4>
                                        <button
                                            type="button"
                                            style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                            onClick={() => {
                                                if (batch.toLowerCase().includes('career')) {
                                                    if (!subject || subject === 'N/A' || subject.includes('/')) setSubject('JEE & NEET Foundation');
                                                    setStartHour('07'); setStartMin('30'); setStartAmpm('AM'); setEndHour('02'); setEndMin('00'); setEndAmpm('PM');
                                                } else {
                                                    if (!subject || subject === 'N/A' || subject.includes('/')) setSubject('Physics');
                                                    setStartHour('08'); setStartMin('00'); setStartAmpm('AM'); setEndHour('02'); setEndMin('00'); setEndAmpm('PM');
                                                }
                                            }}
                                        >
                                            ✨ Auto-Set Subject & Shift
                                        </button>
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        <div>
                                            <input
                                                type="text"
                                                placeholder="Topic / Subject"
                                                value={subject}
                                                onChange={e => setSubject(e.target.value)}
                                                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', marginBottom: '6px' }}
                                            />
                                            <select
                                                style={{ width: '100%', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px', background: '#ffffff', cursor: 'pointer' }}
                                                onChange={e => { if (e.target.value) setSubject(e.target.value); }}
                                            >
                                                <option value="">⚡ Quick Subject Preset...</option>
                                                <option value="Mathematics">Mathematics</option>
                                                <option value="Physics">Physics</option>
                                                <option value="Chemistry">Chemistry</option>
                                                <option value="Biology">Biology</option>
                                                <option value="JEE Mains & Advanced">JEE Mains & Advanced</option>
                                                <option value="NEET Preparation">NEET Preparation</option>
                                                <option value="Career Foundation & Aptitude">Career Foundation & Aptitude</option>
                                                <option value="Science & Career Combined">Science & Career Combined</option>
                                            </select>
                                        </div>
                                        <div>
                                            <select value={batch} onChange={e => setBatch(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box' }}>
                                                <option value="11th PCMB">11th PCMB</option>
                                                <option value="12th PCMB">12th PCMB</option>
                                                <option value="Both (11th & 12th PCMB)">Both (11th & 12th PCMB)</option>
                                                <option value="Career Academy">Career Academy</option>
                                                <option value="Both (Science & Career Academy)">Both (Science & Career Academy)</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                        {batch === 'Other' && (
                                            <input type="text" placeholder="Enter custom batch" value={otherBatch} onChange={e => setOtherBatch(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', gridColumn: 'span 2' }} />
                                        )}
                                    </div>
                                </div>
                            )}

                            {(role === 'staff' || role === 'admin' || role === 'teacher' || role === 'student') && (
                                <>
                                    <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ddd', boxSizing: 'border-box' }}>
                                        <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#555', textTransform: 'uppercase' }}>
                                            {role === 'teacher' ? 'TEACHER PERSONAL DETAILS' : 'STAFF / PERSONAL DETAILS'}
                                        </h4>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                            <div>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    placeholder="Contact Number"
                                                    value={phone}
                                                    onChange={e => {
                                                        const val = e.target.value.replace(/\D/g, '');
                                                        if (val.length <= 10) setPhone(val);
                                                    }}
                                                    style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    placeholder="Aadhar Card No. (12 digits)"
                                                    value={aadhar}
                                                    onChange={e => {
                                                        const val = e.target.value.replace(/\D/g, '');
                                                        if (val.length <= 12) setAadhar(val);
                                                    }}
                                                    style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', width: '100%' }}
                                                />
                                            </div>
                                            <div>
                                                <select value={gender} onChange={e => setGender(e.target.value)} style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', width: '100%' }}>
                                                    <option value="Male">Male</option>
                                                    <option value="Female">Female</option>
                                                    <option value="Other">Other</option>
                                                </select>
                                            </div>
                                            <div style={{ position: 'relative' }}>
                                                <label style={{ position: 'absolute', top: '-8px', left: '10px', background: '#f8f9fa', padding: '0 5px', fontSize: '11px', color: '#666' }}>Date of Birth</label>
                                                <input
                                                    type="date"
                                                    value={dob}
                                                    onChange={e => {
                                                        let val = e.target.value;
                                                        if (val) {
                                                            const parts = val.split('-');
                                                            if (parts[0] && parts[0].length > 4) {
                                                                parts[0] = parts[0].substring(0, 4);
                                                                val = parts.join('-');
                                                            }
                                                        }
                                                        setDob(val);
                                                    }}
                                                    style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', width: '100%', color: '#666', background: 'transparent' }}
                                                />
                                            </div>
                                            <div style={{ gridColumn: 'span 2' }}>
                                                <input
                                                    type="text"
                                                    placeholder="Profession / Job Title (e.g. Accountant, Security)"
                                                    value={profession}
                                                    onChange={e => setProfession(e.target.value)}
                                                    style={{ padding: '10px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', width: '100%' }}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ddd', boxSizing: 'border-box' }}>
                                        <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#555', textTransform: 'uppercase' }}>Member Shift Timing</h4>
                                        
                                        <div style={{ marginBottom: '12px' }}>
                                            <label style={{ fontSize: '11px', color: '#475569', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>⚡ Choose Quick Shift Preset:</label>
                                            <select
                                                style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #3b82f6', background: '#eff6ff', fontSize: '13px', fontWeight: '600', color: '#1e40af', cursor: 'pointer' }}
                                                onChange={(e) => applyShiftPreset(e.target.value)}
                                            >
                                                {SHIFT_PRESETS.map((preset, i) => (
                                                    <option key={i} value={i}>{preset.label}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '10px' }}>
                                            <div>
                                                <label style={{ fontSize: '11px', color: '#666', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Start Time:</label>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <select value={startHour} onChange={e => setStartHour(e.target.value)} style={{ flex: 1.2, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}>
                                                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(h => (
                                                            <option key={h} value={h}>{h}</option>
                                                        ))}
                                                    </select>
                                                    <select value={startMin} onChange={e => setStartMin(e.target.value)} style={{ flex: 1.2, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}>
                                                        {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </select>
                                                    <select value={startAmpm} onChange={e => setStartAmpm(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px', minWidth: '55px' }}>
                                                        <option value="AM">AM</option>
                                                        <option value="PM">PM</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div>
                                                <label style={{ fontSize: '11px', color: '#666', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>End Time:</label>
                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                    <select value={endHour} onChange={e => setEndHour(e.target.value)} style={{ flex: 1.2, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}>
                                                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(h => (
                                                            <option key={h} value={h}>{h}</option>
                                                        ))}
                                                    </select>
                                                    <select value={endMin} onChange={e => setEndMin(e.target.value)} style={{ flex: 1.2, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px' }}>
                                                        {['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'].map(m => (
                                                            <option key={m} value={m}>{m}</option>
                                                        ))}
                                                    </select>
                                                    <select value={endAmpm} onChange={e => setEndAmpm(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '13px', minWidth: '55px' }}>
                                                        <option value="AM">AM</option>
                                                        <option value="PM">PM</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#2c3e50', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', background: '#eef2f7', padding: '8px 12px', borderRadius: '6px' }}>
                                            <span>🕒 Total Shift Duration:</span>
                                            <span style={{ background: '#3498db', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                                                {calculateWorkingHours(startHour, startMin, startAmpm, endHour, endMin, endAmpm).toFixed(1)} Hours
                                            </span>
                                        </div>
                                        <div style={{ marginTop: '12px' }}>
                                            <label style={{ fontSize: '11px', color: '#475569', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                                                ✍️ Manual Working Hours / Custom Shift String:
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g. 09:00 AM - 10:30 AM (1.5 hrs) or 9.0 hrs"
                                                value={customTimingText}
                                                onChange={e => setCustomTimingText(e.target.value)}
                                                style={{ width: '100%', padding: '9px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '600', color: '#1e293b', boxSizing: 'border-box', background: '#ffffff' }}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #ddd', boxSizing: 'border-box' }}>
                                <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#555', textTransform: 'uppercase' }}>Member Photo</h4>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>Option 1: Upload from Computer</div>
                                        <input type="file" accept="image/*" onChange={handleFileUpload} style={{ padding: '8px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>Option 2: Take Live Photo</div>
                                        {!cameraActive && !photoData && (
                                            <button type="button" onClick={startCamera} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>📷 Start Webcam</button>
                                        )}
                                        {cameraActive && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <video ref={videoRef} autoPlay style={{ width: '100%', maxWidth: '250px', borderRadius: '8px', background: '#000' }}></video>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <button type="button" onClick={capturePhoto} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>📸 Capture</button>
                                                    <button type="button" onClick={stopCamera} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                                </div>
                                            </div>
                                        )}
                                        <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                                    </div>
                                    {photoData && (
                                        <div style={{ width: '120px', textAlign: 'center' }}>
                                            <img src={photoData} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #8b5cf6', marginBottom: '8px' }} />
                                            <button type="button" onClick={() => setPhotoData(null)} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Remove</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' }}>
                                <button type="button" className="btn" style={{ background: '#eee', color: '#333' }} onClick={() => { setShowModal(false); stopCamera(); setPhotoData(null); }}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Member</button>
                            </div>
                        </form >
                    </div >
                </div >
            )}

            {/* Global Summary Modal */}
            {showGlobalSummary && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999, padding: '40px' }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '1000px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', position: 'sticky', top: '-30px', background: '#fff', padding: '20px 0', zIndex: 10, borderBottom: '1px solid #eaeaea' }}>
                            <h3 style={{ margin: 0, color: '#2c3e50' }}>📊 Comprehensive Staff Attendance Summary</h3>
                            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    placeholder="🔍 Search by name or role..."
                                    value={summarySearchTerm}
                                    onChange={(e) => setSummarySearchTerm(e.target.value)}
                                    style={{ padding: '8px 15px', borderRadius: '20px', border: '1px solid #d1d8e0', outline: 'none', width: '250px', fontSize: '13px', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.05)', transition: 'border-color 0.2s' }}
                                />
                                <button className="btn" style={{ padding: '6px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px' }} onClick={() => setShowGlobalSummary(false)}>Close</button>
                            </div>
                        </div>

                        {teachers.filter(t => t.name.toLowerCase().includes(summarySearchTerm.toLowerCase()) || t.role.toLowerCase().includes(summarySearchTerm.toLowerCase())).length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>No data available.</div>
                        ) : (
                            teachers.filter(t => t.name.toLowerCase().includes(summarySearchTerm.toLowerCase()) || t.role.toLowerCase().includes(summarySearchTerm.toLowerCase())).map(teacher => (
                                <div key={teacher.id} style={{ marginBottom: '30px', padding: '20px', border: '1px solid #e1e8ed', borderRadius: '8px', background: '#fdfdfe' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h4 style={{ margin: 0, color: '#2980b9', fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <div className="avatar" style={{ width: '30px', height: '30px', fontSize: '14px', background: '#3498db', color: '#fff', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '50%' }}>{teacher.name.charAt(0)}</div>
                                            {teacher.name} <span style={{ fontSize: '13px', color: '#7f8c8d', fontWeight: 'normal' }}>({teacher.role === 'teacher' ? teacher.subject : teacher.profession})</span>
                                        </h4>
                                        <div style={{ fontSize: '13px', background: '#e8f8f5', color: '#16a085', padding: '4px 10px', borderRadius: '12px', fontWeight: '600' }}>
                                            {teacher.daysPresent || 0} Days Present
                                        </div>
                                    </div>

                                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #eee', borderRadius: '6px' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f4f6f7', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#555' }}>Date</th>
                                                    <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#555' }}>Status</th>
                                                    <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#555' }}>In Time</th>
                                                    <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#555' }}>Out Time</th>
                                                    <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#555' }}>Duration</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {teacher.monthlyLog && teacher.monthlyLog.length > 0 ? (
                                                    teacher.monthlyLog.map((log, idx) => (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0', background: log.status === 'Sunday' ? '#fafafa' : 'transparent' }}>
                                                            <td style={{ padding: '10px', color: '#444', fontWeight: '500' }}>{log.date}</td>
                                                            <td style={{ padding: '10px' }}>
                                                                {log.status === 'Present' && <span style={{ color: '#27ae60', fontWeight: '600' }}>Present</span>}
                                                                {log.status === 'Absent' && <span style={{ color: '#e74c3c', fontWeight: '600' }}>Absent</span>}
                                                                {log.status === 'Sunday' && <span style={{ color: '#f39c12' }}>Sunday</span>}
                                                                {log.status === 'Late' && <span style={{ color: '#e67e22', fontWeight: '600' }}>Late</span>}
                                                                {log.status === 'Half Day' && <span style={{ color: '#9b59b6', fontWeight: '600' }}>Half Day</span>}
                                                            </td>
                                                            <td style={{ padding: '10px', color: '#666' }}>{formatISTDisplayTime(log.inTime)}</td>
                                                            <td style={{ padding: '10px', color: '#666' }}>{formatISTDisplayTime(log.outTime)}</td>
                                                            <td style={{ padding: '10px', color: '#666', fontWeight: '500' }}>
                                                                {log.workingHours !== '--' ? `⏱ ${log.workingHours}` : '--'}
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" style={{ padding: '15px', textAlign: 'center', color: '#aaa', fontStyle: 'italic' }}>
                                                            No attendance records for this month.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {
                selectedTeacher && typeof selectedTeacher === 'object' && (
                    <div style={{ marginTop: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', borderLeft: '5px solid #27ae60' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h4 style={{ margin: 0 }}>Professional Payroll Calculator: {selectedTeacher.name}</h4>
                            <button className="btn" style={{ padding: '2px 8px', background: 'transparent', color: '#666' }} onClick={() => setSelectedTeacher(null)}>✕</button>
                        </div>

                        <div style={{ display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            {/* Payroll Statistics */}
                            <div style={{ display: 'flex', gap: '20px', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontSize: '12px', color: '#888' }}>Month-to-Date Pay</div>
                                    <strong style={{ fontSize: '18px', color: '#2980b9' }}>₹ {selectedTeacher.monthPay ? Math.round(selectedTeacher.monthPay).toLocaleString('en-IN') : '0'}</strong>
                                </div>

                                <div style={{ borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
                                    <div style={{ fontSize: '12px', color: '#888' }}>Today's In Time</div>
                                    <strong style={{ fontSize: '16px' }}>{formatISTDisplayTime(selectedTeacher.inTime)}</strong>
                                </div>

                                <div style={{ borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
                                    <div style={{ fontSize: '12px', color: '#888' }}>Daily Base Pay</div>
                                    <strong style={{ fontSize: '16px' }}>₹ {selectedTeacher.salary ? Math.round(selectedTeacher.salary / 30) : 0}</strong>
                                </div>

                                {selectedTeacher.status === 'Late' && (
                                    <div style={{ borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
                                        <div style={{ fontSize: '12px', color: '#e74c3c' }}>Late Penalty (-{selectedTeacher.lateMinutes}m)</div>
                                        <strong style={{ fontSize: '16px', color: '#e74c3c' }}>- ₹ {Math.round((selectedTeacher.salary / 30) - selectedTeacher.dayPay)}</strong>
                                    </div>
                                )}

                                <div style={{ borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
                                    <div style={{ fontSize: '12px', color: '#888' }}>Today's Final Payout</div>
                                    <strong style={{ fontSize: '20px', color: '#27ae60' }}>₹ {selectedTeacher.dayPay ? Math.round(selectedTeacher.dayPay) : 0}</strong>
                                </div>
                            </div>

                            {/* Professional Attendance Details Box */}
                            <div style={{ background: '#fff', border: '1px solid #eaeaea', borderRadius: '8px', padding: '15px', minWidth: '280px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                                <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Attendance Summary</span>
                                    <span style={{ color: '#27ae60', background: '#e8f8f5', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                                        {selectedTeacher.daysPresent || 0} Present
                                    </span>
                                </div>

                                {selectedTeacher.absentDates && selectedTeacher.absentDates.length > 0 ? (
                                    <div>
                                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px', fontWeight: '500' }}>
                                            Absent Days ({selectedTeacher.absentDates.length}):
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '85px', overflowY: 'auto', paddingRight: '5px' }}>
                                            {selectedTeacher.absentDates.map((date, i) => (
                                                <span key={i} style={{ background: '#fdf3f2', color: '#e74c3c', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', border: '1px solid #fadbd8', fontWeight: '500' }}>
                                                    {date}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', marginTop: '10px' }}>
                                        🌟 Perfect attendance so far!
                                    </div>
                                )}
                            </div>

                        </div>

                        {/* 🖐️ Biometric Thumb Latency & Payment Reluctancy Audit Object Card */}
                        <div style={{ marginTop: '20px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <h5 style={{ margin: 0, color: '#0f172a', fontSize: '14px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    🖐️ Biometric Thumb Latency & Payment Audit Object
                                </h5>
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>
                                    Biometric ID: #{selectedTeacher.fingerprint_id || selectedTeacher.id || 'N/A'}
                                </span>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                                <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Scheduled Lecture Start</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>07:30 AM</div>
                                </div>
                                <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: '600' }}>Biometric Thumb Scan In Time</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b' }}>{formatISTDisplayTime(selectedTeacher.inTime)}</div>
                                </div>
                                <div style={{ background: selectedTeacher.status === 'Late' ? '#fff7ed' : '#f0fdf4', padding: '10px', borderRadius: '6px', border: selectedTeacher.status === 'Late' ? '1px solid #ffedd5' : '1px solid #bbf7d0' }}>
                                    <div style={{ fontSize: '11px', color: selectedTeacher.status === 'Late' ? '#c2410c' : '#15803d', fontWeight: '600' }}>Punch Latency (Minutes)</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: selectedTeacher.status === 'Late' ? '#ea580c' : '#16a34a' }}>
                                        {selectedTeacher.lateMinutes ? `+${selectedTeacher.lateMinutes} Mins Late` : '0 Mins (On Time)'}
                                    </div>
                                </div>
                                <div style={{ background: selectedTeacher.status === 'Late' ? '#fef2f2' : '#f8fafc', padding: '10px', borderRadius: '6px', border: selectedTeacher.status === 'Late' ? '1px solid #fecaca' : '1px solid #e2e8f0' }}>
                                    <div style={{ fontSize: '11px', color: selectedTeacher.status === 'Late' ? '#dc2626' : '#64748b', fontWeight: '600' }}>Latency Payment Reluctancy Cut</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: selectedTeacher.status === 'Late' ? '#ef4444' : '#1e293b' }}>
                                        {selectedTeacher.status === 'Late' ? `- ₹ ${Math.round((selectedTeacher.salary / 30) - (selectedTeacher.dayPay || 0))}` : '₹ 0 (Full Pay)'}
                                    </div>
                                </div>
                            </div>

                            {/* Audit Object JSON View */}
                            <div style={{ background: '#0f172a', color: '#38bdf8', padding: '10px 12px', borderRadius: '6px', fontSize: '11px', fontFamily: 'monospace', overflowX: 'auto' }}>
                                <code>
                                    {JSON.stringify({
                                        teacher: selectedTeacher.name,
                                        biometric_thumb_id: String(selectedTeacher.fingerprint_id || selectedTeacher.id),
                                        scheduled_lecture: "07:30 AM",
                                        actual_thumb_scan: formatISTDisplayTime(selectedTeacher.inTime),
                                        latency_minutes: selectedTeacher.lateMinutes || 0,
                                        status: selectedTeacher.status || 'Present',
                                        payment_cut: selectedTeacher.status === 'Late' ? Math.round((selectedTeacher.salary / 30) - (selectedTeacher.dayPay || 0)) : 0
                                    }, null, 2)}
                                </code>
                            </div>
                        </div>

                        {/* Detailed Monthly Log Section */}
                        <div style={{ marginTop: '20px', background: '#fff', border: '1px solid #eaeaea', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                            <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '14px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>📅 Detailed Monthly Log</h5>
                            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', textAlign: 'left' }}>
                                    <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', zIndex: 1 }}>
                                        <tr>
                                            <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#666' }}>Date</th>
                                            <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#666' }}>Status</th>
                                            <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#666' }}>In Time</th>
                                            <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#666' }}>Out Time</th>
                                            <th style={{ padding: '10px', borderBottom: '1px solid #ddd', color: '#666' }}>Duration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedTeacher.monthlyLog && selectedTeacher.monthlyLog.length > 0 ? (
                                            selectedTeacher.monthlyLog.map((log, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <td style={{ padding: '10px', color: '#444', fontWeight: '500' }}>{log.date}</td>
                                                    <td style={{ padding: '10px' }}>
                                                        {log.status === 'Present' && <span style={{ background: '#e8f8f5', color: '#16a085', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Present</span>}
                                                        {log.status === 'Absent' && <span style={{ background: '#fdf3f2', color: '#e74c3c', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Absent</span>}
                                                        {log.status === 'Sunday' && <span style={{ background: '#fcf3cf', color: '#d4ac0d', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Sunday</span>}
                                                        {log.status === 'Late' && <span style={{ background: '#fef5e7', color: '#e67e22', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Late</span>}
                                                        {log.status === 'Half Day' && <span style={{ background: '#f5eef8', color: '#8e44ad', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Half Day</span>}
                                                    </td>
                                                    <td style={{ padding: '10px', color: '#666' }}>{log.inTime}</td>
                                                    <td style={{ padding: '10px', color: '#666' }}>{log.outTime}</td>
                                                    <td style={{ padding: '10px', color: '#666', fontWeight: '500' }}>
                                                        {log.workingHours !== '--' ? `⏱ ${log.workingHours}` : '--'}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
                                                    No attendance logs available for this month yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )
            }
            {showTimetableModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '12px', width: '550px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>📷 Upload Teacher Timetable Photo</h3>
                            <button onClick={() => { setShowTimetableModal(false); setTimetablePhoto(null); }} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                        </div>
                        
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 15px 0', lineHeight: '1.4' }}>
                            Upload a photo of your timetable image. Once attached, click <strong>Apply Timetable</strong> to automatically assign teacher subjects and shift timings.
                        </p>

                        {/* Roll Down Department Select Menu */}
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                                📋 Select Timetable Department (Roll-Down Menu):
                            </label>
                            <select
                                value={selectedUploadDept}
                                onChange={(e) => setSelectedUploadDept(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: '700', color: '#0f172a', background: '#f8fafc' }}
                            >
                                <option value="science">🔬 1. Science Tutorials Timetable (11th & 12th PCMB)</option>
                                <option value="career">🎓 2. Career Academy Timetable (JEE & NEET Foundation)</option>
                            </select>
                        </div>

                        {/* Photo Upload Section */}
                        <div style={{ background: '#f0fdf4', border: '2px dashed #86efac', borderRadius: '10px', padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>
                                📁 Select Image File for {selectedUploadDept === 'science' ? '🔬 Science Tutorials' : '🎓 Career Academy'}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                        const reader = new FileReader();
                                        reader.onload = (evt) => {
                                            const imgData = evt.target.result;
                                            if (selectedUploadDept === 'science') {
                                                setScienceTimetablePhoto(imgData);
                                                setTimetablePhoto(imgData);
                                            } else {
                                                setCareerTimetablePhoto(imgData);
                                            }
                                        };
                                        reader.readAsDataURL(file);
                                    }
                                }}
                                style={{ fontSize: '13px', cursor: 'pointer' }}
                            />
                            {((selectedUploadDept === 'science' ? scienceTimetablePhoto || timetablePhoto : careerTimetablePhoto)) ? (
                                <div style={{ marginTop: '15px', background: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                    <div style={{ fontSize: '12px', color: '#15803d', fontWeight: 'bold', marginBottom: '6px' }}>
                                        ✅ {selectedUploadDept === 'science' ? 'Science' : 'Career Academy'} Timetable Photo Attached:
                                    </div>
                                    <img src={selectedUploadDept === 'science' ? (scienceTimetablePhoto || timetablePhoto) : careerTimetablePhoto} alt="Timetable Preview" style={{ maxWidth: '100%', maxHeight: '250px', borderRadius: '6px', border: '1px solid #cbd5e1', objectFit: 'contain' }} />
                                    <button
                                        type="button"
                                        style={{ display: 'block', margin: '8px auto 0 auto', background: '#ef4444', color: 'white', border: 'none', padding: '4px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                        onClick={() => {
                                            if (selectedUploadDept === 'science') {
                                                setScienceTimetablePhoto(null);
                                                setTimetablePhoto(null);
                                            } else {
                                                setCareerTimetablePhoto(null);
                                            }
                                        }}
                                    >
                                        🗑️ Remove Photo
                                    </button>
                                </div>
                            ) : (
                                <div style={{ marginTop: '12px', fontSize: '12px', color: '#64748b' }}>
                                    ℹ️ No photo attached for {selectedUploadDept === 'science' ? 'Science Tutorials' : 'Career Academy'} yet.
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                style={{ background: '#10b981', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                                onClick={() => handleProcessTimetable('')}
                            >
                                ⚡ Apply Timetable Photo
                            </button>
                            <button
                                type="button"
                                style={{ background: '#64748b', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                                onClick={() => { setShowTimetableModal(false); setTimetablePhoto(null); }}
                            >
                                Cancel
                            </button>
                        </div>
                        
                        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
                            <button
                                type="button"
                                style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', width: '100%' }}
                                onClick={handleAutoAssignAllDefaults}
                            >
                                ✨ Auto-Assign Recommended Subjects & Timings for All Teachers
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showWhiteboardModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ background: 'white', padding: '25px', borderRadius: '14px', width: timetablePhoto ? '1150px' : '950px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.35)', transition: 'all 0.3s ease' }}>
                        
                        {/* Modal Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    📋 Timetable Master Whiteboards
                                </h3>
                                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                                    Two dedicated timetables: Science Tutorials (11th & 12th) and Career Academy.
                                </p>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <button
                                    type="button"
                                    style={{ background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                    onClick={() => {
                                        setWhiteboardMatrix(prev => prev.map(row => row.isBreak ? row : {
                                            ...row,
                                            c11: { subject: 'Blank Lecture', teacher: 'Unassigned', color: '#94a3b8' },
                                            c12: { subject: 'Blank Lecture', teacher: 'Unassigned', color: '#94a3b8' },
                                            career: { subject: 'Blank Lecture', teacher: 'Unassigned', color: '#94a3b8' }
                                        }));
                                        alert("📑 Whiteboard reset to Practice Blank Template!");
                                    }}
                                >
                                    📑 Practice Blank Template
                                </button>
                                <button
                                    type="button"
                                    style={{ background: '#0284c7', color: 'white', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                    onClick={() => window.print()}
                                >
                                    🖨️ Print Timetable
                                </button>
                                <button onClick={() => setShowWhiteboardModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#64748b' }}>&times;</button>
                            </div>
                        </div>

                        {/* Separate Timetable Selection Tabs */}
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', background: '#f8fafc', padding: '8px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <button
                                type="button"
                                onClick={() => setWhiteboardTab('science')}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    background: whiteboardTab === 'science' ? '#2563eb' : 'transparent',
                                    color: whiteboardTab === 'science' ? '#ffffff' : '#64748b',
                                    boxShadow: whiteboardTab === 'science' ? '0 4px 12px rgba(37,99,235,0.25)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                🔬 1. Science Tutorials Timetable (11th & 12th PCMB)
                            </button>
                            <button
                                type="button"
                                onClick={() => setWhiteboardTab('career')}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    background: whiteboardTab === 'career' ? '#059669' : 'transparent',
                                    color: whiteboardTab === 'career' ? '#ffffff' : '#64748b',
                                    boxShadow: whiteboardTab === 'career' ? '0 4px 12px rgba(5,150,105,0.25)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                🎓 2. Career Academy Timetable (JEE & NEET Foundation)
                            </button>
                        </div>

                        {/* Day of Week Selection Bar (Monday through Saturday) */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', background: '#f1f5f9', padding: '6px 10px', borderRadius: '8px', overflowX: 'auto' }}>
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => {
                                const isTodayActual = new Date().toLocaleDateString('en-US', { weekday: 'long' }) === day;
                                return (
                                    <button
                                        key={day}
                                        type="button"
                                        onClick={() => setSelectedDayTab(day)}
                                        style={{
                                            padding: '6px 14px',
                                            borderRadius: '6px',
                                            border: 'none',
                                            fontSize: '13px',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            background: selectedDayTab === day ? '#0f172a' : (isTodayActual ? '#dbeafe' : '#ffffff'),
                                            color: selectedDayTab === day ? '#ffffff' : (isTodayActual ? '#1e40af' : '#475569'),
                                            boxShadow: selectedDayTab === day ? '0 2px 6px rgba(0,0,0,0.15)' : 'none',
                                            transition: 'all 0.15s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        📅 {day} {isTodayActual && <span style={{ background: selectedDayTab === day ? '#ef4444' : '#2563eb', color: 'white', fontSize: '10px', padding: '1px 6px', borderRadius: '8px', fontWeight: '800' }}>TODAY</span>}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Side-by-Side View */}
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                            
                            {/* Left Side: Uploaded Timetable Photo Viewer per Department */}
                            <div style={{ flex: '0 0 320px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '15px' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#0f172a', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    📷 Attached Photo ({selectedDayTab}): {whiteboardTab === 'science' ? '🔬 Science' : '🎓 Career'}
                                </div>

                                {((whiteboardTab === 'science' ? (scienceTimetablePhoto || timetablePhoto) : careerTimetablePhoto)) ? (
                                    <div>
                                        <img src={whiteboardTab === 'science' ? (scienceTimetablePhoto || timetablePhoto) : careerTimetablePhoto} alt="Timetable Photo" style={{ width: '100%', borderRadius: '8px', border: '1px solid #cbd5e1', objectFit: 'contain', maxHeight: '420px' }} />
                                        <button
                                            type="button"
                                            style={{ marginTop: '10px', width: '100%', background: '#ef4444', color: 'white', border: 'none', padding: '6px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                            onClick={() => {
                                                if (whiteboardTab === 'science') {
                                                    setScienceTimetablePhoto(null);
                                                    setTimetablePhoto(null);
                                                } else {
                                                    setCareerTimetablePhoto(null);
                                                }
                                            }}
                                        >
                                            Remove Attached Photo
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ padding: '25px 15px', border: '2px dashed #cbd5e1', borderRadius: '8px', textAlign: 'center', background: '#ffffff' }}>
                                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📷</div>
                                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#475569', marginBottom: '4px' }}>
                                            No Photo Uploaded
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '12px' }}>
                                            No timetable photo uploaded for {whiteboardTab === 'science' ? 'Science Tutorials' : 'Career Academy'} yet.
                                        </div>
                                        <button
                                            type="button"
                                            style={{ background: whiteboardTab === 'science' ? '#2563eb' : '#059669', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                            onClick={() => {
                                                setSelectedUploadDept(whiteboardTab);
                                                setShowTimetableModal(true);
                                            }}
                                        >
                                            📷 Upload {whiteboardTab === 'science' ? 'Science' : 'Career'} Timetable Photo
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Right Side: Specific Whiteboard Matrix */}
                            <div style={{ flex: 1, minWidth: '450px' }}>
                                
                                {whiteboardTab === 'science' ? (
                                    /* SCIENCE TUTORIALS TIMETABLE */
                                    <div>
                                        <div style={{ background: '#dbeafe', color: '#1e40af', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>🔬 <strong>Science Tutorials ({selectedDayTab})</strong> &bull; Faculty: Rtt, Nkt, Pgt, Ds</span>
                                            <span style={{ background: '#2563eb', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>{selectedDayTab} Schedule</span>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '10px', overflow: 'hidden', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                            <thead>
                                                <tr style={{ background: '#1e3a8a', color: 'white', fontSize: '12px', textAlign: 'center', textTransform: 'uppercase' }}>
                                                    <th style={{ padding: '12px', width: '160px' }}>Time Slot</th>
                                                    <th style={{ padding: '12px', background: '#2563eb' }}>📘 Class 11th PCMB</th>
                                                    <th style={{ padding: '12px', background: '#7c3aed' }}>📗 Class 12th PCMB</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(dayWiseWhiteboard[selectedDayTab] || dayWiseWhiteboard.Monday).map((row, idx) => {
                                                    if (row.isBreak) {
                                                        return (
                                                            <tr key={idx} style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
                                                                <td style={{ padding: '10px', fontSize: '12px', fontWeight: '700', color: '#92400e', textAlign: 'center' }}>{row.slot}</td>
                                                                <td colSpan="2" style={{ padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#b45309' }}>
                                                                    {row.label}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    return (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                                            <td style={{ padding: '12px', fontSize: '12px', fontWeight: '700', color: '#475569', textAlign: 'center', background: '#f1f5f9' }}>
                                                                {row.slot}
                                                            </td>
                                                            
                                                            {/* 11th PCMB */}
                                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                                <div style={{ background: '#eff6ff', border: `1px solid ${row.c11.color || '#3b82f6'}`, borderRadius: '8px', padding: '10px' }}>
                                                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#1e3a8a' }}>{row.c11.subject}</div>
                                                                    <div style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: '600', marginTop: '3px' }}>
                                                                        👨‍🏫 Teacher: <strong>{row.c11.teacher}</strong>
                                                                    </div>
                                                                    <button
                                                                        style={{ marginTop: '6px', background: '#dbeafe', color: '#1e40af', border: 'none', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const newT = prompt(`Assign Science Teacher for 11th on ${selectedDayTab} (${row.c11.subject}):`, row.c11.teacher);
                                                                            if (newT) {
                                                                                const updated = { ...dayWiseWhiteboard };
                                                                                updated[selectedDayTab][idx].c11.teacher = newT;
                                                                                setDayWiseWhiteboard(updated);
                                                                            }
                                                                        }}
                                                                    >
                                                                        ✏️ Change Teacher
                                                                    </button>
                                                                </div>
                                                            </td>

                                                            {/* 12th PCMB */}
                                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                                <div style={{ background: '#fcf2ff', border: `1px solid ${row.c12.color || '#a855f7'}`, borderRadius: '8px', padding: '10px' }}>
                                                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#581c87' }}>{row.c12.subject}</div>
                                                                    <div style={{ fontSize: '12px', color: '#7e22ce', fontWeight: '600', marginTop: '3px' }}>
                                                                        👨‍🏫 Teacher: <strong>{row.c12.teacher}</strong>
                                                                    </div>
                                                                    <button
                                                                        style={{ marginTop: '6px', background: '#f3e8ff', color: '#6b21a8', border: 'none', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const newT = prompt(`Assign Science Teacher for 12th on ${selectedDayTab} (${row.c12.subject}):`, row.c12.teacher);
                                                                            if (newT) {
                                                                                const updated = { ...dayWiseWhiteboard };
                                                                                updated[selectedDayTab][idx].c12.teacher = newT;
                                                                                setDayWiseWhiteboard(updated);
                                                                            }
                                                                        }}
                                                                    >
                                                                        ✏️ Change Teacher
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    /* CAREER ACADEMY TIMETABLE */
                                    <div>
                                        <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span>🎓 <strong>Career Academy ({selectedDayTab})</strong> &bull; Faculty: Siraa, Career Faculty</span>
                                            <span style={{ background: '#059669', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>{selectedDayTab} Schedule</span>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: '10px', overflow: 'hidden', border: '1px solid #cbd5e1', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                            <thead>
                                                <tr style={{ background: '#065f46', color: 'white', fontSize: '12px', textAlign: 'center', textTransform: 'uppercase' }}>
                                                    <th style={{ padding: '12px', width: '160px' }}>Time Slot</th>
                                                    <th style={{ padding: '12px', background: '#047857' }}>📙 Career Academy (JEE & NEET) Batch</th>
                                                    <th style={{ padding: '12px', background: '#0f766e' }}>📗 Career Foundation & Aptitude Batch</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(dayWiseWhiteboard[selectedDayTab] || dayWiseWhiteboard.Monday).map((row, idx) => {
                                                    if (row.isBreak) {
                                                        return (
                                                            <tr key={idx} style={{ background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
                                                                <td style={{ padding: '10px', fontSize: '12px', fontWeight: '700', color: '#92400e', textAlign: 'center' }}>{row.slot}</td>
                                                                <td colSpan="2" style={{ padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#b45309' }}>
                                                                    {row.label}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                    return (
                                                        <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0', background: idx % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                                            <td style={{ padding: '12px', fontSize: '12px', fontWeight: '700', color: '#475569', textAlign: 'center', background: '#f1f5f9' }}>
                                                                {row.slot}
                                                            </td>
                                                            
                                                            {/* Career Batch */}
                                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                                <div style={{ background: '#ecfdf5', border: `1px solid ${row.career.color || '#10b981'}`, borderRadius: '8px', padding: '10px' }}>
                                                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#065f46' }}>{row.career.subject}</div>
                                                                    <div style={{ fontSize: '12px', color: '#047857', fontWeight: '600', marginTop: '3px' }}>
                                                                        👨‍🏫 Teacher: <strong>{row.career.teacher}</strong>
                                                                    </div>
                                                                    <button
                                                                        style={{ marginTop: '6px', background: '#d1fae5', color: '#065f46', border: 'none', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const newT = prompt(`Assign Career Teacher for ${selectedDayTab} (${row.career.subject}):`, row.career.teacher);
                                                                            if (newT) {
                                                                                const updated = { ...dayWiseWhiteboard };
                                                                                updated[selectedDayTab][idx].career.teacher = newT;
                                                                                setDayWiseWhiteboard(updated);
                                                                            }
                                                                        }}
                                                                    >
                                                                        ✏️ Change Teacher
                                                                    </button>
                                                                </div>
                                                            </td>

                                                            {/* Career Foundation */}
                                                            <td style={{ padding: '10px', textAlign: 'center' }}>
                                                                <div style={{ background: '#f0fdf4', border: '1px solid #34d399', borderRadius: '8px', padding: '10px' }}>
                                                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#064e3b' }}>
                                                                        {idx === 0 ? 'Foundation Aptitude' : idx === 1 ? 'NEET Bio Practice' : idx === 3 ? 'Aptitude & Math' : 'Doubt Solving'}
                                                                    </div>
                                                                    <div style={{ fontSize: '12px', color: '#047857', fontWeight: '600', marginTop: '3px' }}>
                                                                        👨‍🏫 Teacher: <strong>{idx === 0 ? 'Siraa' : idx === 1 ? 'Ds' : 'Rtt'}</strong>
                                                                    </div>
                                                                    <button
                                                                        style={{ marginTop: '6px', background: '#d1fae5', color: '#065f46', border: 'none', padding: '3px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' }}
                                                                        onClick={() => {
                                                                            const newT = prompt(`Assign Foundation Teacher on ${selectedDayTab}:`, 'Siraa');
                                                                            if (newT) alert(`Assigned ${newT} to Foundation Batch!`);
                                                                        }}
                                                                    >
                                                                        ✏️ Change Teacher
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                            </div>
                        </div>

                    </div>
                </div>
            )}
            <TeacherFormPrintView teacher={printingTeacher} />
        </div >
    );
};

const Simulator = () => {
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(null);
    const [direction, setDirection] = useState('in');
    const [verifyMode, setVerifyMode] = useState('1'); // 1 = Fingerprint, 15 = Face
    const [time, setTime] = useState(new Date().toLocaleTimeString('en-GB', { hour12: false }));
    const [result, setResult] = useState(null);
    useEffect(() => { api.get('/users').then(res => { setUsers(res.data); if (res.data.length) setSelected(res.data[0]); }); }, []);
    const simulate = async () => {
        if (!selected) return;
        const now = new Date(); const [h, m, s] = time.split(':').map(Number); now.setHours(h, m, s || 0);
        try {
            const res = await api.post('/biometric/webhook', {
                fingerprint_id: selected.fingerprint_id,
                timestamp: now.toISOString(),
                direction,
                verifyMode
            });
            setResult({ success: true, msg: `✅ ${selected.name} marked ${direction === 'in' ? 'IN' : 'OUT'} via ${verifyMode === '15' ? 'Face Scan' : 'Fingerprint'} at ${time}`, data: res.data });
        } catch (err) { setResult({ success: false, msg: `❌ Error: ${err.response?.data?.error || err.message}` }); }
    };
    return (
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px', background: 'white', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <h2 style={{ textAlign: 'center' }}>🔍 Biometric Simulator</h2>
            <select style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '6px', border: '1px solid #ddd' }} onChange={(e) => setSelected(users.find(u => u.id === parseInt(e.target.value)))}>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
            <div style={{ marginBottom: '15px' }}>
                <label style={{ marginRight: '10px', fontWeight: 'bold' }}>Verify Mode: </label>
                <label style={{ marginRight: '15px' }}><input type="radio" value="1" checked={verifyMode === '1'} onChange={() => setVerifyMode('1')} /> 👆 Fingerprint</label>
                <label><input type="radio" value="15" checked={verifyMode === '15'} onChange={() => setVerifyMode('15')} /> 👤 Face Scan</label>
            </div>
            <div style={{ marginBottom: '15px' }}><label>Direction: </label><input type="radio" value="in" checked={direction === 'in'} onChange={() => setDirection('in')} /> IN <input type="radio" value="out" checked={direction === 'out'} onChange={() => setDirection('out')} /> OUT</div>
            <input type="time" step="1" value={time} onChange={(e) => setTime(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '15px', borderRadius: '6px', border: '1px solid #ddd' }} />
            <button onClick={simulate} style={{ width: '100%', padding: '12px', borderRadius: '6px', border: 'none', background: '#667eea', color: 'white', fontSize: '16px', cursor: 'pointer' }}>🖐️ Simulate Scan</button>
            {result && <div style={{ marginTop: '15px', padding: '15px', borderRadius: '6px', background: result.success ? '#d4edda' : '#f8d7da', color: result.success ? '#155724' : '#721c24' }}>{result.msg}</div>}
        </div>
    );
};

const EnrollStudentPage = () => {
    const [step, setStep] = useState(1);
    const [successData, setSuccessData] = useState(null);
    const [formData, setFormData] = useState({
        name: '', fatherName: '', motherName: '', aadhar: '', enquiryDate: new Date().toISOString().split('T')[0], gender: 'Male', dob: '', email: '', fingerprint_id: '', address: '',
        fatherContact: '', motherContact: '', studentContact: '',
        course: '', batchTiming: '', section: '',
        previousSchool: '', tenthPercent: '', twelfthPercent: '',
        bankName: '', accountNumber: '', ifscCode: '', accountHolder: '',
        amountReceived: '', paymentMode: '', installment: 'No', totalFee: ''
    });
    const [loading, setLoading] = useState(false);
    const [photoData, setPhotoData] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [cameraActive, setCameraActive] = useState(false);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const draftId = params.get('draftId');
        if (draftId) {
            const drafts = JSON.parse(localStorage.getItem('admission_drafts') || '[]');
            const matched = drafts.find(d => d.id === draftId);
            if (matched) {
                setFormData(matched.formData);
                setStep(matched.step || 1);
                if (matched.photoData) {
                    setPhotoData(matched.photoData);
                }
                localStorage.setItem('current_enroll_draft_id', draftId);
            }
        } else {
            localStorage.removeItem('current_enroll_draft_id');
        }
    }, []);

    useEffect(() => {
        if (!formData.name || formData.name.trim() === '') return;

        const drafts = JSON.parse(localStorage.getItem('admission_drafts') || '[]');

        let currentDraftId = localStorage.getItem('current_enroll_draft_id');
        if (!currentDraftId) {
            currentDraftId = 'draft_' + Date.now();
            localStorage.setItem('current_enroll_draft_id', currentDraftId);
        }

        const existingIdx = drafts.findIndex(d => d.id === currentDraftId);
        const draftData = {
            id: currentDraftId,
            formData,
            step,
            photoData,
            updatedAt: new Date().toISOString()
        };

        if (existingIdx > -1) {
            drafts[existingIdx] = draftData;
        } else {
            drafts.unshift(draftData);
        }
        localStorage.setItem('admission_drafts', JSON.stringify(drafts));
    }, [formData, step, photoData]);

    const startCamera = async () => {
        setCameraActive(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error("Camera error:", err);
            alert("Could not access camera");
            setCameraActive(false);
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            videoRef.current.srcObject.getTracks().forEach(t => t.stop());
        }
        setCameraActive(false);
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            const maxDimension = 300;
            let width = videoRef.current.videoWidth;
            let height = videoRef.current.videoHeight;
            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = Math.round((height * maxDimension) / width);
                    width = maxDimension;
                } else {
                    width = Math.round((width * maxDimension) / height);
                    height = maxDimension;
                }
            }
            canvasRef.current.width = width;
            canvasRef.current.height = height;
            context.drawImage(videoRef.current, 0, 0, width, height);
            const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.7);
            setPhotoData(dataUrl);
            stopCamera();
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                compressImage(reader.result, (compressed) => {
                    setPhotoData(compressed);
                });
            };
            reader.readAsDataURL(file);
        }
    };

    const [existingUserBanner, setExistingUserBanner] = useState(null);

    const checkBiometricId = async (cleanId) => {
        if (!cleanId || cleanId.trim() === '') {
            setExistingUserBanner(null);
            return;
        }
        try {
            const res = await api.get(`/users/check-biometric/${cleanId.trim()}`);
            if (res.data && res.data.exists && res.data.user) {
                const u = res.data.user;
                setFormData(prev => ({
                    ...prev,
                    name: u.name || prev.name,
                    email: u.email || prev.email,
                    studentContact: u.studentPhone || u.studentContact || prev.studentContact,
                    fatherContact: u.parentPhone || u.fatherContact || prev.fatherContact,
                    motherContact: u.motherContact || prev.motherContact,
                    section: u.section || prev.section,
                    aadhar: u.aadhar || prev.aadhar,
                    gender: u.gender || prev.gender,
                    dob: u.dob || prev.dob,
                    address: u.address || prev.address
                }));
                if (u.photo) setPhotoData(u.photo);
                setExistingUserBanner(`ℹ️ Existing user found: "${u.name}" (Biometric ID: ${cleanId}). Submitting will update their profile.`);
            } else {
                setExistingUserBanner(null);
            }
        } catch (e) {
            setExistingUserBanner(null);
        }
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        if (name === 'dob' && value) {
            const parts = value.split('-');
            if (parts[0] && parts[0].length > 4) {
                parts[0] = parts[0].substring(0, 4);
                finalValue = parts.join('-');
            }
        }
        if ((name === 'tenthPercent' || name === 'twelfthPercent') && value) {
            const num = parseFloat(value);
            if (num > 100) {
                finalValue = '100';
            } else if (num < 0) {
                finalValue = '0';
            } else if (value.length > 6) {
                finalValue = value.substring(0, 6);
            }
        }
        if (name === 'fingerprint_id') {
            checkBiometricId(finalValue);
        }
        setFormData({ ...formData, [name]: finalValue });
    };

    const getStepProgressWidth = () => {
        if (step === 1) return '0%';
        if (step === 2) return '33%';
        if (step === 3) return '66%';
        if (step === 4) return '100%';
        return '0%';
    };

    const numberToWords = (num) => {
        const nStr = num ? num.toString() : '0';
        const nNum = parseInt(nStr, 10);
        if (isNaN(nNum) || nNum === 0) return 'Zero';
        const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
        const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
        if (nStr.length > 9) return 'overflow';
        const n = ('000000000' + nStr).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
        if (!n) return 'Zero';
        let str = '';
        str += (n[1] != 0) ? (a[Number(n[1])] || b[n[1][0]] + ' ' + a[n[1][1]]) + 'Crore ' : '';
        str += (n[2] != 0) ? (a[Number(n[2])] || b[n[2][0]] + ' ' + a[n[2][1]]) + 'Lakh ' : '';
        str += (n[3] != 0) ? (a[Number(n[3])] || b[n[3][0]] + ' ' + a[n[3][1]]) + 'Thousand ' : '';
        str += (n[4] != 0) ? a[Number(n[4])] + 'Hundred ' : '';
        str += (n[5] != 0) ? (a[Number(n[5])] || b[n[5][0]] + ' ' + a[n[5][1]]) : '';
        return str.trim() + ' Only';
    };

    const netFee = parseInt(formData.totalFee) || 0;
    const receivedNum = parseInt(formData.amountReceived) || 0;
    const dueFees = Math.max(0, netFee - receivedNum);
    const amountWords = receivedNum ? numberToWords(receivedNum) : 'Zero';

    const step1Keys = ['name', 'fatherName', 'motherName', 'aadhar', 'enquiryDate', 'gender', 'dob', 'email', 'fingerprint_id', 'address', 'fatherContact', 'motherContact', 'studentContact'];
    const step2Keys = ['course', 'batchTiming'];
    const step3Keys = ['previousSchool', 'tenthPercent', 'twelfthPercent'];
    const step4Keys = ['bankName', 'accountNumber', 'ifscCode', 'accountHolder', 'amountReceived', 'paymentMode', 'installment', 'totalFee'];

    const friendlyNames = {
        name: "Student Name", fatherName: "Father's Name", motherName: "Mother's Name", aadhar: "Aadhar Number", enquiryDate: "Enquiry Date", gender: "Gender", dob: "Date of Birth", email: "Email", fingerprint_id: "Biometric Registration No.", address: "Address", fatherContact: "Father Contact", motherContact: "Mother Contact", studentContact: "Student Contact",
        course: "Course", batchTiming: "Batch Timing", previousSchool: "Previous School", tenthPercent: "10th Percentage", twelfthPercent: "12th Percentage",
        bankName: "Bank Name", accountNumber: "Account Number", ifscCode: "IFSC Code", accountHolder: "Account Holder", amountReceived: "Amount Received", paymentMode: "Payment Mode", installment: "Installment", totalFee: "Total Fee"
    };

    const validateStep = (stepToValidate) => {
        if (stepToValidate === 1) {
            if (!formData.name || formData.name.trim() === '') {
                alert("Student Name is strictly required by the database!");
                return false;
            }
            if (!formData.fingerprint_id || formData.fingerprint_id.trim() === '') {
                alert("Biometric Registration No. is strictly required by the database!");
                return false;
            }
            if (formData.email && formData.email.trim() !== '') {
                const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                if (!emailRegex.test(formData.email.trim())) {
                    alert("Please enter a valid email address (e.g. example@gmail.com).");
                    return false;
                }
            }
        }
        return true;
        let keysToCheck = [];
        if (stepToValidate === 1) keysToCheck = step1Keys;
        else if (stepToValidate === 2) keysToCheck = step2Keys;
        else if (stepToValidate === 3) keysToCheck = step3Keys;
        else if (stepToValidate === 4) keysToCheck = step4Keys;

        for (const key of keysToCheck) {
            if (typeof formData[key] === 'string' && formData[key].trim() === '') {
                alert(`Please fill out all fields in this step before proceeding. Missing: ${friendlyNames[key] || key}`);
                return false;
            }
        }

        if (stepToValidate === 1) {
            if (formData.studentContact === formData.fatherContact) {
                alert("Student Contact Number and Father's Contact Number must be different.");
                return false;
            }
            if (formData.studentContact === formData.motherContact) {
                alert("Student Contact Number and Mother's Contact Number must be different.");
                return false;
            }
        }

        return true;
    };

    const handleNextStep = () => {
        if (!validateStep(step)) return;
        setStep(step + 1);
    };

    const handleStepClick = (targetStep) => {
        if (targetStep > step) {
            for (let i = step; i < targetStep; i++) {
                if (!validateStep(i)) {
                    setStep(i);
                    return;
                }
            }
        }
        setStep(targetStep);
    };

    const handleSubmit = async () => {
        // Final sanity check
        for (let i = 1; i <= 4; i++) {
            if (!validateStep(i)) {
                setStep(i);
                return;
            }
        }

        setLoading(true);
        try {
            let receiptNo = null;
            if (receivedNum > 0) {
                const recRes = await api.post('/receipts/generate', {
                    studentName: formData.name,
                    courseName: formData.course,
                    amountReceived: receivedNum,
                    paymentMode: formData.paymentMode
                });
                receiptNo = recRes.data.receiptNo;
            }

            const payload = {
                ...formData,
                role: 'student',
                fee: netFee,
                dueFees,
                amountReceivedWords: amountWords,
                receiptNo,
                photo: photoData
            };
            const res = await api.post('/users/enroll', payload);

            const currentDraftId = localStorage.getItem('current_enroll_draft_id');
            if (currentDraftId) {
                const drafts = JSON.parse(localStorage.getItem('admission_drafts') || '[]');
                const updated = drafts.filter(d => d.id !== currentDraftId);
                localStorage.setItem('admission_drafts', JSON.stringify(updated));
                localStorage.removeItem('current_enroll_draft_id');
            }

            setSuccessData(payload);
        } catch (err) {
            alert('Error: ' + (err.response?.data?.error || err.message));
        }
        setLoading(false);
    };

    if (successData) {
        return (
            <div style={{ background: 'white', padding: '40px', borderRadius: '8px', minHeight: '600px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>✅</div>
                <h2 style={{ color: '#10b981', marginBottom: '10px' }}>Student Enrollment Successful!</h2>
                <p style={{ color: '#666', marginBottom: '30px' }}>{successData.name} has been enrolled successfully.</p>
                {successData.receiptNo && <p style={{ fontWeight: 'bold', marginBottom: '20px' }}>Receipt No: {successData.receiptNo}</p>}

                <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                    <button onClick={() => { document.body.classList.add('print-admission'); window.print(); setTimeout(() => document.body.classList.remove('print-admission'), 500); }} style={{ padding: '12px 24px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
                        🖨️ Print Admission Form
                    </button>
                    <button onClick={() => { document.body.classList.add('print-receipt'); window.print(); setTimeout(() => document.body.classList.remove('print-receipt'), 500); }} style={{ padding: '12px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
                        🖨️ Print Fee Receipt
                    </button>
                    <button onClick={() => window.location.href = '/admissions'} style={{ padding: '12px 24px', background: '#f8f9fa', color: '#555', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
                        Done
                    </button>
                </div>

                {/* Hidden print components */}
                <AdmissionFormPrintView student={successData} />
                <ReceiptPrintView student={successData} />
            </div>
        );
    }

    return (
        <div style={{ background: 'white', padding: '30px', borderRadius: '8px', minHeight: '600px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
            <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold', marginBottom: '50px' }}>
                <span style={{ marginRight: '8px' }}>👤+</span> Enroll Student
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '60px', position: 'relative' }}>
                <div style={{ position: 'absolute', top: '15px', left: '12%', right: '12%', height: '2px', background: '#e0e0e0', zIndex: 1 }}>
                    <div style={{ width: getStepProgressWidth(), height: '100%', background: '#8b5cf6', transition: 'width 0.3s' }}></div>
                </div>

                {[
                    { id: 1, title: 'Basic Details', desc: 'Setup Basic Details' },
                    { id: 2, title: 'Class Details', desc: 'Setup Class Details' },
                    { id: 3, title: 'Academic Details', desc: 'Setup Academic Details' },
                    { id: 4, title: 'Photo & Payment', desc: 'Setup Photo & Payment' }
                ].map((s) => (
                    <div key={s.id} onClick={() => handleStepClick(s.id)} style={{ textAlign: 'center', position: 'relative', zIndex: 2, background: 'white', padding: '0 10px', flex: 1, cursor: 'pointer' }}>
                        <div style={{
                            width: step === s.id ? '20px' : '24px',
                            height: step === s.id ? '20px' : '24px',
                            borderRadius: '50%',
                            border: step >= s.id ? '6px solid #8b5cf6' : '2px solid #e0e0e0',
                            background: 'white',
                            margin: step === s.id ? '0 auto 10px' : '4px auto 10px',
                            transition: 'all 0.3s'
                        }}></div>
                        <div style={{ fontWeight: 'bold', color: step >= s.id ? '#555' : '#888', marginTop: '10px' }}>{s.title}</div>
                        <div style={{ fontSize: '12px', color: '#aaa' }}>{s.desc}</div>
                    </div>
                ))}
            </div>

            {step === 1 && (
                <div>
                    <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', marginBottom: '20px' }}>
                        👤 Basic Detail
                    </div>
                    {existingUserBanner && (
                        <div style={{ background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '12px', fontSize: '13px', marginBottom: '20px', fontWeight: '500', lineHeight: '1.4' }}>
                            {existingUserBanner}
                        </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px', marginTop: '10px' }}>
                        <div>
                            <input type="text" name="name" value={formData.name} onChange={handleInput} placeholder="Student Name*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555' }} />
                        </div>
                        <div>
                            <input type="text" name="fatherName" value={formData.fatherName} onChange={handleInput} placeholder="Father's Name*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555' }} />
                        </div>
                        <div>
                            <input type="text" name="motherName" value={formData.motherName} onChange={handleInput} placeholder="Mother's Name*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555' }} />
                        </div>
                        <div>
                            <input type="text" name="aadhar" value={formData.aadhar} onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 12); handleInput(e); }} placeholder="Aadhar Number*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div style={{ position: 'relative' }}>
                            <label style={{ position: 'absolute', top: '-8px', left: '10px', background: 'white', padding: '0 5px', fontSize: '12px', color: '#666' }}>Enquiry Date*</label>
                            <input type="date" name="enquiryDate" value={formData.enquiryDate} onChange={handleInput} style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555' }} />
                        </div>

                        <div style={{ position: 'relative' }}>
                            <label style={{ position: 'absolute', top: '-8px', left: '10px', background: 'white', padding: '0 5px', fontSize: '12px', color: '#666' }}>Gender*</label>
                            <select name="gender" value={formData.gender} onChange={handleInput} style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555', appearance: 'none', background: 'url("data:image/svg+xml;utf8,<svg fill=%27black%27 height=%2724%27 viewBox=%270 0 24 24%27 width=%2724%27 xmlns=%27http://www.w3.org/2000/svg%27><path d=%27M7 10l5 5 5-5z%27/><path d=%27M0 0h24v24H0z%27 fill=%27none%27/></svg>") no-repeat right 10px center' }}>
                                <option>Male</option>
                                <option>Female</option>
                                <option>Transgender</option>
                                <option>Other</option>
                            </select>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <label style={{ position: 'absolute', top: '-8px', left: '10px', background: 'white', padding: '0 5px', fontSize: '12px', color: '#666' }}>Date of Birth*</label>
                            <input type="date" name="dob" value={formData.dob} onChange={handleInput} style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#999' }} />
                        </div>
                        <div>
                            <input type="email" name="email" value={formData.email} onChange={handleInput} placeholder="Email Id*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div>
                            <input type="text" name="fingerprint_id" value={formData.fingerprint_id} onChange={handleInput} placeholder="Biometric Registration No.*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>

                        <div>
                            <input type="text" name="fatherContact" value={formData.fatherContact} onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10); handleInput(e); }} placeholder="Father Contact Number*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div>
                            <input type="text" name="motherContact" value={formData.motherContact} onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10); handleInput(e); }} placeholder="Mother Contact Number*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div>
                            <input type="text" name="studentContact" value={formData.studentContact} onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10); handleInput(e); }} placeholder="Student Contact Number*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <textarea name="address" value={formData.address} onChange={handleInput} placeholder="Full Residential Address*" rows="3" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', resize: 'vertical' }}></textarea>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div>
                    <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', marginBottom: '20px' }}>
                        📚 Class Detail
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px', marginTop: '10px' }}>
                        <div>
                            <select name="course" value={formData.course} onChange={handleInput} style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555', appearance: 'none', background: 'url("data:image/svg+xml;utf8,<svg fill=%27black%27 height=%2724%27 viewBox=%270 0 24 24%27 width=%2724%27 xmlns=%27http://www.w3.org/2000/svg%27><path d=%27M7 10l5 5 5-5z%27/><path d=%27M0 0h24v24H0z%27 fill=%27none%27/></svg>") no-repeat right 10px center' }}>
                                <option value="" disabled>Select Course*</option>
                                <option>Staff Selection Commission (SSC-CGL)</option>
                                <option>POLICE/ARMY/MILITARY TRAINING BATCH</option>
                                <option>XI - Science PCMB [JEE-NEET-CET]</option>
                                <option>XII - Science PCMB [JEE-NEET-CET]</option>
                                <option>UPSC - Civil Services Examination</option>
                                <option>UGC NET/ MH SET/ CSIR NET</option>
                                <option>MBA Entrance (CAT/MAT/MH CET)</option>
                                <option>MPSC - State Services Examination</option>
                                <option>Maharashtra Engineering Services (MES)</option>
                                <option>Banking [RBI/SBI/NABARD/SSC/IBPS]</option>
                                <option>MPSC (Group B & C)</option>
                                <option>RRB-NTPC</option>
                            </select>
                        </div>
                        <div>
                            <input type="text" name="batchTiming" value={formData.batchTiming} onChange={handleInput} placeholder="Batch Timing*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div>
                            <input type="text" name="section" value={formData.section} onChange={handleInput} placeholder="Section (Optional, E.g. Section A)" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                    </div>
                </div>
            )}

            {step === 3 && (
                <div>
                    <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', marginBottom: '20px' }}>
                        🎓 Academic Detail
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px', marginTop: '10px' }}>
                        <div>
                            <input type="text" name="previousSchool" value={formData.previousSchool} onChange={handleInput} placeholder="Previous School/College Name*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                        </div>
                        <div style={{ position: 'relative' }}>
                            <input type="number" name="tenthPercent" value={formData.tenthPercent} onChange={handleInput} placeholder="10th Percentage*" style={{ width: '100%', padding: '14px 35px 14px 14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                            <span style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontWeight: 'bold', pointerEvents: 'none' }}>%</span>
                        </div>
                        <div style={{ position: 'relative' }}>
                            <input type="number" name="twelfthPercent" value={formData.twelfthPercent} onChange={handleInput} placeholder="12th Percentage*" style={{ width: '100%', padding: '14px 35px 14px 14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                            <span style={{ position: 'absolute', right: '15px', top: '50%', transform: 'translateY(-50%)', color: '#888', fontWeight: 'bold', pointerEvents: 'none' }}>%</span>
                        </div>
                    </div>
                </div>
            )}

            {step === 4 && (
                <div>
                    <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', marginBottom: '20px' }}>
                        📷 Photo & Payment
                    </div>

                    <div style={{ gridColumn: '1 / -1', marginTop: '10px' }}>
                        <div style={{ fontWeight: 'bold', color: '#555', marginBottom: '15px' }}>Student Photo</div>
                        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                            <div style={{ flex: 1, minWidth: '250px' }}>
                                <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>Option 1: Upload from Computer</div>
                                <input type="file" accept="image/*" onChange={handleFileUpload} style={{ padding: '8px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: '250px' }}>
                                <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>Option 2: Take Live Photo</div>
                                {!cameraActive && !photoData && (
                                    <button onClick={startCamera} style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>📷 Start Webcam</button>
                                )}
                                {cameraActive && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        <video ref={videoRef} autoPlay style={{ width: '100%', maxWidth: '300px', borderRadius: '8px', background: '#000' }}></video>
                                        <div style={{ display: 'flex', gap: '10px' }}>
                                            <button onClick={capturePhoto} style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>📸 Capture</button>
                                            <button onClick={stopCamera} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
                            </div>
                            {photoData && (
                                <div style={{ width: '150px', textAlign: 'center' }}>
                                    <img src={photoData} alt="Preview" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #8b5cf6', marginBottom: '8px' }} />
                                    <button onClick={() => setPhotoData(null)} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer' }}>Remove Photo</button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #eee', marginTop: '15px', paddingTop: '25px' }}>
                        <div style={{ fontWeight: 'bold', color: '#555', marginBottom: '15px' }}>Fees & Payment Details</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '25px' }}>
                            <div>
                                <input type="text" name="totalFee" value={formData.totalFee} onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ''); handleInput(e); }} placeholder="Total Fee (₹)*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                            </div>
                            <div>
                                <input type="text" name="amountReceived" value={formData.amountReceived} onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ''); handleInput(e); }} placeholder="Amount Received (₹)*" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none' }} />
                            </div>
                            <div>
                                <input type="text" value={dueFees} readOnly placeholder="Due Fees (₹)" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', background: '#f8f9fa' }} />
                            </div>
                            <div style={{ position: 'relative' }}>
                                <label style={{ position: 'absolute', top: '-8px', left: '10px', background: 'white', padding: '0 5px', fontSize: '12px', color: '#666' }}>Payment Mode*</label>
                                <select name="paymentMode" value={formData.paymentMode} onChange={handleInput} style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555', appearance: 'none', background: 'url("data:image/svg+xml;utf8,<svg fill=%27black%27 height=%2724%27 viewBox=%270 0 24 24%27 width=%2724%27 xmlns=%27http://www.w3.org/2000/svg%27><path d=%27M7 10l5 5 5-5z%27/><path d=%27M0 0h24v24H0z%27 fill=%27none%27/></svg>") no-repeat right 10px center' }}>
                                    <option value="" disabled>Select Payment Mode*</option>
                                    <option>Cash</option>
                                    <option>Online</option>
                                    <option>Net Banking</option>
                                </select>
                            </div>
                            <div style={{ position: 'relative' }}>
                                <label style={{ position: 'absolute', top: '-8px', left: '10px', background: 'white', padding: '0 5px', fontSize: '12px', color: '#666' }}>Installment*</label>
                                <select name="installment" value={formData.installment} onChange={handleInput} style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', color: '#555', appearance: 'none', background: 'url("data:image/svg+xml;utf8,<svg fill=%27black%27 height=%2724%27 viewBox=%270 0 24 24%27 width=%2724%27 xmlns=%27http://www.w3.org/2000/svg%27><path d=%27M7 10l5 5 5-5z%27/><path d=%27M0 0h24v24H0z%27 fill=%27none%27/></svg>") no-repeat right 10px center' }}>
                                    <option value="No">No</option>
                                    <option value="Yes">Yes</option>
                                </select>
                            </div>
                            <div style={{ gridColumn: '1 / -1' }}>
                                <input type="text" value={amountWords} readOnly placeholder="Amount Received In Words" style={{ width: '100%', padding: '14px', border: '1px solid #e0e0e0', borderRadius: '6px', outline: 'none', background: '#f8f9fa' }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '40px' }}>
                {step > 1 && (
                    <button onClick={() => setStep(step - 1)} style={{ padding: '12px 24px', marginRight: '15px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer', color: '#555', fontWeight: 'bold' }}>
                        Previous
                    </button>
                )}
                {step < 4 && (
                    <button onClick={handleNextStep} style={{ padding: '12px 24px', background: '#8b5cf6', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'white', fontWeight: 'bold', boxShadow: '0 4px 6px rgba(139, 92, 246, 0.2)' }}>
                        Next Step
                    </button>
                )}
                {step === 4 && (
                    <button onClick={handleSubmit} disabled={loading} style={{ padding: '12px 24px', background: '#27ae60', border: 'none', borderRadius: '6px', cursor: 'pointer', color: 'white', fontWeight: 'bold', boxShadow: '0 4px 6px rgba(39, 174, 96, 0.2)' }}>
                        {loading ? 'Enrolling...' : 'Enroll Student'}
                    </button>
                )}
            </div>
        </div>
    );
};

// --- DEVICES PAGE ---
const DevicesPage = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingDevice, setEditingDevice] = useState(null);
    const [newName, setNewName] = useState('');
    const [liveScans, setLiveScans] = useState([]);
    const [userMap, setUserMap] = useState({});
    const [users, setUsers] = useState([]);
    const [toast, setToast] = useState(null);
    const [syncStatus, setSyncStatus] = useState({});
    const toastTimerRef = React.useRef(null);

    // States for IP-based biometric connection
    const [serverIp, setServerIp] = useState('');
    const [showConnectIpModal, setShowConnectIpModal] = useState(false);
    const [deviceIp, setDeviceIp] = useState('');
    const [devicePort, setDevicePort] = useState('4370');
    const [connectLoading, setConnectLoading] = useState(false);
    const [connectResult, setConnectResult] = useState(null);
    const [discoverLoading, setDiscoverLoading] = useState(false);

    // States for full import from machine
    const [fullImportIp, setFullImportIp] = useState('192.168.0.150');
    const [fullImportPort, setFullImportPort] = useState('4370');
    const [fullImportLoading, setFullImportLoading] = useState(false);
    const [fullImportResult, setFullImportResult] = useState(null);
    const [forceSyncLoading, setForceSyncLoading] = useState(false);
    const [forceSyncResult, setForceSyncResult] = useState(null);

    const handleDiscoverLAN = () => {
        setDiscoverLoading(true);
        api.post('/devices/discover-lan')
            .then(res => {
                setDiscoverLoading(false);
                alert(`🔍 LAN Auto-Discovery Complete!\nFound & Connected ${res.data.count} Biometric Device(s) on your Wi-Fi/LAN.`);
                fetchDevices();
            })
            .catch(err => {
                setDiscoverLoading(false);
                alert("Auto-discovery failed: " + (err.response?.data?.error || err.message));
            });
    };

    const handleFullImport = (e) => {
        e.preventDefault();
        if (!fullImportIp.trim()) return;
        setFullImportLoading(true);
        setFullImportResult(null);
        api.post('/devices/full-import', { ip: fullImportIp, port: fullImportPort })
            .then(res => {
                setFullImportLoading(false);
                setFullImportResult({ success: true, ...res.data });
                fetchDevices();
            })
            .catch(err => {
                setFullImportLoading(false);
                setFullImportResult({ success: false, message: err.response?.data?.error || err.message });
            });
    };

    const handleForceSyncAdms = () => {
        setForceSyncLoading(true);
        setForceSyncResult(null);
        api.post('/devices/force-sync-adms')
            .then(res => {
                setForceSyncLoading(false);
                setForceSyncResult(res.data);
            })
            .catch(err => {
                setForceSyncLoading(false);
                setForceSyncResult({ success: false, message: err.response?.data?.error || err.message });
            });
    };

    const handleConnectIp = (e) => {
        e.preventDefault();
        if (!deviceIp.trim()) return;
        setConnectLoading(true);
        setConnectResult(null);

        api.post('/devices/connect-ip', { ip: deviceIp, port: devicePort })
            .then(res => {
                setConnectLoading(false);
                setConnectResult({
                    success: true,
                    message: res.data.message || 'Connected successfully!',
                    usersImported: res.data.usersImported,
                    punchesImported: res.data.punchesImported
                });
                fetchDevices();
            })
            .catch(err => {
                setConnectLoading(false);
                const errMsg = err.response?.data?.error || err.message || 'Failed to connect.';
                setConnectResult({
                    success: false,
                    message: errMsg
                });
            });
    };

    const resolvedName = (scan) => {
        const user = users.find(u => String(u.id) === String(scan.userId) || String(u.fingerprint_id) === String(scan.userId));
        return user ? user.name : (scan.userName || `User ${scan.userId}`);
    };

    const resolvedPhoto = (scan) => {
        let photo = null;
        if (scan.userPhoto) {
            photo = scan.userPhoto;
        } else {
            const user = users.find(u => String(u.id) === String(scan.userId) || String(u.fingerprint_id) === String(scan.userId));
            photo = user ? user.photo : null;
        }
        if (photo) {
            if (photo.startsWith('http') || photo.startsWith('data:')) return photo;
            return getPhotoUrl(photo);
        }
        return null;
    };

    const isFaceScan = (scan) => String(scan.verifyMode) === '15';

    const handleSyncUsers = (serialNumber) => {
        setSyncStatus(prev => ({ ...prev, [serialNumber]: 'Syncing...' }));
        api.post('/devices/sync-users', { serialNumber })
            .then(res => {
                setSyncStatus(prev => ({ ...prev, [serialNumber]: 'Command Queued!' }));
                setTimeout(() => {
                    setSyncStatus(prev => ({ ...prev, [serialNumber]: null }));
                }, 5000);
            })
            .catch(err => {
                setSyncStatus(prev => ({ ...prev, [serialNumber]: 'Failed' }));
                setTimeout(() => {
                    setSyncStatus(prev => ({ ...prev, [serialNumber]: null }));
                }, 5000);
                console.error(err);
            });
    };

    const handleSyncPhotos = (serialNumber) => {
        setSyncStatus(prev => ({ ...prev, [serialNumber + '_photo']: 'Syncing...' }));
        api.post('/devices/sync-photos', { serialNumber })
            .then(res => {
                setSyncStatus(prev => ({ ...prev, [serialNumber + '_photo']: 'Command Queued!' }));
                setTimeout(() => {
                    setSyncStatus(prev => ({ ...prev, [serialNumber + '_photo']: null }));
                }, 5000);
            })
            .catch(err => {
                setSyncStatus(prev => ({ ...prev, [serialNumber + '_photo']: 'Failed' }));
                setTimeout(() => {
                    setSyncStatus(prev => ({ ...prev, [serialNumber + '_photo']: null }));
                }, 5000);
                console.error(err);
            });
    };

    const handleDeleteDevice = (serialNumber) => {
        if (!window.confirm("Are you sure you want to remove this biometric device?")) return;
        
        api.delete(`/devices/${serialNumber}`)
            .then(() => {
                alert("Device removed successfully.");
                fetchDevices();
            })
            .catch(err => {
                alert("Failed to remove device: " + (err.response?.data?.error || err.message));
            });
    };

    const handleRegisterUser = (e) => {
        e.preventDefault();
        if (!registeringUserName.trim() || !registeringUserId) return;
        api.post('/users', {
            id: registeringUserId,
            name: registeringUserName,
            role: registeringUserRole || 'student',
            fingerprint_id: String(registeringUserId)
        })
            .then(() => {
                // Refresh userMap
                api.get('/users/map').then(res => setUserMap(res.data)).catch(() => { });
                setRegisteringUserId(null);
                setRegisteringUserName('');
            })
            .catch(err => console.error(err));
    };

    const fetchDevices = () => {
        setLoading(true);
        api.get('/devices')
            .then(res => { setDevices(res.data); setLoading(false); })
            .catch(err => { console.error(err); setLoading(false); });
    };

    useEffect(() => {
        fetchDevices();

        // Fetch server LAN IP info
        api.get('/info').then(res => setServerIp(res.data.ip)).catch(() => { });

        // Load users list
        api.get('/users').then(res => setUsers(res.data)).catch(() => { });

        // Load user ID → name map
        api.get('/users/map').then(res => setUserMap(res.data)).catch(() => { });

        // Listen for live biometric punches via Socket.io
        const handleLivePunch = (data) => {
            const scanEntry = {
                ...data,
                id: Date.now(),
                receivedAt: new Date().toLocaleTimeString()
            };

            // Add to live feed (keep last 20)
            setLiveScans(prev => [scanEntry, ...prev].slice(0, 20));

            // Show pop-up toast
            setToast(scanEntry);
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            toastTimerRef.current = setTimeout(() => setToast(null), 5000);

            // Refresh device status to update lastActive
            fetchDevices();
        };

        const handleLivePunchPhoto = (data) => {
            setLiveScans(prev => prev.map(s => {
                if (String(s.userId) === String(data.userId)) {
                    return { ...s, userPhoto: data.userPhoto };
                }
                return s;
            }));

            setToast(prev => {
                if (prev && String(prev.userId) === String(data.userId)) {
                    return { ...prev, userPhoto: data.userPhoto };
                }
                return prev;
            });

            setUsers(prev => prev.map(u => {
                if (String(u.id) === String(data.userId) || String(u.fingerprint_id) === String(data.userId)) {
                    return { ...u, photo: data.userPhoto };
                }
                return u;
            }));
        };

        socket.on('live_punch', handleLivePunch);
        socket.on('live_punch_photo', handleLivePunchPhoto);
        return () => {
            socket.off('live_punch', handleLivePunch);
            socket.off('live_punch_photo', handleLivePunchPhoto);
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    const handleRename = (e) => {
        e.preventDefault();
        if (!newName.trim() || !editingDevice) return;
        api.post('/devices/rename', { serialNumber: editingDevice.serialNumber, name: newName })
            .then(() => {
                setDevices(devices.map(d =>
                    d.serialNumber === editingDevice.serialNumber ? { ...d, name: newName } : d
                ));
                setEditingDevice(null);
                setNewName('');
            })
            .catch(err => console.error(err));
    };

    const getInitials = (name) => name ? name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '??';

    const toastStyle = {
        position: 'fixed', top: '24px', right: '24px', zIndex: 9999,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        color: 'white', borderRadius: '16px', padding: '20px 24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.4)', minWidth: '320px',
        border: '1px solid rgba(102,126,234,0.4)',
        animation: 'slideInRight 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
        display: 'flex', alignItems: 'center', gap: '16px'
    };

    return (
        <div>
            {/* LIVE TOAST NOTIFICATION */}
            {toast && (
                <div style={toastStyle}>
                    <div style={{
                        width: '48px', height: '48px', borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea, #764ba2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontWeight: '800', fontSize: '16px', flexShrink: 0,
                        overflow: 'hidden'
                    }}>
                        {resolvedPhoto(toast) ? (
                            <img src={resolvedPhoto(toast)} alt="Face" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                            isFaceScan(toast) ? '👤' : '👆'
                        )}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '11px', color: '#a0a8c0', fontWeight: '600', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' }}>
                            🔴 {isFaceScan(toast) ? 'Live Face Scan Detected' : 'Live Fingerprint Scan Detected'}
                        </div>
                        <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '2px' }}>
                            {resolvedName(toast)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#8892b0' }}>
                            ID: {toast.userId} &nbsp;•&nbsp; 📟 {toast.deviceSn} &nbsp;•&nbsp; 🕐 {toast.receivedAt}
                        </div>
                    </div>
                    <button onClick={() => setToast(null)} style={{
                        background: 'transparent', border: 'none', color: '#8892b0',
                        fontSize: '18px', cursor: 'pointer', padding: '4px', lineHeight: 1
                    }}>✕</button>
                </div>
            )}

            {/* CSS Animations */}
            <style>{`
        @keyframes slideInRight {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.5); }
        }
        @keyframes scanFadeIn {
          from { opacity: 0; transform: translateY(-12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .live-scan-row { animation: scanFadeIn 0.4s ease-out; }
        .pulse-red {
          width: 10px; height: 10px; border-radius: 50%; background: #e74c3c;
          animation: pulse-dot 1.2s ease-in-out infinite;
          display: inline-block; margin-right: 8px;
        }
        .pulse-green {
          width: 10px; height: 10px; border-radius: 50%; background: #27ae60;
          animation: pulse-dot 1.2s ease-in-out infinite;
          display: inline-block; margin-right: 8px;
        }
      `}</style>

            {/* DEVICES TABLE */}
            <div className="dashboard-card" style={{ marginBottom: '20px' }}>
                <div className="card-header">
                    <h2>🔒 Biometric Devices Manager</h2>
                    <div className="actions" style={{ display: 'flex', gap: '10px' }}>
                        <button className="btn btn-success" onClick={() => { setShowConnectIpModal(true); setConnectResult(null); setDeviceIp(''); setDevicePort('4370'); }} style={{ background: '#8b5cf6', borderColor: '#8b5cf6' }}>🔌 Connect via IP</button>
                        <button className="btn btn-primary" onClick={fetchDevices}>🔄 Refresh Status</button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>Loading devices...</div>
                ) : (
                    <table className="dashboard-table">
                        <thead>
                            <tr>
                                <th>Device Name</th>
                                <th>Serial Number</th>
                                <th>Last Active / Ping</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {devices.map((device, idx) => (
                                <tr key={device.serialNumber || idx}>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <span style={{ fontSize: '22px' }}>📟</span>
                                            <div>
                                                <strong style={{ fontSize: '15px' }}>{device.name}</strong>
                                                <div style={{ fontSize: '12px', color: '#888' }}>MAC: 00:17:61:10:35:c5</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td><code style={{ background: '#f0f2f5', padding: '4px 8px', borderRadius: '4px', fontSize: '13px' }}>{device.serialNumber}</code></td>
                                    <td>{new Date(device.lastActive).toLocaleString()}</td>
                                    <td>
                                        <span className={`status ${device.status === 'online' ? 'present' : 'absent'}`}>
                                            {device.status === 'online' ? '● Online' : '● Offline'}
                                        </span>
                                    </td>
                                    <td>
                                        <button
                                            className="btn btn-warning"
                                            style={{ padding: '4px 10px', fontSize: '13px' }}
                                            onClick={() => { setEditingDevice(device); setNewName(device.name); }}
                                        >✏️ Rename</button>
                                        <button
                                            className="btn btn-primary"
                                            style={{ padding: '4px 10px', fontSize: '13px', background: '#3498db', border: 'none', marginRight: '8px' }}
                                            disabled={syncStatus[device.serialNumber]}
                                            onClick={() => handleSyncUsers(device.serialNumber)}
                                        >
                                            {syncStatus[device.serialNumber] || '📥 Sync Names'}
                                        </button>
                                        <button
                                            className="btn btn-success"
                                            style={{ padding: '4px 10px', fontSize: '13px', background: '#27ae60', border: 'none' }}
                                            disabled={syncStatus[device.serialNumber + '_photo']}
                                            onClick={() => handleSyncPhotos(device.serialNumber)}
                                        >
                                            {syncStatus[device.serialNumber + '_photo'] || '📸 Sync Photos'}
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            style={{ padding: '4px 10px', fontSize: '13px', background: '#e74c3c', border: 'none', marginLeft: '8px', color: 'white' }}
                                            onClick={() => handleDeleteDevice(device.serialNumber)}
                                        >
                                            🗑️ Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* LIVE SYNC PANEL */}
            <div className="dashboard-card">
                <div className="card-header">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={liveScans.length > 0 ? 'pulse-red' : 'pulse-green'}></span>
                        Live Sync Feed
                        <span style={{
                            fontSize: '12px', fontWeight: '600', padding: '3px 10px',
                            background: liveScans.length > 0 ? '#e74c3c' : '#27ae60',
                            color: 'white', borderRadius: '20px', letterSpacing: '0.5px'
                        }}>
                            {liveScans.length > 0 ? `${liveScans.length} SCANS` : 'WAITING FOR SCAN'}
                        </span>
                    </h2>
                    <button className="btn" style={{ background: '#eee', fontSize: '13px' }} onClick={() => setLiveScans([])}>
                        🗑 Clear
                    </button>
                </div>

                {liveScans.length === 0 ? (
                    <div style={{
                        padding: '60px 20px', textAlign: 'center',
                        background: 'linear-gradient(135deg, #f8f9ff, #f0f4ff)',
                        borderRadius: '8px', margin: '10px 0'
                    }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧑/👆</div>
                        <div style={{ fontSize: '18px', fontWeight: '600', color: '#555', marginBottom: '8px' }}>
                            Waiting for biometric scan...
                        </div>
                        <div style={{ fontSize: '14px', color: '#888' }}>
                            Ask someone to scan their face or fingerprint on the <strong>eSSL Device</strong>.<br />
                            Their scan will appear here instantly in real time.
                        </div>
                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}>
                            <span className="pulse-green"></span>
                            <span style={{ fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>
                                Socket connected → Live
                            </span>
                        </div>
                    </div>
                ) : (
                    <div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '12px',
                            padding: '10px 0'
                        }}>
                            {liveScans.map((scan, idx) => (
                                <div key={scan.id || idx} className="live-scan-row" style={{
                                    background: idx === 0
                                        ? 'linear-gradient(135deg, #667eea22, #764ba222)'
                                        : '#f8f9fa',
                                    border: idx === 0 ? '2px solid #667eea55' : '1px solid #eee',
                                    borderRadius: '12px', padding: '16px',
                                    position: 'relative', overflow: 'hidden'
                                }}>
                                    {idx === 0 && (
                                        <div style={{
                                            position: 'absolute', top: '10px', right: '10px',
                                            background: '#667eea', color: 'white',
                                            fontSize: '10px', fontWeight: '700', padding: '2px 8px',
                                            borderRadius: '20px', letterSpacing: '1px'
                                        }}>NEW</div>
                                    )}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{
                                            width: '44px', height: '44px', borderRadius: '50%',
                                            background: idx === 0
                                                ? 'linear-gradient(135deg, #667eea, #764ba2)'
                                                : 'linear-gradient(135deg, #bdc3c7, #95a5a6)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: 'white', fontWeight: '800', fontSize: '15px', flexShrink: 0,
                                            overflow: 'hidden'
                                        }}>
                                            {resolvedPhoto(scan) ? (
                                                <img src={resolvedPhoto(scan)} alt="Face" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                isFaceScan(scan) ? '👤' : '👆'
                                            )}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '700', fontSize: '15px', marginBottom: '2px' }}>
                                                {resolvedName(scan)}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                ID: {scan.userId} &nbsp;•&nbsp; 📟 {scan.deviceSn}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                                                🕐 {scan.receivedAt}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ===== MACHINE SETUP GUIDE ===== */}
            <div className="dashboard-card" style={{ marginTop: '20px', background: 'linear-gradient(135deg, #fff9f0, #fff)', border: '1px solid #fbbf24' }}>
                <div className="card-header">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>⚙️ Machine Setup Guide <span style={{ fontSize: '13px', fontWeight: '400', background: '#fef3c7', color: '#92400e', padding: '3px 10px', borderRadius: '20px', marginLeft: '8px' }}>One-time Setup</span></h2>
                </div>
                <div style={{ padding: '4px 0 10px' }}>
                    <p style={{ color: '#555', fontSize: '14px', marginBottom: '16px' }}>To receive <strong>real-time attendance data</strong> from the biometric machine on the cloud server, configure the machine's <strong>Push/Cloud Server</strong> settings as follows:</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: '10px', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', letterSpacing: '1px', marginBottom: '6px' }}>SERVER ADDRESS</div>
                            <code style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>erp.drbhagwan.in</code>
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: '10px', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', letterSpacing: '1px', marginBottom: '6px' }}>PORT</div>
                            <code style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>80</code>
                            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>HTTP (not 443/HTTPS — machine firmware doesn't support SSL)</div>
                        </div>
                        <div style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: '10px', padding: '14px' }}>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', letterSpacing: '1px', marginBottom: '6px' }}>HEARTBEAT / PUSH INTERVAL</div>
                            <code style={{ fontSize: '15px', color: '#1e293b', fontWeight: '700' }}>30 seconds</code>
                        </div>
                    </div>
                    <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#713f12' }}>
                        <strong>📖 How to configure:</strong> On the machine → Menu → Communication → Cloud Server / Push Server → Set server address & port → Save & Restart machine.
                    </div>
                    <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                        <button
                            className="btn btn-primary"
                            style={{ background: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                            onClick={handleForceSyncAdms}
                            disabled={forceSyncLoading}
                        >
                            {forceSyncLoading ? '⏳ Sending...' : '🔄 Force Sync All Connected Devices (ADMS)'}
                        </button>
                        {forceSyncResult && (
                            <div style={{
                                padding: '8px 14px', borderRadius: '6px', fontSize: '13px',
                                background: forceSyncResult.success ? '#ecfdf5' : '#fef2f2',
                                color: forceSyncResult.success ? '#065f46' : '#991b1b',
                                border: forceSyncResult.success ? '1px solid #a7f3d0' : '1px solid #fca5a5',
                                display: 'flex', alignItems: 'center'
                            }}>
                                {forceSyncResult.success ? '✅' : '❌'} {forceSyncResult.message}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ===== FULL IMPORT FROM MACHINE (LOCAL NETWORK) ===== */}
            <div className="dashboard-card" style={{ marginTop: '20px', background: 'linear-gradient(135deg, #f0fdf4, #fff)', border: '1px solid #bbf7d0' }}>
                <div className="card-header">
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>📥 Import All Data from Machine <span style={{ fontSize: '13px', fontWeight: '400', background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '20px', marginLeft: '8px' }}>Local Network Only</span></h2>
                </div>
                <div style={{ padding: '4px 0 10px' }}>
                    <p style={{ color: '#555', fontSize: '14px', marginBottom: '16px' }}>Use this when the backend is running on the <strong>same local network</strong> as the biometric machine. This will pull <strong>all users + all historical attendance logs</strong> directly from the machine via ZKLib (TCP port 4370).</p>
                    <form onSubmit={handleFullImport} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '5px' }}>Machine IP Address</label>
                            <input
                                type="text"
                                value={fullImportIp}
                                onChange={e => setFullImportIp(e.target.value)}
                                placeholder="e.g. 192.168.0.150"
                                required
                                style={{ padding: '10px 14px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', width: '200px' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '5px' }}>Port</label>
                            <input
                                type="text"
                                value={fullImportPort}
                                onChange={e => setFullImportPort(e.target.value)}
                                placeholder="4370"
                                style={{ padding: '10px 14px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', width: '100px' }}
                            />
                        </div>
                        <button
                            type="submit"
                            className="btn btn-success"
                            style={{ background: '#16a34a', borderColor: '#16a34a', color: 'white', padding: '10px 20px' }}
                            disabled={fullImportLoading}
                        >
                            {fullImportLoading ? '⏳ Importing... (may take 30–60s)' : '📥 Import All Users + Attendance'}
                        </button>
                    </form>

                    {fullImportResult && (
                        <div style={{
                            marginTop: '16px', padding: '14px', borderRadius: '8px',
                            background: fullImportResult.success ? '#ecfdf5' : '#fef2f2',
                            border: fullImportResult.success ? '1px solid #a7f3d0' : '1px solid #fca5a5',
                            color: fullImportResult.success ? '#065f46' : '#991b1b'
                        }}>
                            <strong>{fullImportResult.success ? '✅ Import Complete!' : '❌ Import Failed'}</strong>
                            <p style={{ margin: '8px 0 0', fontSize: '13px' }}>{fullImportResult.message}</p>
                            {fullImportResult.success && (
                                <div style={{ marginTop: '8px', fontSize: '12px', display: 'flex', gap: '16px' }}>
                                    <span>👥 Users imported: <strong>{fullImportResult.users}</strong></span>
                                    <span>📋 Attendance records: <strong>{fullImportResult.attendance}</strong></span>
                                    {fullImportResult.errors?.length > 0 && <span>⚠️ Errors: {fullImportResult.errors.length}</span>}
                                </div>
                            )}
                        </div>
                    )}

                    <div style={{ marginTop: '14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#166534' }}>
                        <strong>💡 Tip:</strong> Run the backend server locally (on the same WiFi as the machine) and use this button to do a one-time bulk import. After that, configure the machine to push to the cloud server (see guide above) for real-time sync.
                    </div>
                </div>
            </div>

            {/* RENAME MODAL */}
            {editingDevice && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '350px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>✏️ Rename Device</h3>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                            Set a friendly name for: <code>{editingDevice.serialNumber}</code>
                        </p>
                        <form onSubmit={handleRename}>
                            <input
                                type="text" value={newName} onChange={e => setNewName(e.target.value)}
                                placeholder="e.g. Main Gate, Library..."
                                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #ddd', fontSize: '14px', marginBottom: '20px', boxSizing: 'border-box' }}
                                required autoFocus
                            />
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button type="button" className="btn" style={{ background: '#eee', color: '#333' }} onClick={() => setEditingDevice(null)}>Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* CONNECT IP MODAL */}
            {showConnectIpModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '400px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', color: '#333' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '18px', fontWeight: 'bold' }}>🔌 Connect Biometric Device via IP</h3>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                            Enter the IP address of the biometric machine on your local network to connect and sync data.
                        </p>
                        <form onSubmit={handleConnectIp}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '5px' }}>Machine IP Address</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={deviceIp}
                                    onChange={(e) => setDeviceIp(e.target.value)}
                                    placeholder="e.g. 192.168.0.117"
                                    required
                                    style={{ width: '100%', padding: '10px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '6px' }}
                                />
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#555', marginBottom: '5px' }}>Port (Default 4370)</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={devicePort}
                                    onChange={(e) => setDevicePort(e.target.value)}
                                    placeholder="4370"
                                    style={{ width: '100%', padding: '10px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '6px' }}
                                />
                            </div>

                            {connectLoading && (
                                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '6px', fontSize: '13px', color: '#8b5cf6', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600' }}>
                                    <span>⏳</span> Connecting and fetching device users & punches...
                                </div>
                            )}

                            {connectResult && (
                                <div style={{ 
                                    padding: '12px', 
                                    borderRadius: '6px', 
                                    fontSize: '13px', 
                                    marginBottom: '15px',
                                    background: connectResult.success ? '#ecfdf5' : '#fef2f2',
                                    color: connectResult.success ? '#065f46' : '#991b1b',
                                    border: connectResult.success ? '1px solid #a7f3d0' : '1px solid #fca5a5'
                                }}>
                                    <strong style={{ display: 'block', marginBottom: '4px' }}>
                                        {connectResult.success ? '✅ Success' : '❌ Error'}
                                    </strong>
                                    {connectResult.message}
                                    {connectResult.success && (
                                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#047857' }}>
                                            • Users Synced: {connectResult.usersImported}<br/>
                                            • Punches Synced: {connectResult.punchesImported}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <button type="button" className="btn" style={{ background: '#eee', color: '#333', padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} onClick={() => setShowConnectIpModal(false)} disabled={connectLoading}>Close</button>
                                <button type="submit" className="btn btn-primary" style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} disabled={connectLoading}>
                                    {connectLoading ? 'Connecting...' : 'Connect & Sync'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};


const BirthdaysPage = () => {
    const [users, setUsers] = useState([]);

    useEffect(() => {
        Promise.all([
            api.get('/users').catch(() => ({ data: [] })),
            api.get('/teachers').catch(() => ({ data: [] }))
        ]).then(([studRes, teachRes]) => {
            setUsers([...(studRes.data || []), ...(teachRes.data || [])]);
        }).catch(err => console.error(err));
    }, []);

    const today = new Date();

    const upcomingBirthdays = users.filter(u => {
        if (!u.dob) return false;
        const dob = new Date(u.dob);
        if (isNaN(dob)) return false;

        // Check if birthday is coming up in next 30 days
        const thisYearBday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate());
        const nextYearBday = new Date(today.getFullYear() + 1, dob.getMonth(), dob.getDate());

        // Use whichever is closest in the future or today
        let bday = thisYearBday;
        if (thisYearBday < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
            bday = nextYearBday;
        }

        const diffTime = bday - new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        u.daysUntil = diffDays;
        u.ageTurning = bday.getFullYear() - dob.getFullYear();
        return diffDays >= 0 && diffDays <= 30;
    }).sort((a, b) => a.daysUntil - b.daysUntil);

    return (
        <div className="dashboard-card">
            <div className="card-header">
                <h2>🎂 Upcoming Birthdays (Next 30 Days)</h2>
            </div>
            <table className="dashboard-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Role</th>
                        <th>Date of Birth</th>
                        <th>Turning Age</th>
                        <th>Countdown</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {upcomingBirthdays.map((u, idx) => (
                        <tr key={u.id || idx}>
                            <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div className="avatar" style={{ background: u.daysUntil === 0 ? '#e74c3c' : '#f39c12' }}>{u.name.charAt(0)}</div>
                                    {u.name}
                                </div>
                            </td>
                            <td style={{ textTransform: 'capitalize' }}>{u.role}</td>
                            <td>{new Date(u.dob).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                            <td>{u.ageTurning} years</td>
                            <td>
                                {u.daysUntil === 0 ? <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>🎉 TODAY!</span> :
                                    u.daysUntil === 1 ? <span style={{ color: '#f39c12', fontWeight: 'bold' }}>Tomorrow</span> :
                                        `${u.daysUntil} days`}
                            </td>
                            <td>
                                <button className="btn btn-primary" style={{ padding: '4px 10px', background: '#e74c3c', borderColor: '#e74c3c' }}>✉️ Send Wish</button>
                            </td>
                        </tr>
                    ))}
                    {upcomingBirthdays.length === 0 && (
                        <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>No upcoming birthdays found in the next 30 days.</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// --- MAIN APP ---

const TeacherProfilePage = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('subjects');
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [weeklySchedule, setWeeklySchedule] = useState([
        { day: 'Monday', slots: '07:30 AM - 09:00 AM (11th) | 09:00 AM - 10:30 AM (12th)', hrs: '3.0 hrs' },
        { day: 'Tuesday', slots: '07:30 AM - 09:00 AM (11th) | 09:00 AM - 10:30 AM (12th)', hrs: '3.0 hrs' },
        { day: 'Wednesday', slots: '07:30 AM - 09:00 AM (11th) | 09:00 AM - 10:30 AM (12th)', hrs: '3.0 hrs' },
        { day: 'Thursday', slots: '07:30 AM - 09:00 AM (11th) | 11:30 AM - 01:00 PM (12th)', hrs: '3.0 hrs' },
        { day: 'Friday', slots: '07:30 AM - 09:00 AM (11th) | 11:30 AM - 01:00 PM (12th)', hrs: '3.0 hrs' },
        { day: 'Saturday', slots: '08:00 AM - 11:30 AM (Practice & Test Batch)', hrs: '3.5 hrs' },
        { day: 'Sunday', slots: 'Off / Holiday', hrs: '0.0 hrs' }
    ]);

    const handleExport = (format) => {
        if (!data || !data.records || data.records.length === 0) return;

        const tName = (teacher && teacher.name) ? teacher.name : 'User';
        const baseSalary = Number(teacher?.salary) || 0;
        const daysInM = new Date(filterYear, filterMonth, 0).getDate() || 30;
        const dailyRate = baseSalary > 0 ? baseSalary / daysInM : 0;
        const expectedHours = getExpectedHours(teacher?.timing);
        const records = data.records;

        const absentDays = records.filter(r => r.status === 'Absent').length;
        const sundayDays = records.filter(r => r.status === 'Sunday' || r.status === 'Holiday').length;
        const halfDays = records.filter(r => r.status === 'Half Day').length;
        const presentDays = records.filter(r => r.status === 'Present' || r.status === 'Late').length;

        let totalPay = 0;
        let computedPaidDays = 0;
        records.forEach(r => {
            if (r.status === 'Sunday' || r.status === 'Holiday') {
                computedPaidDays += 1;
                totalPay += dailyRate;
            } else if (r.status === 'Present' || r.status === 'Late') {
                const actualHours = (r.durationMinutes || 0) / 60;
                const ratio = expectedHours > 0 ? Math.min(1, actualHours / expectedHours) : 1;
                computedPaidDays += ratio;
                totalPay += dailyRate * ratio;
            } else if (r.status === 'Half Day') {
                computedPaidDays += 0.5;
                totalPay += dailyRate * 0.5;
            }
        });

        const absentDeduction = Math.round(absentDays * dailyRate);
        const halfDayDeduction = Math.round(halfDays * (dailyRate * 0.5));
        const netCalculatedPay = Math.round(totalPay > 0 ? totalPay : Math.max(0, baseSalary - absentDeduction - halfDayDeduction));

        const exportData = data.records.map(r => ({
            "Date": r.date,
            "In / Out": r.firstIn !== '--:--' ? `${r.firstIn} / ${r.lastOut}` : '--:-- / --:--',
            "Punches": r.totalEntries || 0,
            "Total Hours": r.durationMinutes ? `${Math.floor(r.durationMinutes / 60)}h ${r.durationMinutes % 60}m` : '0h 0m',
            "Status": r.status
        }));

        if (format === 'excel') {
            const workbook = XLSX.utils.book_new();

            // Sheet 1: Punches with Summary appended at bottom
            const summaryRows = [
                {},
                { "Date": "====================", "In / Out": "====================", "Punches": "====================", "Total Hours": "====================", "Status": "====================" },
                { "Date": "SALARY & PAYROLL SUMMARY", "In / Out": "", "Punches": "", "Total Hours": "", "Status": "" },
                { "Date": "Staff Member Name:", "In / Out": tName },
                { "Date": "Biometric ID:", "In / Out": String(teacher?.fingerprint_id || teacher?.id || '') },
                { "Date": "Base Monthly Salary (₹):", "In / Out": baseSalary.toFixed(2) },
                { "Date": "Daily Rate (₹):", "In / Out": dailyRate.toFixed(2) },
                { "Date": "Days Present:", "In / Out": presentDays },
                { "Date": "Half Days Worked:", "In / Out": halfDays },
                { "Date": "Absent Days / Unpaid:", "In / Out": absentDays },
                { "Date": "Holidays / Sundays (Paid):", "In / Out": sundayDays },
                { "Date": "Absent Deduction (₹):", "In / Out": absentDeduction },
                { "Date": "Half Day Deduction (₹):", "In / Out": halfDayDeduction },
                { "Date": "NET PAYABLE SALARY (₹):", "In / Out": netCalculatedPay }
            ];

            const fullData = [...exportData, ...summaryRows];
            const worksheet = XLSX.utils.json_to_sheet(fullData);
            XLSX.utils.book_append_sheet(workbook, worksheet, "Punches & Attendance");

            // Sheet 2: Dedicated Salary Calculation Breakdown
            const salaryData = [
                { "Field": "Staff Member Name", "Value": tName },
                { "Field": "Role", "Value": teacher?.role || 'Staff' },
                { "Field": "Biometric ID", "Value": String(teacher?.fingerprint_id || teacher?.id || '') },
                { "Field": "Month / Year", "Value": `${filterMonth}/${filterYear}` },
                { "Field": "Base Monthly Salary (₹)", "Value": baseSalary },
                { "Field": "Per Day Rate (₹)", "Value": Math.round(dailyRate) },
                { "Field": "Days Present (Full Pay)", "Value": presentDays },
                { "Field": "Half Days (Half Pay)", "Value": halfDays },
                { "Field": "Absent Days (Unpaid)", "Value": absentDays },
                { "Field": "Holidays / Sundays (Paid)", "Value": sundayDays },
                { "Field": "Absent Deduction (₹)", "Value": absentDeduction },
                { "Field": "Half Day Deduction (₹)", "Value": halfDayDeduction },
                { "Field": "Total Salary Deductions (₹)", "Value": absentDeduction + halfDayDeduction },
                { "Field": "NET AUTOMATIC CALCULATED SALARY PAYABLE (₹)", "Value": netCalculatedPay }
            ];
            const salaryWorksheet = XLSX.utils.json_to_sheet(salaryData);
            XLSX.utils.book_append_sheet(workbook, salaryWorksheet, "Salary Breakdown");

            XLSX.writeFile(workbook, `${tName}_Salary_Attendance_${filterMonth}_${filterYear}.xlsx`);
        } else if (format === 'csv') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
            const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `${tName}_Punches_${filterMonth}_${filterYear}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'pdf') {
            const doc = new jsPDF();
            doc.text(`${tName} - Punches & Salary (${filterMonth}/${filterYear})`, 14, 15);
            doc.autoTable({
                head: [['Date', 'In / Out', 'Punches', 'Total Hours', 'Status']],
                body: exportData.map(r => [r['Date'], r['In / Out'], r['Punches'], r['Total Hours'], r['Status']]),
                startY: 20
            });
            const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 150;
            doc.setFontSize(12);
            doc.text(`Base Salary: Rs ${baseSalary} | Net Payable Salary: Rs ${netCalculatedPay}`, 14, finalY);
            doc.save(`${tName}_Punches_${filterMonth}_${filterYear}.pdf`);
        }
        setExportMenuOpen(false);
    };

    const getExpectedHours = (timingStr) => {
        if (!timingStr) return 1.5;
        const matchRange = timingStr.match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)\s*-\s*(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i);
        if (matchRange) {
            let startH = parseInt(matchRange[1], 10);
            const startM = parseInt(matchRange[2] || '0', 10);
            const startAmpm = matchRange[3].toUpperCase();
            let endH = parseInt(matchRange[4], 10);
            const endM = parseInt(matchRange[5] || '0', 10);
            const endAmpm = matchRange[6].toUpperCase();

            if (startAmpm === 'PM' && startH !== 12) startH += 12;
            if (startAmpm === 'AM' && startH === 12) startH = 0;
            if (endAmpm === 'PM' && endH !== 12) endH += 12;
            if (endAmpm === 'AM' && endH === 12) endH = 0;

            let startTotalMins = startH * 60 + startM;
            let endTotalMins = endH * 60 + endM;

            if (endTotalMins < startTotalMins) {
                endTotalMins += 24 * 60;
            }
            return (endTotalMins - startTotalMins) / 60;
        }
        return 1.5;
    };

    const handleSalaryExport = () => {
        if (!data || !data.records) return;
        const tName = (teacher && teacher.name) ? teacher.name : 'User';
        const baseSalary = Number(teacher?.salary) || 0;
        const dailyRate = baseSalary / 30;
        const expectedHours = getExpectedHours(teacher?.timing);
        const records = data.records;
        const totalDays = records.length;
        const absentDays = records.filter(r => r.status === 'Absent').length;
        const sundayDays = records.filter(r => r.status === 'Sunday').length;
        const presentDays = records.filter(r => r.status === 'Present' || r.status === 'Late').length;

        let totalPay = 0;
        let computedPaidDays = 0;
        records.forEach(r => {
            if (r.status === 'Sunday') {
                computedPaidDays += 1;
                totalPay += dailyRate;
            } else if (r.status === 'Present' || r.status === 'Late') {
                const actualHours = (r.durationMinutes || 0) / 60;
                const ratio = expectedHours > 0 ? Math.min(1, actualHours / expectedHours) : 1;
                computedPaidDays += ratio;
                totalPay += dailyRate * ratio;
            }
        });

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text(`Salary Slip - ${tName}`, 14, 20);
        doc.setFontSize(12);
        doc.text(`Month/Year: ${filterMonth}/${filterYear}`, 14, 30);
        doc.text(`Base Salary (Monthly): Rs ${baseSalary.toFixed(2)}`, 14, 40);
        doc.text(`Daily Rate (30 Days): Rs ${dailyRate.toFixed(2)}`, 14, 50);
        doc.text(`Shift Timing: ${teacher?.timing || 'N/A'} (Expected Hours: ${expectedHours.toFixed(1)}h)`, 14, 60);

        doc.text(`Total Days: ${totalDays}`, 14, 75);
        doc.text(`Present Days: ${presentDays}`, 14, 85);
        doc.text(`Holidays/Sundays: ${sundayDays}`, 14, 95);
        doc.text(`Absent Days: ${absentDays}`, 14, 105);
        doc.text(`Equivalent Paid Days: ${computedPaidDays.toFixed(2)}`, 14, 115);

        doc.setFontSize(14);
        doc.setTextColor('#16a34a');
        doc.text(`Net Payable Amount: Rs ${totalPay.toFixed(2)}`, 14, 130);

        doc.save(`${tName}_Salary_Slip_${filterMonth}_${filterYear}.pdf`);
    };

    const fetchTeacherData = (month = filterMonth, year = filterYear) => {
        api.get(`/attendance/student/${id}?month=${month}&year=${year}`) // This endpoint works for any user
            .then(res => {
                setData(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    const teacher = data?.student;
    const isStaff = teacher && (teacher.role === 'staff' || (teacher.role && teacher.role.toLowerCase() !== 'teacher'));

    useEffect(() => {
        fetchTeacherData();

        const handleLivePunch = (punch) => {
            if (!punch) return;
            const cleanId = String(id).trim();
            const teacherFp = data?.student?.fingerprint_id || teacher?.fingerprint_id;
            const teacherId = data?.student?.id || teacher?.id;
            const teacherDbId = data?.student?._id || teacher?._id;

            const matches = [
                String(punch.userId || '').trim(),
                String(punch.fingerprint_id || '').trim(),
                String(punch.userDbId || '').trim()
            ];

            const targets = [
                cleanId,
                teacherFp ? String(teacherFp).trim() : null,
                teacherId ? String(teacherId).trim() : null,
                teacherDbId ? String(teacherDbId).trim() : null
            ].filter(Boolean);

            if (matches.some(m => targets.includes(m))) {
                fetchTeacherData();
            }
        };

        socket.on('live_punch', handleLivePunch);
        return () => socket.off('live_punch', handleLivePunch);
    }, [id, data, teacher]);

    useEffect(() => {
        if (isStaff && (activeTab === 'subjects' || activeTab === 'schedule')) {
            setActiveTab('profile');
        }
    }, [isStaff, activeTab]);

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontSize: '18px', color: '#666' }}>Loading Profile...</div>;
    if (!data || !data.student) return <div style={{ padding: '40px', textAlign: 'center', fontSize: '18px', color: '#e74c3c' }}>Profile Not Found.</div>;

    return (
        <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: '"Inter", sans-serif' }}>
            <div style={{ display: 'flex', gap: '25px', alignItems: 'flex-start' }}>
                {/* Left Panel */}
                <div style={{ flex: '0 0 320px', background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#a855f7', padding: '6px 14px', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '25px' }}>
                        👤 {isStaff ? 'Staff Profile' : 'Teacher Profile'}
                    </div>

                    <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', alignItems: 'center' }}>
                        {teacher.photo ? (
                            <img src={getPhotoUrl(teacher.photo)} alt="Profile" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e2e8f0' }} />
                        ) : (
                            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#dcfce7', color: '#22c55e', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '32px', fontWeight: 'bold' }}>
                                {teacher.name.substring(0, 2).toUpperCase()}
                            </div>
                        )}
                        <div>
                            <div style={{ fontSize: '14px', color: '#64748b' }}>Full Name:</div>
                            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>{teacher.name}</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', color: '#475569', fontSize: '14px' }}>
                        <div><strong>Email:</strong> {teacher.email || 'N/A'}</div>
                        <div><strong>Telephone:</strong> {teacher.studentContact || teacher.phone || 'N/A'}</div>
                        <div><strong>Biometric Id:</strong> {teacher.fingerprint_id}</div>
                        <div><strong>Section:</strong> {teacher.section || 'N/A'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <strong>Status:</strong>
                            <span style={{ background: '#dcfce7', color: '#16a34a', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>Active</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginTop: '30px' }}>
                        <button style={{ flex: 1, background: '#8b5cf6', color: 'white', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>☁️ Upload</button>
                        <button style={{ flex: 1, background: '#fca5a5', color: 'white', border: 'none', padding: '10px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>🗑️ Delete</button>
                    </div>

                    <div style={{ marginTop: '35px' }}>
                        <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '6px 16px', borderRadius: '20px', fontWeight: '600', marginBottom: '15px', fontSize: '14px' }}>
                            Quick Links
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <button onClick={() => setActiveTab('device')} style={{ background: 'white', color: '#8b5cf6', border: '1px solid #8b5cf6', padding: '10px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>👆</span> Punches
                            </button>
                            <button style={{ background: 'white', color: '#64748b', border: '1px solid #cbd5e1', padding: '10px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>☑️</span> Lectures
                            </button>
                            <button onClick={() => setActiveTab('salary')} style={{ background: activeTab === 'salary' ? '#fef2f2' : 'white', color: '#ef4444', border: '1px solid #ef4444', padding: '10px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px', border: '1px solid #ef4444', borderRadius: '50%', width: '18px', height: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>₹</span> Salary
                            </button>
                            <button onClick={() => setActiveTab('performance')} style={{ background: activeTab === 'performance' ? '#f0fdf4' : 'white', color: '#22c55e', border: '1px solid #22c55e', padding: '10px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '14px' }}>
                                <span style={{ fontSize: '16px' }}>📈</span> Performance
                            </button>
                        </div>
                    </div>
                </div>

                {/* Right Panel */}
                <div style={{ flex: 1, background: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0' }}>
                        {!isStaff && (
                            <button onClick={() => setActiveTab('subjects')} style={{ flex: 1, padding: '15px', background: 'none', border: 'none', borderBottom: activeTab === 'subjects' ? '3px solid #8b5cf6' : '3px solid transparent', color: activeTab === 'subjects' ? '#8b5cf6' : '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                🖥️ Subjects
                            </button>
                        )}
                        {!isStaff && (
                            <button onClick={() => setActiveTab('schedule')} style={{ flex: 1, padding: '15px', background: 'none', border: 'none', borderBottom: activeTab === 'schedule' ? '3px solid #8b5cf6' : '3px solid transparent', color: activeTab === 'schedule' ? '#8b5cf6' : '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                🗓️ Weekly Timetable
                            </button>
                        )}
                        <button onClick={() => setActiveTab('profile')} style={{ flex: 1, padding: '15px', background: 'none', border: 'none', borderBottom: activeTab === 'profile' ? '3px solid #8b5cf6' : '3px solid transparent', color: activeTab === 'profile' ? '#8b5cf6' : '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            📝 Profile
                        </button>
                        <button onClick={() => setActiveTab('device')} style={{ flex: 1, padding: '15px', background: 'none', border: 'none', borderBottom: activeTab === 'device' ? '3px solid #8b5cf6' : '3px solid transparent', color: activeTab === 'device' ? '#8b5cf6' : '#64748b', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            👆 Punches
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div style={{ padding: '25px' }}>
                        {activeTab === 'schedule' && (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                                    <div>
                                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '17px' }}>🗓️ Teacher Weekly Routine & Timetable Box</h3>
                                        <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>Day-by-day lecture timing schedule synced from your timetable.</p>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        <button
                                            style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', padding: '8px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                            onClick={() => {
                                                setWeeklySchedule([
                                                    { day: 'Monday', slots: 'Blank Slot (Click Edit to add lectures)', hrs: '0.0 hrs' },
                                                    { day: 'Tuesday', slots: 'Blank Slot (Click Edit to add lectures)', hrs: '0.0 hrs' },
                                                    { day: 'Wednesday', slots: 'Blank Slot (Click Edit to add lectures)', hrs: '0.0 hrs' },
                                                    { day: 'Thursday', slots: 'Blank Slot (Click Edit to add lectures)', hrs: '0.0 hrs' },
                                                    { day: 'Friday', slots: 'Blank Slot (Click Edit to add lectures)', hrs: '0.0 hrs' },
                                                    { day: 'Saturday', slots: 'Blank Slot (Click Edit to add lectures)', hrs: '0.0 hrs' },
                                                    { day: 'Sunday', slots: 'Off / Holiday', hrs: '0.0 hrs' }
                                                ]);
                                                alert("📋 Timetable opened as blank template! You can practice adding slots for each day.");
                                            }}
                                        >
                                            📑 Open Blank Timetable Template
                                        </button>
                                        <button
                                            style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                            onClick={() => {
                                                const dayInput = prompt("Enter Day (e.g. Monday, Tuesday, Wednesday...):");
                                                if (!dayInput) return;
                                                const slotInput = prompt("Enter Lecture Slots & Timing (e.g. 07:30 AM - 09:00 AM Class 11th):");
                                                if (!slotInput) return;
                                                setWeeklySchedule(prev => prev.map(s => s.day.toLowerCase() === dayInput.trim().toLowerCase() ? { ...s, slots: slotInput, hrs: '1.5 - 3.0 hrs' } : s));
                                            }}
                                        >
                                            ➕ Edit Day Slot
                                        </button>
                                    </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                                    {weeklySchedule.map((item, idx) => (
                                        <div key={idx} style={{ background: item.day === 'Sunday' ? '#fef2f2' : '#f8fafc', border: item.day === 'Sunday' ? '1px solid #fecaca' : '1px solid #e2e8f0', borderRadius: '10px', padding: '15px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                <span style={{ fontWeight: '700', fontSize: '14px', color: item.day === 'Sunday' ? '#ef4444' : '#2563eb' }}>📅 {item.day}</span>
                                                <span style={{ fontSize: '11px', background: item.day === 'Sunday' ? '#fee2e2' : '#dbeafe', color: item.day === 'Sunday' ? '#dc2626' : '#1d4ed8', padding: '3px 9px', borderRadius: '12px', fontWeight: '600' }}>
                                                    ⏱ {item.hrs}
                                                </span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#334155', background: '#ffffff', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontWeight: '500', lineHeight: '1.4' }}>
                                                {item.slots}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {activeTab === 'subjects' && (
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <button style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>+ Subject</button>
                                    <select style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', color: '#64748b', outline: 'none' }}>
                                        <option value="">Default Year</option>
                                        <option value="2026">2026</option>
                                        <option value="2027">2027</option>
                                        <option value="2028">2028</option>
                                        <option value="2029">2029</option>
                                        <option value="2030">2030</option>
                                    </select>
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', color: '#475569' }}>
                                            <th style={{ padding: '12px' }}>YEAR ↑</th>
                                            <th style={{ padding: '12px' }}>SUBJECT/ BATCH ↑</th>
                                            <th style={{ padding: '12px' }}>HOURS ↑</th>
                                            <th style={{ padding: '12px' }}>COVERAGE ↑</th>
                                            <th style={{ padding: '12px' }}>IMPACT ↑</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {teacher.role === 'teacher' && teacher.subject ? (
                                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '12px' }}>{new Date().getFullYear()}</td>
                                                <td style={{ padding: '12px', color: '#1e293b' }}><strong>{teacher.subject}</strong> <span style={{ color: '#64748b' }}>- {teacher.batch || 'N/A'}</span></td>
                                                <td style={{ padding: '12px', color: '#475569' }}>{teacher.timing ? teacher.timing.split('(')[0] : 'N/A'}</td>
                                                <td style={{ padding: '12px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ width: '80px', background: '#e2e8f0', borderRadius: '4px', height: '6px' }}>
                                                            <div style={{ width: '92%', background: '#22c55e', borderRadius: '4px', height: '100%' }}></div>
                                                        </div>
                                                        <span style={{ fontSize: '12px', fontWeight: '600', color: '#22c55e' }}>92%</span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '12px' }}>
                                                    <span style={{ background: '#ecfdf5', color: '#059669', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>EXCELLENT</span>
                                                </td>
                                            </tr>
                                        ) : (
                                            <tr>
                                                <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                                                    No performance data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {activeTab === 'profile' && (
                            <div style={{ color: '#475569', lineHeight: '1.6' }}>
                                <h3 style={{ marginTop: 0, color: '#1e293b' }}>Personal Information</h3>
                                <p><strong>Role:</strong> <span style={{ textTransform: 'capitalize' }}>{teacher.role}</span></p>
                                <p><strong>Experience:</strong> {teacher.experience || 'N/A'}</p>
                                <p><strong>Timing:</strong> {teacher.timing || 'N/A'}</p>
                                <p><strong>Salary:</strong> ₹ {teacher.salary || 0}</p>
                                <p><strong>Aadhar:</strong> {teacher.aadhar || 'N/A'}</p>
                                <p><strong>Gender:</strong> {teacher.gender || 'N/A'}</p>
                            </div>
                        )}
                        {activeTab === 'device' && (
                            <div style={{ display: 'flex', gap: '25px' }}>
                                {/* Left Filter Panel */}
                                <div style={{ flex: '0 0 220px', background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                                    <div style={{ display: 'inline-block', background: '#f3e8ff', color: '#8b5cf6', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', marginBottom: '25px' }}>
                                        Filter
                                    </div>
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Month</label>
                                        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', backgroundColor: '#f8fafc' }}>
                                            <option value={1}>January</option>
                                            <option value={2}>February</option>
                                            <option value={3}>March</option>
                                            <option value={4}>April</option>
                                            <option value={5}>May</option>
                                            <option value={6}>June</option>
                                            <option value={7}>July</option>
                                            <option value={8}>August</option>
                                            <option value={9}>September</option>
                                            <option value={10}>October</option>
                                            <option value={11}>November</option>
                                            <option value={12}>December</option>
                                        </select>
                                    </div>
                                    <div style={{ marginBottom: '30px' }}>
                                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px', fontWeight: '500' }}>Year</label>
                                        <select value={filterYear} onChange={e => setFilterYear(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', color: '#475569', backgroundColor: '#f8fafc' }}>
                                            <option value={2026}> 2026 </option>
                                            <option value={2027}> 2027 </option>
                                            <option value={2028}> 2028 </option>
                                            <option value={2029}> 2029 </option>
                                            <option value={2030}> 2030 </option>
                                        </select>
                                    </div>
                                    <button onClick={() => fetchTeacherData(filterMonth, filterYear)} style={{ width: '100%', background: '#8b5cf6', color: 'white', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', transition: 'background 0.2s', ':hover': { background: '#7c3aed' } }}>
                                        Get Punches
                                    </button>
                                </div>

                                {/* Right Table Panel */}
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <h3 style={{ margin: 0, color: '#1e293b', fontSize: '18px' }}>{teacher.name}</h3>
                                        <div style={{ position: 'relative' }}>
                                            <button onClick={() => setExportMenuOpen(!exportMenuOpen)} style={{ background: '#f3e8ff', color: '#8b5cf6', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                                <span>📤</span> Export <span style={{ fontSize: '10px' }}>▼</span>
                                            </button>
                                            {exportMenuOpen && (
                                                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '150px', overflow: 'hidden' }}>
                                                    <button onClick={() => handleExport('excel')} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f1f5f9' }}>
                                                        <span style={{ color: '#10b981' }}>📊</span> Excel (.xlsx)
                                                    </button>
                                                    <button onClick={() => handleExport('csv')} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f1f5f9' }}>
                                                        <span style={{ color: '#f59e0b' }}>📝</span> CSV (.csv)
                                                    </button>
                                                    <button onClick={() => handleExport('pdf')} style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '13px', color: '#475569', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <span style={{ color: '#ef4444' }}>📄</span> PDF (.pdf)
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <input type="text" placeholder="Search..." style={{ width: '100%', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '20px', outline: 'none', color: '#475569', fontSize: '14px', backgroundColor: '#f8fafc' }} />

                                    <div style={{ maxHeight: '450px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white' }}>
                                        {data.records && data.records.length > 0 ? (
                                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                                    <tr style={{ background: '#f8fafc', color: '#64748b', fontSize: '12px', letterSpacing: '0.5px' }}>
                                                        <th style={{ padding: '15px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>DATE</th>
                                                        <th style={{ padding: '15px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>IN / OUT</th>
                                                        <th style={{ padding: '15px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>PUNCHES</th>
                                                        <th style={{ padding: '15px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>TOTAL HOURS</th>
                                                        <th style={{ padding: '15px 12px', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>STATUS</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {data.records.map((r, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', ':hover': { backgroundColor: '#f8fafc' } }}>
                                                            <td style={{ padding: '15px 12px', fontWeight: '500', color: '#1e293b' }}>{r.date}</td>
                                                            <td style={{ padding: '15px 12px', color: '#64748b' }}>
                                                                {r.firstIn !== '--:--' ? (
                                                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                                                                        <span style={{ color: '#10b981', fontWeight: '600' }}>{r.firstIn}</span> /
                                                                        <span style={{ color: '#f59e0b', fontWeight: '600' }}>{r.lastOut}</span>
                                                                    </div>
                                                                ) : (
                                                                    <span style={{ color: '#94a3b8' }}>--:-- / --:--</span>
                                                                )}
                                                            </td>
                                                            <td style={{ padding: '15px 12px', color: '#64748b' }}>{r.totalEntries || 0}</td>
                                                            <td style={{ padding: '15px 12px', color: '#1e293b', fontWeight: '500' }}>
                                                                {r.durationMinutes ? `${Math.floor(r.durationMinutes / 60)}h ${r.durationMinutes % 60}m` : '0h 0m'}
                                                            </td>
                                                            <td style={{ padding: '15px 12px' }}>
                                                                {r.status === 'Late' ? (
                                                                    <span style={{ background: '#fef5e7', color: '#e67e22', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>Late</span>
                                                                ) : r.status === 'Absent' ? (
                                                                    <span style={{ background: '#fef2f2', color: '#ef4444', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>Absent</span>
                                                                ) : r.status === 'Sunday' ? (
                                                                    <span style={{ background: '#f8fafc', color: '#64748b', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600' }}>Sunday</span>
                                                                ) : (
                                                                    <span style={{ color: '#10b981', fontWeight: '600' }}>Present</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No data available in table</div>
                                        )}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '20px 0 10px 0', color: '#1e293b', fontSize: '14px', fontWeight: '600' }}>
                                        <div>TOTAL</div>
                                        <div>{data.summary ? data.summary.totalHours : '0.00'} HRS</div>
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>
                                        Showing {data.records ? data.records.length : 0} out of {data.records ? data.records.length : 0} entries
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'salary' && (
                            <div style={{ background: 'white', borderRadius: '12px', padding: '25px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px' }}>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Salary Slip</h3>
                                        <p style={{ margin: '5px 0 0 0', color: '#64748b', fontSize: '13px' }}>{new Date(filterYear, filterMonth - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                                    </div>
                                    <button onClick={handleSalaryExport} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                                        <span>📄</span> Export to PDF
                                    </button>
                                </div>

                                {(() => {
                                    const baseSalary = Number(teacher?.salary) || 0;
                                    const dailyRate = baseSalary / 30;
                                    const expectedHours = getExpectedHours(teacher?.timing);

                                    const records = data?.records || [];

                                    const totalDays = records.length;
                                    const absentDays = records.filter(r => r.status === 'Absent').length;
                                    const sundayDays = records.filter(r => r.status === 'Sunday').length;
                                    const presentDays = records.filter(r => r.status === 'Present' || r.status === 'Late').length;

                                    let totalPay = 0;
                                    let paidDays = 0;
                                    records.forEach(r => {
                                        if (r.status === 'Sunday') {
                                            paidDays += 1;
                                            totalPay += dailyRate;
                                        } else if (r.status === 'Present' || r.status === 'Late') {
                                            const actualHours = (r.durationMinutes || 0) / 60;
                                            const ratio = expectedHours > 0 ? Math.min(1, actualHours / expectedHours) : 1;
                                            paidDays += ratio;
                                            totalPay += dailyRate * ratio;
                                        }
                                    });

                                    return (
                                        <div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px' }}>
                                                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '10px' }}>
                                                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Base Salary (Monthly)</div>
                                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>₹ {baseSalary.toFixed(2)}</div>
                                                </div>
                                                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '10px' }}>
                                                    <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '5px' }}>Daily Rate (30 Days)</div>
                                                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b' }}>₹ {dailyRate.toFixed(2)}</div>
                                                </div>
                                            </div>

                                            <h4 style={{ margin: '0 0 15px 0', color: '#334155' }}>Attendance & Shift Summary</h4>
                                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '10px', marginBottom: '20px', fontSize: '14px', color: '#475569' }}>
                                                <strong>Shift Timing:</strong> {teacher?.timing || 'N/A'} (Expected: {expectedHours.toFixed(1)} hours/day)
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '30px' }}>
                                                <div style={{ textAlign: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#3b82f6' }}>{totalDays}</div>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Total Days</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#10b981' }}>{presentDays}</div>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Present</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#f59e0b' }}>{sundayDays}</div>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Holidays / Sundays</div>
                                                </div>
                                                <div style={{ textAlign: 'center', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                                    <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#ef4444' }}>{absentDays}</div>
                                                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Absent</div>
                                                </div>
                                            </div>

                                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '20px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div>
                                                    <h4 style={{ margin: '0 0 5px 0', color: '#166534', fontSize: '16px' }}>Net Payable Amount</h4>
                                                    <div style={{ fontSize: '13px', color: '#15803d' }}>Calculated for {paidDays.toFixed(2)} equivalent paid days out of {totalDays} generated days</div>
                                                </div>
                                                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>
                                                    ₹ {totalPay.toFixed(2)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                        {activeTab === 'performance' && (
                            <div style={{ background: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                                <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', color: '#1f2937' }}>Teacher Daily Attendance Duration (Minutes)</h3>
                                {data?.records?.length > 0 ? (
                                    <div style={{ height: '350px', width: '100%' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={[...data.records].reverse().map(r => ({ date: r.date.substring(0, 5), duration: r.durationMinutes || 0 }))} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} dy={10} />
                                                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} dx={-10} />
                                                <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }} />
                                                <Bar dataKey="duration" fill="#22c55e" radius={[6, 6, 0, 0]} barSize={40} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                ) : (
                                    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No performance data available for this month.</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const PortraitIDCard = ({ student, isPrint }) => {
    return (
        <div
            className={isPrint ? 'printable-icard-portrait' : ''}
            style={{
                width: '280px',
                height: '440px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'white',
                boxShadow: isPrint ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'row',
                fontFamily: "'Outfit', 'Inter', sans-serif",
                position: 'relative',
                boxSizing: 'border-box'
            }}
        >
            {/* Left Vertical Stripe */}
            <div style={{
                width: '42px',
                backgroundColor: '#202124',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '15px 0',
                borderRight: '2px solid #3b82f6',
                boxSizing: 'border-box',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
            }}>
                <div style={{ width: '1.5px', height: '40px', background: 'rgba(255,255,255,0.4)' }}></div>
                <div style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontSize: '14px',
                    fontWeight: '800',
                    letterSpacing: '3px',
                    color: '#f8fafc',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                }}>
                    IDENTITY-CARD
                </div>
                <div style={{ width: '1.5px', height: '40px', background: 'rgba(255,255,255,0.4)' }}></div>
            </div>

            {/* Right Information Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '15px 12px 10px 12px',
                boxSizing: 'border-box',
                position: 'relative'
            }}>
                {/* Top Logo */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                    <img src={logo} alt="Logo" style={{ height: '55px', objectFit: 'contain' }} />
                </div>

                {/* Photo */}
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                    {student.photo ? (
                        <img
                            src={getPhotoUrl(student.photo)}
                            alt={student.name}
                            style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #2563eb' }}
                        />
                    ) : (
                        <div style={{ width: '90px', height: '90px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px', fontWeight: 'bold', border: '3px solid #2563eb' }}>
                            {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                        </div>
                    )}
                </div>

                {/* Name */}
                <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                    <h3 style={{
                        fontSize: '13px',
                        fontWeight: '800',
                        color: '#1e3a8a',
                        margin: 0,
                        textTransform: 'uppercase',
                        lineHeight: '1.4',
                        letterSpacing: '0.2px'
                    }}>
                        {student.name}
                    </h3>
                </div>

                {/* Details */}
                <div style={{
                    fontSize: '10.5px',
                    color: '#1e3a8a',
                    fontWeight: '700',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    paddingLeft: '5px',
                    marginBottom: '15px'
                }}>
                    <div>
                        <span style={{ color: '#475569', display: 'inline-block', width: '60px' }}>ROLL NO:</span>
                        <span style={{ color: '#1f2937' }}>{student.id || student.fingerprint_id || '--'}</span>
                    </div>
                    <div>
                        <span style={{ color: '#475569', display: 'inline-block', width: '60px' }}>COURSE:</span>
                        <span style={{ color: '#1f2937' }}>{student.course || 'XI-PCMB'}</span>
                    </div>
                    <div>
                        <span style={{ color: '#475569', display: 'inline-block', width: '60px' }}>BRANCH:</span>
                        <span style={{ color: '#1f2937' }}>{student.branch || 'BKSA'}</span>
                    </div>
                    <div>
                        <span style={{ color: '#475569', display: 'inline-block', width: '60px', verticalAlign: 'top', lineHeight: '1.2' }}>PARENT NO:</span>
                        <span style={{ color: '#1f2937' }}>{student.parentPhone || student.fatherPhone || '--'}</span>
                    </div>
                    <div>
                        <span style={{ color: '#475569', display: 'inline-block', width: '60px', verticalAlign: 'top', lineHeight: '1.2' }}>STUDENT NO:</span>
                        <span style={{ color: '#1f2937' }}>{student.studentPhone || '--'}</span>
                    </div>
                </div>

                {/* Footer Stripe */}
                <div style={{
                    backgroundColor: '#202124',
                    color: 'white',
                    textAlign: 'center',
                    padding: '6px 0',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact',
                    fontSize: '10px',
                    fontWeight: '800',
                    borderRadius: '4px',
                    letterSpacing: '1px',
                    textTransform: 'uppercase'
                }}>
                    BK SCIENCE ACADEMY
                </div>
            </div>
        </div>
    );
};

const LandscapeIDCard = ({ student, isPrint }) => {
    return (
        <div
            className={isPrint ? 'printable-icard-landscape' : ''}
            style={{
                width: '440px',
                height: '280px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'white',
                boxShadow: isPrint ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'row',
                fontFamily: "'Outfit', 'Inter', sans-serif",
                position: 'relative',
                boxSizing: 'border-box'
            }}
        >
            {/* Left Vertical Stripe */}
            <div style={{
                width: '42px',
                backgroundColor: '#202124',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '15px 0',
                borderRight: '2px solid #3b82f6',
                boxSizing: 'border-box',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
            }}>
                <div style={{ width: '1.5px', height: '25px', background: 'rgba(255,255,255,0.4)' }}></div>
                <div style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    fontSize: '14px',
                    fontWeight: '800',
                    letterSpacing: '3px',
                    color: '#f8fafc',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap'
                }}>
                    IDENTITY-CARD
                </div>
                <div style={{ width: '1.5px', height: '25px', background: 'rgba(255,255,255,0.4)' }}></div>
            </div>

            {/* Right Information Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                padding: '12px 15px 8px 15px',
                boxSizing: 'border-box'
            }}>
                {/* Top Row with Photo and Logo */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    {/* Student Photo */}
                    {student.photo ? (
                        <img
                            src={getPhotoUrl(student.photo)}
                            alt={student.name}
                            style={{ width: '75px', height: '75px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #2563eb' }}
                        />
                    ) : (
                        <div style={{ width: '75px', height: '75px', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: 'bold', border: '3px solid #2563eb' }}>
                            {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                        </div>
                    )}

                    {/* Logo */}
                    <img src={logo} alt="Logo" style={{ height: '52px', objectFit: 'contain' }} />
                </div>

                {/* Name */}
                <div style={{ marginBottom: '10px' }}>
                    <h3 style={{
                        fontSize: '14px',
                        fontWeight: '800',
                        color: '#1e3a8a',
                        margin: 0,
                        textTransform: 'uppercase',
                        lineHeight: '1.2',
                        letterSpacing: '0.2px'
                    }}>
                        {student.name}
                    </h3>
                </div>

                {/* Details */}
                <div style={{
                    fontSize: '10px',
                    color: '#1e3a8a',
                    fontWeight: '700',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    marginBottom: '8px'
                }}>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <div>
                            <span style={{ color: '#475569' }}>ROLL NO: </span>
                            <span style={{ color: '#1f2937' }}>{student.id || student.fingerprint_id || '--'}</span>
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <span style={{ color: '#475569' }}>COURSE: </span>
                            <span style={{ color: '#1f2937' }}>{student.course || 'XI-PCMB'}</span>
                        </div>
                        <div>
                            <span style={{ color: '#475569' }}>BRANCH: </span>
                            <span style={{ color: '#1f2937' }}>{student.branch || 'BKSA'}</span>
                        </div>
                    </div>
                    <div>
                        <span style={{ color: '#475569' }}>PARENT CONTACT NO: </span>
                        <span style={{ color: '#1f2937' }}>{student.parentPhone || student.fatherPhone || '--'}</span>
                    </div>
                    <div>
                        <span style={{ color: '#475569' }}>STUDENT CONTACT NO: </span>
                        <span style={{ color: '#1f2937' }}>{student.studentPhone || '--'}</span>
                    </div>
                </div>

                {/* Footer Stripe */}
                <div style={{
                    backgroundColor: '#202124',
                    color: 'white',
                    textAlign: 'center',
                    padding: '5px 0',
                    WebkitPrintColorAdjust: 'exact',
                    printColorAdjust: 'exact',
                    fontSize: '10px',
                    fontWeight: '800',
                    borderRadius: '4px',
                    letterSpacing: '1.2px',
                    textTransform: 'uppercase'
                }}>
                    BK SCIENCE ACADEMY
                </div>
            </div>
        </div>
    );
};

const StudentProfilePage = () => {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [activeTab, setActiveTab] = useState('overview');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
    const [filterYear, setFilterYear] = useState(new Date().getFullYear());

    const fetchStudentData = (month = filterMonth, year = filterYear) => {
        api.get(`/attendance/student/${id}?month=${month}&year=${year}`)
            .then(res => {
                setData(res.data);
                setEditForm(prev => (prev && Object.keys(prev).length > 0 ? prev : res.data.student));
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchStudentData();

        const handleLivePunch = (punch) => {
            if (!punch) return;
            const cleanId = String(id).trim();
            const studFp = data?.student?.fingerprint_id;
            const studId = data?.student?.id;
            const studDbId = data?.student?._id;

            const matches = [
                String(punch.userId || '').trim(),
                String(punch.fingerprint_id || '').trim(),
                String(punch.userDbId || '').trim()
            ];

            const targets = [
                cleanId,
                studFp ? String(studFp).trim() : null,
                studId ? String(studId).trim() : null,
                studDbId ? String(studDbId).trim() : null
            ].filter(Boolean);

            if (matches.some(m => targets.includes(m))) {
                fetchStudentData();
            }
        };

        socket.on('live_punch', handleLivePunch);
        return () => socket.off('live_punch', handleLivePunch);
    }, [id]);

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        let finalValue = value;
        if (name === 'dob' && value) {
            const parts = value.split('-');
            if (parts[0] && parts[0].length > 4) {
                parts[0] = parts[0].substring(0, 4);
                finalValue = parts.join('-');
            }
        }
        let newForm = { ...editForm, [name]: finalValue };

        // Auto-calculate remaining due if fee or amount paid changes
        if (name === 'fee' || name === 'amountReceived') {
            const total = Number(newForm.fee) || 0;
            const paid = Number(newForm.amountReceived) || 0;
            newForm.dueFees = total - paid;
        }

        setEditForm(newForm);
    };

    const handleSave = async () => {
        if (editForm.email && editForm.email.trim() !== '') {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(editForm.email.trim())) {
                alert("Please enter a valid email address (e.g. example@gmail.com).");
                return;
            }
        }
        try {
            const payload = { ...editForm };
            delete payload._id;
            delete payload.__v;
            await api.put(`/users/${id}`, payload);
            alert('Profile updated successfully!');
            setIsEditing(false);
            setActiveTab('overview');
            fetchStudentData();
        } catch (err) {
            console.error('Failed to save profile:', err);
            alert('Failed to save profile: ' + (err.response?.data?.error || err.message));
        }
    };

    const getSecurityCode = (uid) => {
        if (!uid) return '0-000000';
        let hash = 0;
        const str = String(uid);
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const code = Math.abs(hash % 900000) + 100000;
        return `${uid}-${code}`;
    };

    const handlePhotoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            compressImage(reader.result, async (compressed) => {
                try {
                    await api.put(`/users/${id}`, { ...editForm, photo: compressed });
                    fetchStudentData();
                    alert('Photo uploaded successfully!');
                } catch (err) {
                    console.error('Failed to upload photo:', err);
                    alert('Failed to upload photo: ' + (err.response?.data?.error || err.message));
                }
            });
        };
        reader.readAsDataURL(file);
    };

    const handleDeleteUser = async () => {
        if (window.confirm(`Are you sure you want to delete ${data?.student?.name || 'this student'}? This action cannot be undone.`)) {
            try {
                await api.delete(`/users/${id}`);
                alert('Student deleted successfully!');
                window.history.back();
            } catch (err) {
                console.error('Failed to delete student:', err);
                alert('Failed to delete student: ' + (err.response?.data?.error || err.message));
            }
        }
    };

    const handleFeePayment = async () => {
        try {
            const payment = Number(paymentAmount) || 0;
            const currentPaid = Number(data.student.amountReceived) || 0;
            const totalFee = Number(editForm.fee) || 0;

            const newPaid = currentPaid + payment;
            const newDue = totalFee - newPaid;

            const payload = { ...editForm, amountReceived: newPaid, dueFees: newDue };

            await api.put(`/users/${id}`, payload);
            setData({ ...data, student: payload });
            setEditForm(payload);
            setPaymentAmount('');
            alert('Fee payment recorded successfully!');
        } catch (err) {
            console.error(err);
            alert('Failed to record payment');
        }
    };

    const [exportMenuOpen, setExportMenuOpen] = useState(false);

    const handleExport = (format) => {
        if (!data || !data.records || data.records.length === 0) return;

        const exportData = data.records.map(r => ({
            "Date": r.date,
            "In / Out": r.firstIn !== '--:--' ? `${r.firstIn} / ${r.lastOut}` : '--:-- / --:--',
            "Punches": r.totalEntries || 0,
            "Total Hours": r.durationMinutes ? `${Math.floor(r.durationMinutes / 60)}h ${r.durationMinutes % 60}m` : '0h 0m',
            "Status": r.status
        }));

        const sName = (data.student && data.student.name) ? data.student.name : 'Student';

        if (format === 'excel') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Punches");
            XLSX.writeFile(workbook, `${sName}_Punches_${filterMonth}_${filterYear}.xlsx`);
        } else if (format === 'csv') {
            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
            const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `${sName}_Punches_${filterMonth}_${filterYear}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else if (format === 'pdf') {
            const doc = new jsPDF();
            doc.text(`${sName} - Punches (${filterMonth}/${filterYear})`, 14, 15);
            doc.autoTable({
                head: [['Date', 'In / Out', 'Punches', 'Total Hours', 'Status']],
                body: exportData.map(r => [r['Date'], r['In / Out'], r['Punches'], r['Total Hours'], r['Status']]),
                startY: 20
            });
            doc.save(`${sName}_Punches_${filterMonth}_${filterYear}.pdf`);
        }
        setExportMenuOpen(false);
    };

    // ── Print helper: opens a clean new window with the form ──────────────────
    const printStudentForm = (className) => {
        const el = document.querySelector('.' + className);
        if (!el) return;
        const clone = el.cloneNode(true);
        // Remove the embedded <style> that keeps the element hidden by default
        clone.querySelectorAll('style').forEach(s => s.remove());
        const printWin = window.open('', '_blank', 'width=900,height=700');
        if (!printWin) return;

        const isIcard = className.includes('icard');
        const customStyle = isIcard
            ? `* { margin: 0; padding: 0; box-sizing: border-box; font-family: Arial, sans-serif; }
               body { background: white; display: flex; align-items: center; justify-content: center; height: 100vh; padding: 0; }
               @page { size: auto; margin: 0; }`
            : `* { margin: 0; padding: 0; box-sizing: border-box; font-family: Arial, sans-serif; }
               body { background: white; padding: 4mm 15px 0 15px; }
               @page { size: A4; margin: 0; }`;

        printWin.document.write(`<!DOCTYPE html><html><head><title>Print</title>
<style>${customStyle}</style></head><body>${clone.innerHTML}</body></html>`);
        printWin.document.close();
        printWin.focus();
        setTimeout(() => { printWin.print(); printWin.close(); }, 600);
    };

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', fontSize: '18px', color: '#666' }}>Loading Student Profile...</div>;
    if (!data) return <div style={{ padding: '40px', textAlign: 'center', fontSize: '18px', color: '#e74c3c' }}>Student Profile Not Found.</div>;

    const { student, summary, records } = data;

    const chartData = [...records].reverse().map(r => ({
        date: r.date.split('-').slice(1).reverse().join('/'), // DD/MM
        duration: r.durationMinutes || 0
    }));

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', gap: '30px', alignItems: 'flex-start', background: '#f8fafc', padding: '20px', borderRadius: '16px' }}>
            {/* Hidden forms for printing */}
            <ReceiptPrintView student={student} />
            <AdmissionFormPrintView student={student} />
            <div className="printable-icard-portrait" style={{ display: 'none' }}><PortraitIDCard student={student} isPrint={true} /></div>
            <div className="printable-icard-landscape" style={{ display: 'none' }}><LandscapeIDCard student={student} isPrint={true} /></div>

            {/* Left Column (Student Profile, Quick Links, Permission) */}
            <div style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '25px', flexShrink: 0 }}>

                {/* Student Profile Card */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ background: '#f3e8ff', color: '#8b5cf6', padding: '4px 12px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            👤 Student Profile
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                        {student.photo ? (
                            <img
                                src={getPhotoUrl(student.photo)}
                                alt={student.name}
                                style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #8b5cf6', boxShadow: '0 4px 10px rgba(139,92,246,0.1)' }}
                            />
                        ) : (
                            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #8b5cf6, #d946ef)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px', fontWeight: 'bold', border: '3px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }}>
                                {student.name ? student.name.charAt(0).toUpperCase() : '?'}
                            </div>
                        )}
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#1f2937', margin: '0 0 4px 0' }}>{student.name}</h3>
                            <div style={{ display: 'inline-block', background: '#dcfce7', color: '#16a34a', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>Direct Admission</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', borderBottom: '1px solid #f1f5f9', paddingBottom: '15px', marginBottom: '15px', fontSize: '13px', color: '#4b5563' }}>
                        <div><strong style={{ color: '#1f2937' }}>Full Name:</strong> {student.name}</div>
                        <div style={{ wordBreak: 'break-all' }}><strong style={{ color: '#1f2937' }}>Email:</strong> {student.email || 'N/A'}</div>
                        <div><strong style={{ color: '#1f2937' }}>Telephone:</strong> {student.studentPhone || student.parentPhone || 'N/A'}</div>
                        <div><strong style={{ color: '#1f2937' }}>Biometric Id:</strong> {student.fingerprint_id}</div>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input type="file" id="photo-upload-input" style={{ display: 'none' }} accept="image/*" onChange={handlePhotoUpload} />
                        <button
                            onClick={() => document.getElementById('photo-upload-input').click()}
                            style={{ flex: 1, background: '#8b5cf6', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px' }}>
                            📤 Upload
                        </button>
                        <button
                            onClick={handleDeleteUser}
                            style={{ flex: 1, background: '#ef4444', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px' }}>
                            🗑️ Delete
                        </button>
                    </div>
                </div>

                {/* Quick Links Card */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <div style={{ background: '#f3e8ff', color: '#8b5cf6', padding: '4px 12px', borderRadius: '20px', fontWeight: 'bold', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            🔗 Quick Links
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <button onClick={() => setActiveTab('punches')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: activeTab === 'punches' ? '#f3e8ff' : 'white', border: '1px solid #a855f7', borderRadius: '8px', color: '#a855f7', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}>
                            <span>👆</span> Punches
                        </button>
                        <button onClick={() => setActiveTab('fees')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: activeTab === 'fees' ? '#fee2e2' : 'white', border: '1px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><span>₹</span> Fees</div>
                            <span style={{ fontSize: '10px' }}>▼</span>
                        </button>
                        <button onClick={() => setActiveTab('performance')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: activeTab === 'performance' ? '#f1f5f9' : 'white', border: '1px solid #94a3b8', borderRadius: '8px', color: '#475569', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}>
                            <span>📈</span> Performance
                        </button>
                        <button onClick={() => setActiveTab('attendance')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: activeTab === 'attendance' ? '#dcfce7' : 'white', border: '1px solid #22c55e', borderRadius: '8px', color: '#16a34a', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}>
                            <span>🕒</span> Attendance
                        </button>
                        <button onClick={() => printStudentForm('printable-admission-view')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: 'white', border: '1px solid #0ea5e9', borderRadius: '8px', color: '#0ea5e9', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }}>
                            <span>🖨️</span> Admission
                        </button>
                        <button onClick={() => setActiveTab('icard')} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px', background: activeTab === 'icard' ? '#f59e0b15' : 'white', border: '1px solid #f59e0b', borderRadius: '8px', color: '#d97706', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', gridColumn: 'span 2', justifyContent: 'center' }}>
                            <span>🪪</span> Print Icard / ID Card
                        </button>
                    </div>
                </div>


            </div>

            {/* Right Column (Tabs Header and Subpages) */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '25px', minWidth: 0, background: 'white', borderRadius: '12px', padding: '25px', border: '1px solid #e2e8f0', boxShadow: '0 4px 10px rgba(0,0,0,0.02)' }}>

                {/* Tabs row */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', overflowX: 'auto', gap: '5px', paddingBottom: '1px', marginBottom: '10px' }}>
                    {[
                        { id: 'overview', label: 'Overview', icon: '📋' },
                        { id: 'edit', label: 'Edit', icon: '✏️' },
                        { id: 'contacts', label: 'Contacts', icon: '📞' },
                        { id: 'admission', label: 'Admission', icon: '📂' },
                        { id: 'subject', label: 'Subject', icon: '📚' },
                        { id: 'batch', label: 'Batch', icon: '👥' },
                        { id: 'device', label: 'Device', icon: '📟' },
                        { id: 'icard', label: 'ID Card', icon: '🪪' },
                        { id: 'delete', label: 'Delete', icon: '🗑️' }
                    ].map(t => (
                        <button
                            key={t.id}
                            onClick={() => setActiveTab(t.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '10px 16px',
                                border: 'none',
                                background: 'transparent',
                                borderBottom: (activeTab === t.id || (t.id === 'edit' && isEditing)) ? '3px solid #8b5cf6' : '3px solid transparent',
                                color: (activeTab === t.id || (t.id === 'edit' && isEditing)) ? '#8b5cf6' : '#64748b',
                                fontWeight: (activeTab === t.id || (t.id === 'edit' && isEditing)) ? '700' : '600',
                                fontSize: '13px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                transition: 'all 0.15s'
                            }}>
                            <span>{t.icon}</span> {t.label}
                        </button>
                    ))}
                </div>

                {/* Tab content conditional sections */}
                <div style={{ minHeight: '300px' }}>
                    {(activeTab === 'overview' && !isEditing) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
                            {/* Profile Details */}
                            <div>
                                <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1f2937', marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>Profile Details</h3>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#4b5563' }}>
                                    <tbody>
                                        {[
                                            { label: 'Status', value: <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '700' }}>Direct Admission</span> <span onClick={() => setActiveTab('edit')} style={{ cursor: 'pointer', fontSize: '11px' }}>✏️</span></div> },
                                            { label: 'Full Name', value: student.name },
                                            { label: 'Roll No.', value: student.id || '--' },
                                            { label: 'Section', value: student.section || '--' },
                                            { label: 'Gender', value: student.gender || '--' },
                                            { label: 'Biometric Id', value: student.fingerprint_id },
                                            { label: 'Admission Date', value: student.enquiryDate ? new Date(student.enquiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '--' },
                                            { label: 'DOB', value: student.dob ? new Date(student.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '--' },
                                            { label: 'Security Code', value: <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span style={{ fontWeight: '600', color: '#8b5cf6' }}>{getSecurityCode(student.id || student.fingerprint_id)}</span> <span style={{ cursor: 'pointer', color: '#8b5cf6', fontSize: '11px' }}>✈️</span></div> }
                                        ].map((item, i) => (
                                            <tr key={i}>
                                                <td style={{ padding: '10px 0', fontWeight: '600', color: '#64748b', width: '130px' }}>{item.label}</td>
                                                <td style={{ padding: '10px 0', color: '#1f2937' }}>{item.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Contact Details */}
                            <div>
                                <h3 style={{ fontSize: '15px', fontWeight: '800', color: '#1f2937', marginBottom: '15px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>Contact Details</h3>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', color: '#4b5563' }}>
                                    <tbody>
                                        {[
                                            { label: 'Father', value: student.parentPhone ? <span style={{ color: '#8b5cf6', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>📞 {student.parentPhone}</span> : '--' },
                                            { label: 'Mother', value: student.motherPhone ? <span style={{ color: '#8b5cf6', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>📞 {student.motherPhone}</span> : '--' },
                                            { label: 'Student', value: student.studentPhone ? <span style={{ color: '#8b5cf6', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>📞 {student.studentPhone}</span> : '--' },
                                            { label: 'Other', value: '--' },
                                            { label: 'Email', value: student.email || '--' },
                                            { label: 'Address', value: student.address || '--' },
                                            { label: 'Aadhar No', value: student.aadhar || '--' }
                                        ].map((item, i) => (
                                            <tr key={i}>
                                                <td style={{ padding: '10px 0', fontWeight: '600', color: '#64748b', width: '90px' }}>{item.label}</td>
                                                <td style={{ padding: '10px 0', color: '#1f2937', lineHeight: '1.4' }}>{item.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {(activeTab === 'edit' || isEditing) && (
                        <div style={{ maxWidth: '600px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Edit Student Profile</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Full Name</label>
                                    <input name="name" value={editForm.name || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Email</label>
                                    <input name="email" value={editForm.email || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Student Phone</label>
                                    <input name="studentPhone" value={editForm.studentPhone || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Parent/Father Phone</label>
                                    <input name="parentPhone" value={editForm.parentPhone || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Gender</label>
                                    <select name="gender" value={editForm.gender || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', background: 'white', fontSize: '13px' }}>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>DOB</label>
                                    <input type="date" name="dob" value={editForm.dob || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Section</label>
                                    <input name="section" value={editForm.section || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} placeholder="E.g. Section A" />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Role</label>
                                    <select name="role" value={editForm.role || 'student'} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', background: 'white', fontSize: '13px' }}>
                                        <option value="student">Student</option>
                                        <option value="teacher">Teacher</option>
                                        <option value="staff">Staff</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>
                            </div>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px', color: '#4b5563', fontWeight: 600 }}>Address</label>
                                <textarea name="address" value={editForm.address || ''} onChange={handleEditChange} style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', height: '60px', resize: 'vertical', fontSize: '13px' }} />
                            </div>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={handleSave} style={{ background: '#8b5cf6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Save Changes</button>
                                <button onClick={() => { setEditForm(student); setIsEditing(false); setActiveTab('overview'); }} style={{ background: 'transparent', border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, color: '#4b5563', fontSize: '13px' }}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'contacts' && (
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Emergency & Contact List</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '15px' }}>
                                {[
                                    { relation: 'Student', name: student.name, phone: student.studentPhone },
                                    { relation: 'Father / Parent', name: 'Father', phone: student.parentPhone },
                                    { relation: 'Mother', name: 'Mother', phone: student.motherPhone || 'Not Configured' }
                                ].map((c, i) => (
                                    <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                                        <div style={{ fontSize: '11px', color: '#8b5cf6', fontWeight: '700', textTransform: 'uppercase', marginBottom: '4px' }}>{c.relation}</div>
                                        <div style={{ fontSize: '14px', fontWeight: '800', color: '#1f2937', marginBottom: '6px' }}>{c.name}</div>
                                        <div style={{ fontSize: '13px', color: '#475569', fontWeight: '600' }}>📞 {c.phone || 'N/A'}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {activeTab === 'admission' && (
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Admission details</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
                                <div>
                                    <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '10px' }}>Academic details</h4>
                                    <table style={{ width: '100%', fontSize: '13px' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>10th Marks</td>
                                                <td style={{ padding: '6px 0', color: '#1f2937' }}>{student.marks10th ? `${student.marks10th}%` : '--'}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>12th Marks</td>
                                                <td style={{ padding: '6px 0', color: '#1f2937' }}>{student.marks12th ? `${student.marks12th}%` : '--'}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>Qualification</td>
                                                <td style={{ padding: '6px 0', color: '#1f2937' }}>{student.qualification || '--'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div>
                                    <h4 style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', marginBottom: '10px' }}>Documentation Info</h4>
                                    <table style={{ width: '100%', fontSize: '13px' }}>
                                        <tbody>
                                            <tr>
                                                <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>Documents Submitted</td>
                                                <td style={{ padding: '6px 0', color: '#1f2937' }}>{student.documentSubmitted || 'None'}</td>
                                            </tr>
                                            <tr>
                                                <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>Aadhar status</td>
                                                <td style={{ padding: '6px 0', color: '#1f2937' }}>{student.aadhar ? '✅ Aadhar verified' : '❌ Aadhar missing'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'subject' && (
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Enrolled Courses & Subjects</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {student.courses && student.courses.length > 0 ? (
                                    student.courses.map((course, i) => (
                                        <div key={i} style={{ background: '#f8fafc', borderLeft: '4px solid #8b5cf6', padding: '12px 16px', borderRadius: '0 6px 6px 0', fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                                            {course}
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ color: '#94a3b8', fontSize: '13px' }}>No courses enrolled.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'batch' && (
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Batch timing details</h3>
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '350px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                                    <span style={{ color: '#64748b', fontWeight: '600' }}>Selected Batch:</span>
                                    <span style={{ color: '#1f2937', fontWeight: '700' }}>{student.batch || 'General Batch'}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                                    <span style={{ color: '#64748b', fontWeight: '600' }}>Time Slot:</span>
                                    <span style={{ color: '#8b5cf6', fontWeight: '700' }}>{student.batchTiming || 'Flexible'}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'device' && (
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Biometric Device connection</h3>
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '400px' }}>
                                <table style={{ width: '100%', fontSize: '13px' }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>Biometric Register ID</td>
                                            <td style={{ padding: '6px 0', color: '#1f2937', fontWeight: '700' }}>{student.fingerprint_id}</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>Device Link</td>
                                            <td style={{ padding: '6px 0', color: '#22c55e', fontWeight: '700' }}>🟢 Active connection</td>
                                        </tr>
                                        <tr>
                                            <td style={{ padding: '6px 0', color: '#64748b', fontWeight: '600' }}>Protocol used</td>
                                            <td style={{ padding: '6px 0', color: '#1f2937' }}>ADMS Push Protocol</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {activeTab === 'delete' && (
                        <div style={{ maxWidth: '450px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#ef4444', marginBottom: '10px' }}>Danger Zone</h3>
                            <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '15px', lineHeight: '1.4' }}>Permanently remove student profile and associated punch records. This action is irreversible.</p>
                            <button onClick={handleDeleteUser} style={{ background: '#ef4444', color: 'white', padding: '10px 18px', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer', fontSize: '13px' }}>
                                Delete Student Profile
                            </button>
                        </div>
                    )}

                    {activeTab === 'icard' && (
                        <div>
                            <h3 style={{ fontSize: '18px', fontWeight: '800', color: '#1f2937', marginBottom: '20px' }}>Student Identity Card Preview</h3>

                            <div style={{ display: 'flex', gap: '40px', flexWrap: 'wrap', marginBottom: '30px', alignItems: 'flex-start' }}>
                                {/* Portrait card Preview */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#64748b' }}>Portrait View</div>
                                    <PortraitIDCard student={student} isPrint={false} />
                                    <button
                                        onClick={() => printStudentForm('printable-icard-portrait')}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#8b5cf6', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(139, 92, 246, 0.2)' }}
                                    >
                                        📥 Portrait
                                    </button>
                                </div>

                                {/* Landscape card Preview */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#64748b' }}>Landscape View</div>
                                    <LandscapeIDCard student={student} isPrint={false} />
                                    <button
                                        onClick={() => printStudentForm('printable-icard-landscape')}
                                        style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#22c55e', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600', fontSize: '13px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(34, 197, 94, 0.2)' }}
                                    >
                                        📥 Landscape
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'punches' && (
                        <>
                            {/* Stats Summary */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '20px' }}>
                                {[
                                    { label: 'Present Days', value: summary.daysPresentThisMonth, color: '#22c55e', icon: '📅' },
                                    { label: 'Absent Days', value: summary.absentDaysThisMonth || 0, color: '#ef4444', icon: '❌' },
                                    { label: 'Attendance %', value: `${summary.monthlyPercentage}%`, color: '#3b82f6', icon: '📊' },
                                    { label: 'Total Hours', value: summary.totalHours, color: '#8b5cf6', icon: '⏱️' }
                                ].map((stat, i) => (
                                    <div key={i} style={{ background: '#f8fafc', padding: '15px', borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '12px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ fontSize: '20px', background: `${stat.color}15`, width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: stat.color }}>{stat.icon}</div>
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{stat.label}</div>
                                            <div style={{ fontSize: '16px', fontWeight: 800, color: '#1f2937' }}>{stat.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Graph and Table Section */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                                {/* Recharts Graph */}
                                <div style={{ background: 'white', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#1f2937' }}>Daily Classroom Duration (Minutes)</h3>
                                    {chartData.length > 0 ? (
                                        <div style={{ height: '250px', width: '100%' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                                                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 11 }} />
                                                    <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ borderRadius: '6px', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }} />
                                                    <Bar dataKey="duration" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={25} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <div style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: '8px', fontSize: '13px' }}>
                                            No attendance data available for chart.
                                        </div>
                                    )}
                                </div>

                                {/* Detailed Logs Table */}
                                <div style={{ background: 'white', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h3 style={{ margin: 0, fontSize: '15px', color: '#1f2937' }}>Recent Attendance Logs</h3>
                                        <div style={{ position: 'relative' }}>
                                            <button onClick={() => setExportMenuOpen(!exportMenuOpen)} style={{ background: '#f3e8ff', color: '#8b5cf6', border: 'none', padding: '6px 12px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                                                <span>📤</span> Export <span style={{ fontSize: '9px' }}>▼</span>
                                            </button>
                                            {exportMenuOpen && (
                                                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '5px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 10px rgba(0,0,0,0.08)', zIndex: 10, minWidth: '120px', overflow: 'hidden' }}>
                                                    <button onClick={() => handleExport('excel')} style={{ width: '100%', padding: '10px 12px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '12px', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9' }}>
                                                        <span style={{ color: '#10b981' }}>📊</span> Excel (.xlsx)
                                                    </button>
                                                    <button onClick={() => handleExport('csv')} style={{ width: '100%', padding: '10px 12px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '12px', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid #f1f5f9' }}>
                                                        <span style={{ color: '#f59e0b' }}>📝</span> CSV (.csv)
                                                    </button>
                                                    <button onClick={() => handleExport('pdf')} style={{ width: '100%', padding: '10px 12px', border: 'none', background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '12px', color: '#475569', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{ color: '#ef4444' }}>📄</span> PDF (.pdf)
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ overflowY: 'auto', maxHeight: '250px' }}>
                                        {records.length > 0 ? (
                                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                                                <thead>
                                                    <tr style={{ color: '#6b7280', borderBottom: '2px solid #f3f4f6' }}>
                                                        <th style={{ padding: '10px 6px', fontWeight: 600 }}>Date</th>
                                                        <th style={{ padding: '10px 6px', fontWeight: 600 }}>First In</th>
                                                        <th style={{ padding: '10px 6px', fontWeight: 600 }}>Last Out</th>
                                                        <th style={{ padding: '10px 6px', fontWeight: 600 }}>Duration</th>
                                                        <th style={{ padding: '10px 6px', fontWeight: 600 }}>Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {records.map((r, i) => (
                                                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                            <td style={{ padding: '10px 6px', color: '#1f2937', fontWeight: 500 }}>{r.date.split('-').reverse().join('/')}</td>
                                                            <td style={{ padding: '10px 6px', color: '#10b981', fontWeight: 600 }}>{r.firstIn}</td>
                                                            <td style={{ padding: '10px 6px', color: '#f59e0b', fontWeight: 600 }}>{r.lastOut}</td>
                                                            <td style={{ padding: '10px 6px', color: '#6366f1', fontWeight: 600 }}>
                                                                {r.durationSeconds !== undefined
                                                                    ? `${Math.floor(r.durationSeconds / 3600)}h ${Math.floor((r.durationSeconds % 3600) / 60)}m`
                                                                    : `${Math.floor(r.durationMinutes / 60)}h ${r.durationMinutes % 60}m`}
                                                            </td>
                                                            <td style={{ padding: '10px 6px' }}>
                                                                {r.status === 'Late' ? (
                                                                    <span style={{ background: '#fef5e7', color: '#e67e22', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Late</span>
                                                                ) : r.status === 'Absent' ? (
                                                                    <span style={{ background: '#fef2f2', color: '#ef4444', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Absent</span>
                                                                ) : r.status === 'Sunday' ? (
                                                                    <span style={{ background: '#f8fafc', color: '#64748b', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' }}>Sunday</span>
                                                                ) : (
                                                                    <span style={{ color: '#10b981', fontWeight: '600', fontSize: '12px' }}>Present</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <div style={{ padding: '20px 0', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No recent logs found.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'fees' && (
                        <div style={{ background: 'white', padding: '20px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h2 style={{ margin: 0, fontSize: '18px', color: '#1f2937' }}>Fee Management</h2>
                                <button
                                    onClick={() => { document.body.classList.add('print-receipt'); window.print(); setTimeout(() => document.body.classList.remove('print-receipt'), 500); }}
                                    style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                    <span>🖨️</span> Print Fee Receipt
                                </button>
                            </div>

                            {/* Fee Info Cards */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' }}>
                                <div style={{ padding: '15px', borderRadius: '8px', background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                                    <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 600, marginBottom: '4px' }}>Total Fees</div>
                                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#1e3a8a' }}>₹ {student.fee || 0}</div>
                                </div>
                                <div style={{ padding: '15px', borderRadius: '8px', background: '#dcfce7', border: '1px solid #bbf7d0' }}>
                                    <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 600, marginBottom: '4px' }}>Fees Paid</div>
                                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#166534' }}>₹ {student.amountReceived || 0}</div>
                                </div>
                                <div style={{ padding: '15px', borderRadius: '8px', background: '#fee2e2', border: '1px solid #fecaca' }}>
                                    <div style={{ fontSize: '12px', color: '#ef4444', fontWeight: 600, marginBottom: '4px' }}>Remaining Due</div>
                                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#991b1b' }}>₹ {student.dueFees !== undefined ? student.dueFees : ((student.fee || 0) - (student.amountReceived || 0))}</div>
                                </div>
                            </div>

                            {/* Pay Fee Form */}
                            <div style={{ padding: '20px', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#f9fafb' }}>
                                <h3 style={{ margin: '0 0 15px 0', fontSize: '15px', color: '#374151' }}>Record New Payment</h3>
                                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                    <div style={{ flex: '1 1 180px' }}>
                                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px', color: '#4b5563', fontWeight: '600' }}>Total Fee</label>
                                        <input type="number" name="fee" value={editForm.fee || ''} onChange={handleEditChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                    </div>
                                    <div style={{ flex: '1 1 180px' }}>
                                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px', color: '#4b5563', fontWeight: '600' }}>Payment (₹)</label>
                                        <input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="e.g. 5000" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                    </div>
                                    <div style={{ flex: '1 1 180px' }}>
                                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px', color: '#4b5563', fontWeight: '600' }}>Payment Mode</label>
                                        <select name="paymentMode" value={editForm.paymentMode || ''} onChange={handleEditChange} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', background: 'white', fontSize: '13px' }}>
                                            <option value="">Select Mode</option>
                                            <option value="Cash">Cash</option>
                                            <option value="Online">Online</option>
                                            <option value="Cheque">Cheque</option>
                                        </select>
                                    </div>
                                    <div style={{ flex: '1 1 180px' }}>
                                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '6px', color: '#4b5563', fontWeight: '600' }}>Cheque/Txn No.</label>
                                        <input type="text" name="chequeNo" value={editForm.chequeNo || ''} onChange={handleEditChange} placeholder="e.g. TXN123456" style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '13px' }} />
                                    </div>
                                    <button onClick={handleFeePayment} style={{ background: '#10b981', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', transition: 'background 0.2s', flex: '1 1 180px', fontSize: '13px' }}>
                                        Confirm Payment
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'performance' && (
                        <div>
                            <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#1f2937', marginBottom: '15px' }}>Attendance Performance (Last 30 Days)</h3>
                            <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', height: '300px' }}>
                                {records && records.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={[...records].reverse()} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                                            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(tick) => tick.substring(5)} />
                                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(tick) => `${tick}h`} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                                formatter={(value) => [`${value} hrs`, 'Duration']}
                                                labelFormatter={(label) => `Date: ${label}`}
                                            />
                                            <Line type="monotone" dataKey={(r) => r.durationSeconds !== undefined ? (r.durationSeconds / 3600).toFixed(1) : (r.durationMinutes / 60).toFixed(1)} stroke="#8b5cf6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '14px' }}>
                                        No attendance data available to generate performance graph.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};


// --- FINANCES PAGE ---
const FinancesPage = ({ activeTab }) => {
    const [tab, setTab] = useState(activeTab || 'cleared-fees');
    const [students, setStudents] = useState([]);
    const [incomeEntries, setIncomeEntries] = useState([]);
    const [expenseEntries, setExpenseEntries] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Search/Filters
    const [studentSearch, setStudentSearch] = useState('');
    const [expenseSearch, setExpenseSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [payrollRoleFilter, setPayrollRoleFilter] = useState('all');
    const [downloadingPayroll, setDownloadingPayroll] = useState(false);
    const [downloadingBarti, setDownloadingBarti] = useState(false);

    const filteredPayrollTeachers = teachers.filter(t => {
        if (payrollRoleFilter === 'teacher') return (t.role || '').toLowerCase() === 'teacher';
        if (payrollRoleFilter === 'staff') return (t.role || '').toLowerCase() === 'staff';
        return true;
    });

    const handleDownloadPayrollExcel = async () => {
        setDownloadingPayroll(true);
        try {
            const now = new Date();
            const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const targetDept = payrollRoleFilter === 'teacher' ? 'Teachers' : (payrollRoleFilter === 'staff' ? 'Staff' : 'Teachers & Staff');
            
            const response = await api.get('/attendance/export-monthly', {
                params: { month: monthStr, department: targetDept },
                responseType: 'blob',
                timeout: 300000
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `BARTI_${targetDept.replace(/\s+/g, '_')}_Biometric_Report_${monthStr}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download BARTI Excel error:', err);
            let errMsg = err.message;
            if (err.response?.data instanceof Blob) {
                try {
                    const errText = await err.response.data.text();
                    const parsed = JSON.parse(errText);
                    if (parsed?.error) errMsg = parsed.error;
                } catch (_) {}
            } else if (err.response?.data?.error) {
                errMsg = err.response.data.error;
            }
            alert('Failed to download BARTI Excel report: ' + errMsg);
        } finally {
            setDownloadingPayroll(false);
        }
    };

    const handleDownloadMonthlyBartiAttendance = async () => {
        setDownloadingBarti(true);
        try {
            const now = new Date();
            const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            const targetDept = payrollRoleFilter === 'teacher' ? 'Teachers' : (payrollRoleFilter === 'staff' ? 'Staff' : 'Teachers & Staff');
            
            const response = await api.get('/attendance/export-monthly', {
                params: { month: monthStr, department: targetDept },
                responseType: 'blob',
                timeout: 300000
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `BARTI_${targetDept.replace(/\s+/g, '_')}_Attendance_Report_${monthStr}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download BARTI report error:', err);
            let errMsg = err.message;
            if (err.response?.data instanceof Blob) {
                try {
                    const errText = await err.response.data.text();
                    const parsed = JSON.parse(errText);
                    if (parsed?.error) errMsg = parsed.error;
                } catch (_) {}
            } else if (err.response?.data?.error) {
                errMsg = err.response.data.error;
            }
            alert('Failed to download BARTI Attendance Report: ' + errMsg);
        } finally {
            setDownloadingBarti(false);
        }
    };

    // Pay Modal
    const [payingStudent, setPayingStudent] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [totalFeeInput, setTotalFeeInput] = useState('');
    const [paymentMode, setPaymentMode] = useState('Cash');
    const [txnId, setTxnId] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [modalSuccessMsg, setModalSuccessMsg] = useState('');

    // New Income Form
    const [newIncome, setNewIncome] = useState({ description: '', amount: '', date: new Date().toISOString().substring(0, 10), source: 'Other' });

    // New Expense Form
    const [newExpense, setNewExpense] = useState({ description: '', amount: '', date: new Date().toISOString().substring(0, 10), category: 'daily' });

    // Teacher Salary Form
    const [editingTeacher, setEditingTeacher] = useState(null);
    const [newSalary, setNewSalary] = useState('');

    const isWithinDateRange = (itemDate) => {
        if (!itemDate) return true;
        const d = new Date(itemDate);
        if (isNaN(d.getTime())) return true;

        // Reset times for date-only comparison
        const checkDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());

        if (startDate) {
            const start = new Date(startDate);
            const startVal = new Date(start.getFullYear(), start.getMonth(), start.getDate());
            if (checkDate < startVal) return false;
        }

        if (endDate) {
            const end = new Date(endDate);
            const endVal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
            if (checkDate > endVal) return false;
        }

        return true;
    };

    // Multi-sheet Full Financial Report Workbook Export
    const handleExportAll = () => {
        const wb = XLSX.utils.book_new();

        // 1. Overview Sheet
        const overviewData = [
            ["Financial Summary Report"],
            [`Date Range: ${startDate || 'All Time'} to ${endDate || 'All Time'}`],
            ["Generated Date:", new Date().toLocaleString()],
            [],
            ["KPI Metric", "Amount (₹)"],
            ["Total Revenues", totalIncome],
            ["Total Expenses", totalExpenses],
            ["Net Cashflow Balance", profitOrLoss],
            ["Outstanding Fees Due", totalFeesOutstanding],
            ["Total Expected Fees", totalFeesExpected],
            ["Student Fees Collected", totalFeesCollected],
            ["Miscellaneous Income Logged", manualIncomeTotal]
        ];
        const wsOverview = XLSX.utils.aoa_to_sheet(overviewData);
        XLSX.utils.book_append_sheet(wb, wsOverview, "Summary Overview");

        // 2. Fee Collection Sheet
        const feeRows = dateFilteredStudents.map(s => {
            const feeVal = Number(s.fee) || 0;
            const paidVal = Number(s.amountReceived) || 0;
            return {
                "Student Name": s.name || '',
                "Batch / Class": s.batch || 'N/A',
                "Total Fee (₹)": feeVal,
                "Paid Amount (₹)": paidVal,
                "Remaining Due (₹)": Math.max(0, feeVal - paidVal),
                "Payment Mode": s.paymentMode || '—',
                "Receipt / Txn ID": s.receiptNo || '—',
                "Admission Date": s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'N/A'
            };
        });
        const wsFees = XLSX.utils.json_to_sheet(feeRows);
        XLSX.utils.book_append_sheet(wb, wsFees, "Student Fees");

        // 3. Income Ledger Sheet
        const incomeRows = [];
        // Add student fees
        dateFilteredStudents.filter(s => (Number(s.amountReceived) || 0) > 0).forEach(s => {
            incomeRows.push({
                "Description": `Fee Payment from ${s.name}`,
                "Category/Source": "Student Fees",
                "Amount (₹)": Number(s.amountReceived) || 0,
                "Date": s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'N/A',
                "Payment Mode/Details": `Receipt: ${s.receiptNo || 'Auto'} | Mode: ${s.paymentMode || 'Cash'}`
            });
        });
        // Add other income
        dateFilteredIncome.forEach(inc => {
            incomeRows.push({
                "Description": inc.description,
                "Category/Source": inc.source || 'Other',
                "Amount (₹)": Number(inc.amount) || 0,
                "Date": inc.date ? new Date(inc.date).toLocaleDateString() : 'N/A',
                "Payment Mode/Details": "Manually Logged"
            });
        });
        const wsIncome = XLSX.utils.json_to_sheet(incomeRows);
        XLSX.utils.book_append_sheet(wb, wsIncome, "Income Ledger");

        // 4. Staff Payroll Sheet
        const payrollRows = teachers.map(t => {
            const baseSal = Number(t.salary) || 0;
            const daysInM = t.daysInMonth || 30;
            const dailyRate = Math.round(t.dailyRate || (baseSal > 0 ? baseSal / daysInM : 0));
            const absentDaysCount = t.absentDaysCount || 0;
            const halfDaysCount = t.halfDaysCount || 0;
            const absentDeduction = Math.round(t.absentDeduction || (absentDaysCount * dailyRate));
            const halfDayDeduction = Math.round(t.halfDayDeduction || (halfDaysCount * (dailyRate * 0.5)));
            const totalDeductions = absentDeduction + halfDayDeduction + Math.round(t.totalLatePenalty || 0) + Math.round(t.partialHoursDeduction || 0);
            const netPayout = Math.max(0, Math.round(t.monthPay !== undefined ? t.monthPay : (baseSal - totalDeductions)));

            return {
                "Staff Name": t.name || '',
                "Role": t.role || '',
                "Biometric ID": t.fingerprint_id || t.id || '',
                "Base Salary (₹)": baseSal,
                "Total Amount To Pay (₹)": netPayout
            };
        });
        const wsPayroll = XLSX.utils.json_to_sheet(payrollRows);
        XLSX.utils.book_append_sheet(wb, wsPayroll, "Staff Payroll");

        // 5. Daily Expenses Sheet
        const dailyRows = dateFilteredExpenses.filter(e => e.category === 'daily').map(e => ({
            "Description": e.description,
            "Amount (₹)": Number(e.amount) || 0,
            "Date": e.date ? new Date(e.date).toLocaleDateString() : 'N/A'
        }));
        const wsDaily = XLSX.utils.json_to_sheet(dailyRows);
        XLSX.utils.book_append_sheet(wb, wsDaily, "Daily Expenses");

        // 6. Monthly Expenses Sheet
        const monthlyRows = dateFilteredExpenses.filter(e => e.category === 'monthly').map(e => ({
            "Description": e.description,
            "Amount (₹)": Number(e.amount) || 0,
            "Date": e.date ? new Date(e.date).toLocaleDateString() : 'N/A'
        }));
        const wsMonthly = XLSX.utils.json_to_sheet(monthlyRows);
        XLSX.utils.book_append_sheet(wb, wsMonthly, "Monthly Expenses");

        // Write file
        XLSX.writeFile(wb, `BK_Financial_Report_${new Date().toISOString().substring(0, 10)}.xlsx`);
    };

    // Tab-specific Excel Export helper
    const handleExportCurrentTab = () => {
        const wb = XLSX.utils.book_new();
        let sheetName = "";
        let exportData = [];

        if (tab === 'cleared-fees') {
            sheetName = "Fees Collection";
            exportData = filteredStudents.map(s => ({
                "Student Name": s.name,
                "Batch / Class": s.batch || 'N/A',
                "Total Fee (₹)": Number(s.fee) || 0,
                "Paid Amount (₹)": Number(s.amountReceived) || 0,
                "Remaining Due (₹)": Math.max(0, (Number(s.fee) || 0) - (Number(s.amountReceived) || 0)),
                "Payment Mode": s.paymentMode || '—',
                "Receipt No": s.receiptNo || '—'
            }));
        } else if (tab === 'income') {
            sheetName = "Income Ledger";
            // Student fee collections
            dateFilteredStudents.filter(s => (Number(s.amountReceived) || 0) > 0).forEach(s => {
                exportData.push({
                    "Description": `Fee Payment from ${s.name}`,
                    "Category": "Student Fees",
                    "Amount (₹)": Number(s.amountReceived) || 0,
                    "Date": s.createdAt ? new Date(s.createdAt).toLocaleDateString() : 'N/A',
                    "Details": `Receipt: ${s.receiptNo || 'Auto'} | Mode: ${s.paymentMode || 'Cash'}`
                });
            });
            // Manual logged income
            dateFilteredIncome.forEach(inc => {
                exportData.push({
                    "Description": inc.description,
                    "Category": inc.source || 'Other',
                    "Amount (₹)": Number(inc.amount) || 0,
                    "Date": inc.date ? new Date(inc.date).toLocaleDateString() : 'N/A',
                    "Details": "Manually Logged"
                });
            });
        } else if (tab === 'salary') {
            sheetName = "Staff Payroll";
            exportData = teachers.map(t => {
                const baseSal = Number(t.salary) || 0;
                const daysInM = t.daysInMonth || 30;
                const dailyRate = Math.round(t.dailyRate || (baseSal > 0 ? baseSal / daysInM : 0));
                const absentDaysCount = t.absentDaysCount || 0;
                const halfDaysCount = t.halfDaysCount || 0;
                const absentDeduction = Math.round(t.absentDeduction || (absentDaysCount * dailyRate));
                const halfDayDeduction = Math.round(t.halfDayDeduction || (halfDaysCount * (dailyRate * 0.5)));
                const totalDeductions = absentDeduction + halfDayDeduction + Math.round(t.totalLatePenalty || 0) + Math.round(t.partialHoursDeduction || 0);
                const netPayout = Math.max(0, Math.round(t.monthPay !== undefined ? t.monthPay : (baseSal - totalDeductions)));

                return {
                    "Staff Name": t.name || '',
                    "Role": t.role || '',
                    "Biometric ID": t.fingerprint_id || t.id || '',
                    "Base Salary (₹)": baseSal,
                    "Total Amount To Pay (₹)": netPayout
                };
            });
        } else if (tab === 'daily-expenses') {
            sheetName = "Daily Expenses";
            exportData = filteredExpenses.map(e => ({
                "Description": e.description,
                "Amount (₹)": Number(e.amount) || 0,
                "Date": e.date ? new Date(e.date).toLocaleDateString() : 'N/A'
            }));
        } else if (tab === 'monthly-expenses') {
            sheetName = "Monthly Expenses";
            exportData = filteredExpenses.map(e => ({
                "Description": e.description,
                "Amount (₹)": Number(e.amount) || 0,
                "Date/Month": e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'
            }));
        }

        const ws = XLSX.utils.json_to_sheet(exportData);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `BK_${sheetName.replace(/\s+/g, '_')}_Report_${new Date().toISOString().substring(0, 10)}.xlsx`);
    };

    useEffect(() => {
        setTab(activeTab);
    }, [activeTab]);

    const fetchData = async (isInitial = false) => {
        if (isInitial) setLoading(true);
        setError('');
        try {
            // Fetch users (for students)
            const usersRes = await api.get('/users');
            setStudents(usersRes.data.filter(u => u.role === 'student'));

            // Fetch income entries
            const incomeRes = await api.get('/income');
            setIncomeEntries(incomeRes.data);

            // Fetch expense entries
            const expenseRes = await api.get('/expenses');
            setExpenseEntries(expenseRes.data);

            // Fetch teachers / payroll
            const payrollRes = await api.get('/staff/payroll-summary');
            setTeachers(payrollRes.data);
        } catch (err) {
            console.error('Error fetching financial data:', err);
            setError('Could not fetch financial details. Please verify backend connection.');
        } finally {
            if (isInitial) setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(true);
    }, []);

    // Payment Handler
    const handleCollectPayment = async (e) => {
        e.preventDefault();
        if (!payingStudent || !paymentAmount) return;

        const currentPaid = Number(payingStudent.amountReceived) || 0;
        const numAmount = Number(paymentAmount) || 0;
        const newPaid = currentPaid + numAmount;
        
        const customTotalFee = Number(totalFeeInput);
        const totalFee = (!isNaN(customTotalFee) && customTotalFee > 0) ? customTotalFee : (Number(payingStudent.fee) || (currentPaid + (Number(payingStudent.dueFees) || numAmount)));
        const newDue = Math.max(0, totalFee - newPaid);
        const receipt = txnId || `REC-${Math.floor(10000 + Math.random() * 90000)}`;

        const payload = {
            ...payingStudent,
            fee: totalFee,
            amountReceived: newPaid,
            dueFees: newDue,
            paymentMode: paymentMode,
            receiptNo: receipt
        };
        delete payload._id;
        if (payload.id && isNaN(Number(payload.id))) {
            delete payload.id;
        }

        const targetId = payingStudent._id || payingStudent.id || payingStudent.fingerprint_id;
        if (!targetId) {
            alert('Error: Could not identify student ID. Please refresh.');
            return;
        }

        try {
            await api.put(`/users/${targetId}`, payload);

            try {
                await api.post('/fee-payments', {
                    studentId: String(payingStudent.fingerprint_id || payingStudent.id || payingStudent._id),
                    amountPaid: numAmount,
                    paymentMode: paymentMode,
                    notes: `Receipt ${receipt}`
                });
            } catch (e) {
                // non-blocking
            }

            const studentName = payingStudent.name;
            const msg = `✅ Payment of ₹${numAmount.toLocaleString()} collected successfully for ${studentName}! (Receipt #: ${receipt})`;
            setModalSuccessMsg(msg);
            setSuccessMsg(msg);
            setTimeout(() => setSuccessMsg(''), 5000);

            setTimeout(() => {
                setModalSuccessMsg('');
                setPayingStudent(null);
                setPaymentAmount('');
                setTotalFeeInput('');
                setTxnId('');
                fetchData(false);
            }, 1500);
        } catch (err) {
            console.error('Payment Error:', err);
            alert('Failed to save fee payment: ' + (err.message || 'Server error'));
        }
    };

    // Add Income
    const handleAddIncome = async (e) => {
        e.preventDefault();
        if (!newIncome.description || !newIncome.amount) return;

        try {
            await api.post('/income', newIncome);
            setNewIncome({ description: '', amount: '', date: new Date().toISOString().substring(0, 10), source: 'Other' });
            fetchData();
        } catch (err) {
            console.error('Add Income Error:', err);
            alert('Failed to save income entry.');
        }
    };

    // Delete Income
    const handleDeleteIncome = async (id) => {
        if (!window.confirm('Delete this income record?')) return;
        try {
            await api.delete(`/income/${id}`);
            fetchData();
        } catch (err) {
            console.error('Delete Income Error:', err);
            alert('Failed to delete income.');
        }
    };

    // Add Expense
    const handleAddExpense = async (e, type) => {
        e.preventDefault();
        if (!newExpense.description || !newExpense.amount) return;

        try {
            await api.post('/expenses', { ...newExpense, category: type });
            setNewExpense({ description: '', amount: '', date: new Date().toISOString().substring(0, 10), category: 'daily' });
            fetchData();
        } catch (err) {
            console.error('Add Expense Error:', err);
            alert('Failed to save expense.');
        }
    };

    // Delete Expense
    const handleDeleteExpense = async (id) => {
        if (!window.confirm('Delete this expense record?')) return;
        try {
            await api.delete(`/expenses/${id}`);
            fetchData();
        } catch (err) {
            console.error('Delete Expense Error:', err);
            alert('Failed to delete expense.');
        }
    };

    // Update Base Salary
    const handleUpdateSalary = async (e) => {
        e.preventDefault();
        if (!editingTeacher || !newSalary) return;

        // Find the full user model in local list or database payload
        try {
            await api.put(`/users/${editingTeacher.id}`, { ...editingTeacher, salary: Number(newSalary) });
            setEditingTeacher(null);
            setNewSalary('');
            fetchData();
        } catch (err) {
            console.error('Update Salary Error:', err);
            alert('Failed to update base salary.');
        }
    };

    // Payout Salary (Add Expense & Mark Paid mock)
    const handlePayoutSalary = async (teacher) => {
        const baseSal = Number(teacher.salary) || 0;
        const netPay = Math.round(teacher.monthPay || 0);
        const absentDeduction = Math.round(teacher.absentDeduction || 0);
        const latePenalty = Math.round(teacher.totalLatePenalty || 0);
        const absentDaysCount = teacher.absentDaysCount || 0;

        if (teacher.role !== 'teacher' && teacher.role !== 'staff') {
            alert('Payout is only applicable to staff & teachers.');
            return;
        }

        const breakdown = [
            `Base Salary:     ₹${baseSal.toLocaleString()}`,
            absentDeduction > 0 ? `Absent (${absentDaysCount} days): - ₹${absentDeduction.toLocaleString()}` : null,
            latePenalty > 0 ? `Late Penalty:    - ₹${latePenalty.toLocaleString()}` : null,
            `─────────────────────────`,
            `Net Pay:         ₹${netPay.toLocaleString()}`
        ].filter(Boolean).join('\n');

        if (!window.confirm(`Payout Salary for ${teacher.name}?\n\n${breakdown}\n\nThis will record a Monthly Expense.`)) return;

        try {
            // Log as monthly expense
            await api.post('/expenses', {
                description: `Salary Payout - ${teacher.name} (${teacher.role})`,
                amount: netPay,
                date: new Date(),
                category: 'monthly'
            });
            alert(`Salary of ₹${netPay} successfully paid out and recorded under Monthly Expenses!`);
            fetchData();
        } catch (err) {
            console.error('Salary Payout Error:', err);
            alert('Failed to record salary payout expense.');
        }
    };


    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', fontSize: '18px', fontWeight: '600', color: '#666' }}>
                ⌛ Loading Financial Transactions & Records...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444', background: '#fee2e2', borderRadius: '12px', border: '1px solid #fca5a5', margin: '40px auto', maxWidth: '600px' }}>
                <h3>⚠️ Error Loading Data</h3>
                <p>{error}</p>
                <button onClick={fetchData} style={{ padding: '10px 20px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', marginTop: '10px', fontWeight: 'bold' }}>Retry Connection</button>
            </div>
        );
    }

    // Filtered collections based on Date Range
    const dateFilteredStudents = students.filter(s => isWithinDateRange(s.createdAt));
    const dateFilteredIncome = incomeEntries.filter(i => isWithinDateRange(i.date || i.createdAt));
    const dateFilteredExpenses = expenseEntries.filter(e => isWithinDateRange(e.date || e.createdAt));

    // Calculations based on filtered lists
    const totalFeesExpected = dateFilteredStudents.reduce((acc, curr) => acc + (Number(curr.fee) || 0), 0);
    const totalFeesCollected = dateFilteredStudents.reduce((acc, curr) => acc + (Number(curr.amountReceived) || 0), 0);
    const totalFeesOutstanding = Math.max(0, totalFeesExpected - totalFeesCollected);

    const manualIncomeTotal = dateFilteredIncome.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    const totalIncome = totalFeesCollected + manualIncomeTotal;

    const dailyExpensesTotal = dateFilteredExpenses.filter(e => e.category === 'daily').reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    const monthlyExpensesTotal = dateFilteredExpenses.filter(e => e.category === 'monthly').reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
    const totalStaffPayroll = teachers.reduce((acc, curr) => acc + (Number(curr.monthPay) || 0), 0);
    const totalExpenses = dailyExpensesTotal + monthlyExpensesTotal + totalStaffPayroll;

    const profitOrLoss = totalIncome - totalExpenses;

    const filteredStudents = dateFilteredStudents.filter(s => s.name.toLowerCase().includes(studentSearch.toLowerCase()));
    const filteredExpenses = dateFilteredExpenses.filter(e => e.description.toLowerCase().includes(expenseSearch.toLowerCase()));

    return (
        <div style={{ padding: '10px 20px 40px', fontFamily: '"Inter", sans-serif' }}>
            {successMsg && (
                <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '14px 20px', borderRadius: '10px', marginBottom: '20px', fontWeight: 'bold', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 12px rgba(22,163,74,0.1)' }}>
                    <span>{successMsg}</span>
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: '1px solid #e2e8f0', paddingBottom: '20px' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '26px', color: '#1e293b', fontWeight: '700' }}>📊 Financial Management</h1>
                    <p style={{ margin: '5px 0 0', color: '#64748b', fontSize: '14px' }}>Track student payments, other revenues, staff payroll payouts, and expenses.</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setTab('cleared-fees')} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: tab === 'cleared-fees' ? '#667eea' : 'white', color: tab === 'cleared-fees' ? 'white' : '#475569', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>Cleared Fees</button>
                    <button onClick={() => setTab('income')} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: tab === 'income' ? '#667eea' : 'white', color: tab === 'income' ? 'white' : '#475569', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>Income</button>
                    <button onClick={() => setTab('salary')} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: tab === 'salary' ? '#667eea' : 'white', color: tab === 'salary' ? 'white' : '#475569', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>Salary / Payroll</button>
                    <button onClick={() => setTab('daily-expenses')} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: tab === 'daily-expenses' ? '#667eea' : 'white', color: tab === 'daily-expenses' ? 'white' : '#475569', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>Daily Expenses</button>
                    <button onClick={() => setTab('monthly-expenses')} style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', background: tab === 'monthly-expenses' ? '#667eea' : 'white', color: tab === 'monthly-expenses' ? 'white' : '#475569', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>Monthly Expenses</button>
                </div>
            </div>

            {/* Date Filters & Export Control Strip */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', background: '#f8fafc', padding: '16px 20px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '25px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>📅 From Date:</span>
                        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', outline: 'none', fontSize: '14px', background: 'white' }} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>📅 To Date:</span>
                        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', outline: 'none', fontSize: '14px', background: 'white' }} />
                    </div>
                    {(startDate || endDate) && (
                        <button onClick={() => { setStartDate(''); setEndDate(''); }} style={{ padding: '8px 14px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            Clear Filter
                        </button>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={handleExportCurrentTab} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.2s' }}>
                        📊 Export Active Tab
                    </button>
                    <button onClick={handleExportAll} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'all 0.2s' }}>
                        📥 Export Full Report (Excel)
                    </button>
                </div>
            </div>

            {/* Quick Summary Strip */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', borderLeft: '5px solid #10b981' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Total Revenues</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#0f766e', marginTop: '5px' }}>₹{totalIncome.toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Fees + Extra Income</div>
                </div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', borderLeft: '5px solid #ef4444' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Total Expenses</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#b91c1c', marginTop: '5px' }}>₹{totalExpenses.toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Daily + Fixed + Staff Payroll</div>
                </div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', borderLeft: '5px solid #3b82f6' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Net Cashflow Balance</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: profitOrLoss >= 0 ? '#1e3a8a' : '#7f1d1d', marginTop: '5px' }}>{profitOrLoss >= 0 ? '+' : ''}₹{profitOrLoss.toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: profitOrLoss >= 0 ? '#10b981' : '#ef4444', fontWeight: '700', marginTop: '4px' }}>{profitOrLoss >= 0 ? 'Surplus / Profit' : 'Deficit / Loss'}</div>
                </div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', borderLeft: '5px solid #eab308' }}>
                    <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' }}>Outstanding Fees Due</div>
                    <div style={{ fontSize: '24px', fontWeight: '800', color: '#a16207', marginTop: '5px' }}>₹{totalFeesOutstanding.toLocaleString()}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Unpaid student balances</div>
                </div>
            </div>

            {/* TAB CONTENT: Cleared Fees */}
            {tab === 'cleared-fees' && (
                <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                        <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Fee Collection & Cleared Receipts</h3>
                        <input type="text" placeholder="🔍 Search student name..." value={studentSearch} onChange={(e) => setStudentSearch(e.target.value)} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', outline: 'none', width: '260px', fontSize: '14px' }} />
                    </div>

                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Student Name</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Batch / Class</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Total Fee</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Paid Amount</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Remaining Due</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Payment Mode</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Receipt / Txn ID</th>
                                    <th style={{ padding: '12px 16px', color: '#475569', fontWeight: '600', fontSize: '14px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.map(student => {
                                    const feeVal = Number(student.fee) || 0;
                                    const paidVal = Number(student.amountReceived) || 0;
                                    const remainingDue = Math.max(0, feeVal - paidVal);
                                    const isCleared = remainingDue === 0 && feeVal > 0;

                                    return (
                                        <tr key={student.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '14px 16px', fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>{student.name}</td>
                                            <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '14px' }}>{student.batch || 'N/A'}</td>
                                            <td style={{ padding: '14px 16px', color: '#334155', fontWeight: '500', fontSize: '14px' }}>₹{feeVal.toLocaleString()}</td>
                                            <td style={{ padding: '14px 16px', color: '#166534', fontWeight: '600', fontSize: '14px' }}>₹{paidVal.toLocaleString()}</td>
                                            <td style={{ padding: '14px 16px', fontSize: '14px' }}>
                                                {isCleared ? (
                                                    <span style={{ padding: '4px 8px', background: '#dcfce7', color: '#15803d', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold' }}>✓ Paid / Cleared</span>
                                                ) : (
                                                    <span style={{ color: '#b91c1c', fontWeight: '600' }}>₹{remainingDue.toLocaleString()}</span>
                                                )}
                                            </td>
                                            <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '14px' }}>{student.paymentMode || '—'}</td>
                                            <td style={{ padding: '14px 16px', color: '#64748b', fontFamily: 'monospace', fontSize: '14px' }}>{student.receiptNo || '—'}</td>
                                            <td style={{ padding: '14px 16px', fontSize: '14px' }}>
                                                <button onClick={() => { setPayingStudent(student); setPaymentAmount(remainingDue); }} style={{ padding: '6px 12px', background: isCleared ? '#f1f5f9' : '#10b981', color: isCleared ? '#94a3b8' : 'white', border: 'none', borderRadius: '6px', cursor: isCleared ? 'default' : 'pointer', fontWeight: 'bold' }} disabled={isCleared}>
                                                    Collect Fee
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: Income */}
            {tab === 'income' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '30px' }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#1e293b' }}>Log Miscellaneous Income</h3>
                        <form onSubmit={handleAddIncome} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Income Source Description*</label>
                                <input type="text" placeholder="e.g. Bank Interest, Equipment Rental, Book Sales" value={newIncome.description} onChange={(e) => setNewIncome({ ...newIncome, description: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} required />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Amount (₹)*</label>
                                    <input type="number" placeholder="₹" value={newIncome.amount} onChange={(e) => setNewIncome({ ...newIncome, amount: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} required />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Category/Source</label>
                                    <select value={newIncome.source} onChange={(e) => setNewIncome({ ...newIncome, source: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}>
                                        <option value="Other">Other</option>
                                        <option value="Rent">Rent</option>
                                        <option value="Interest">Interest</option>
                                        <option value="Sponsorship">Sponsorship</option>
                                        <option value="Book Sale">Book Sale</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Date</label>
                                <input type="date" value={newIncome.date} onChange={(e) => setNewIncome({ ...newIncome, date: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <button type="submit" style={{ padding: '12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>Save Income Entry</button>
                        </form>

                        {/* Chart representation */}
                        <div style={{ marginTop: '30px', height: '240px' }}>
                            <h4 style={{ margin: '0 0 10px', color: '#475569' }}>Income Breakdown Chart</h4>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={[{ name: 'Revenues', 'Student Fees': totalFeesCollected, 'Other Income': manualIncomeTotal }]} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="Student Fees" fill="#10b981" />
                                    <Bar dataKey="Other Income" fill="#3b82f6" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#1e293b' }}>Income Ledger</h3>
                        <div style={{ overflowY: 'auto', maxHeight: '480px' }}>
                            {/* Student Fees items */}
                            {/* Student Fees items */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 10px', color: '#64748b', fontSize: '13px', textTransform: 'uppercase' }}>Auto-collected Student Fees</h4>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {dateFilteredStudents.filter(s => (Number(s.amountReceived) || 0) > 0).map(s => (
                                        <li key={`fee-${s.id}`} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: '#fafafb', borderRadius: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>Fee Payment from {s.name}</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>Receipt: {s.receiptNo || 'Auto'} | Mode: {s.paymentMode || 'Cash'}</div>
                                            </div>
                                            <div style={{ color: '#166534', fontWeight: '700', fontSize: '14px' }}>+₹{(Number(s.amountReceived) || 0).toLocaleString()}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Manual entries */}
                            <div>
                                <h4 style={{ margin: '0 0 10px', color: '#64748b', fontSize: '13px', textTransform: 'uppercase' }}>Logged Incomes</h4>
                                {dateFilteredIncome.length === 0 ? (
                                    <div style={{ textShadow: 'none', color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No manual income logs yet.</div>
                                ) : (
                                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                        {dateFilteredIncome.map(inc => (
                                            <li key={inc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid #f1f5f9', background: 'white', borderRadius: '6px', marginBottom: '6px', border: '1px solid #e2e8f0' }}>
                                                <div>
                                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>{inc.description}</div>
                                                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Source: {inc.source} | Date: {inc.date ? new Date(inc.date).toLocaleDateString() : 'N/A'}</div>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                    <span style={{ color: '#10b981', fontWeight: '700', fontSize: '14px' }}>+₹{(Number(inc.amount) || 0).toLocaleString()}</span>
                                                    <button onClick={() => handleDeleteIncome(inc.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>🗑️</button>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: Salary / Payroll */}
            {tab === 'salary' && (
                <div style={{ background: 'white', padding: '28px', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.01)', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a', tracking: '-0.02em' }}>Staff Payroll & Attendance Pay Ledger</h3>
                                <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>BETTER PLUS</span>
                            </div>
                            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '13px' }}>100% Transparent Mathematical Pay Audit & Biometric Synchronization</p>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <select 
                                value={payrollRoleFilter} 
                                onChange={(e) => setPayrollRoleFilter(e.target.value)}
                                style={{ padding: '9px 14px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', fontSize: '13px', fontWeight: '600', color: '#334155', outline: 'none', cursor: 'pointer' }}
                            >
                                <option value="all">👥 All Members (Teachers & Staff)</option>
                                <option value="teacher">👨‍🏫 Teachers Only</option>
                                <option value="staff">💼 Staff Only</option>
                            </select>

                            <button 
                                onClick={handleDownloadPayrollExcel}
                                disabled={downloadingPayroll || downloadingBarti}
                                style={{ 
                                    background: downloadingPayroll ? '#059669' : '#10b981', 
                                    color: 'white', 
                                    border: 'none', 
                                    padding: '9px 16px', 
                                    borderRadius: '8px', 
                                    fontSize: '13px', 
                                    fontWeight: '600', 
                                    cursor: (downloadingPayroll || downloadingBarti) ? 'not-allowed' : 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px', 
                                    boxShadow: '0 2px 5px rgba(16,185,129,0.2)',
                                    opacity: downloadingPayroll ? 0.8 : 1,
                                    transition: 'all 0.2s'
                                }}
                                title="Download Payroll Ledger Excel in BARTI Pune format"
                            >
                                {downloadingPayroll ? (
                                    <>
                                        <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                                        Generating Excel...
                                    </>
                                ) : (
                                    <>📥 Download Payroll Excel (BARTI Format)</>
                                )}
                            </button>

                            <button 
                                onClick={handleDownloadMonthlyBartiAttendance}
                                disabled={downloadingPayroll || downloadingBarti}
                                style={{ 
                                    background: downloadingBarti ? '#1d4ed8' : '#2563eb', 
                                    color: 'white', 
                                    border: 'none', 
                                    padding: '9px 16px', 
                                    borderRadius: '8px', 
                                    fontSize: '13px', 
                                    fontWeight: '600', 
                                    cursor: (downloadingPayroll || downloadingBarti) ? 'not-allowed' : 'pointer', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px', 
                                    boxShadow: '0 2px 5px rgba(37,99,235,0.2)',
                                    opacity: downloadingBarti ? 0.8 : 1,
                                    transition: 'all 0.2s'
                                }}
                                title="Download Monthly Day-wise Attendance Sheet in BARTI Pune format"
                            >
                                {downloadingBarti ? (
                                    <>
                                        <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
                                        Generating Sheet...
                                    </>
                                ) : (
                                    <>📊 Monthly Attendance Sheet (BARTI Format)</>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Formula Banner */}
                    <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '14px 18px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '14px', fontSize: '13px', color: '#1e40af', boxShadow: '0 2px 4px rgba(59,130,246,0.04)' }}>
                        <span style={{ fontSize: '22px' }}>📊</span>
                        <div>
                            <div style={{ fontWeight: '700', fontSize: '13px', color: '#1e3a8a', marginBottom: '2px' }}>Net Pay Formula:</div>
                            <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#2563eb' }}>
                                Base Salary &minus; (Absent Days &times; Daily Rate) &minus; (Half Days &times; 0.5 Daily Rate) &minus; Short Shift Cuts &minus; Late Penalties = Net Calculate Pay
                            </span>
                        </div>
                    </div>

                    <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '920px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Staff Name</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>FP ID</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Base Salary</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Days Present</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Absent Days</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Working Hours</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Calculate Pay</th>
                                    <th style={{ padding: '14px 18px', color: '#475569', fontWeight: '700', fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPayrollTeachers.map(teacher => {
                                    const baseSal = Number(teacher.salary) || 0;
                                    const netPayout = Math.round(teacher.monthPay || 0);
                                    const absentDeduction = Math.round(teacher.absentDeduction || 0);
                                    const halfDayDeduction = Math.round(teacher.halfDayDeduction || 0);
                                    const partialHoursDeduction = Math.round(teacher.partialHoursDeduction || 0);
                                    const latePenalty = Math.round(teacher.totalLatePenalty || 0);
                                    const absentDaysCount = teacher.absentDaysCount || 0;
                                    const halfDaysCount = teacher.halfDaysCount || 0;

                                    return (
                                        <tr key={teacher.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                                            <td style={{ padding: '16px 18px', fontWeight: '600', color: '#0f172a', fontSize: '14px' }}>
                                                <div>{teacher.name}</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '400' }}>{teacher.email || 'Staff Member'}</div>
                                            </td>
                                            <td style={{ padding: '16px 18px', fontSize: '13px' }}>
                                                <span style={{ background: '#f1f5f9', color: '#475569', padding: '4px 10px', borderRadius: '20px', fontWeight: '600', textTransform: 'capitalize' }}>{teacher.role}</span>
                                            </td>
                                            <td style={{ padding: '16px 18px', color: '#64748b', fontFamily: 'monospace', fontSize: '13px', fontWeight: '600' }}>#{teacher.fingerprint_id}</td>
                                            <td style={{ padding: '16px 18px', fontSize: '14px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span style={{ fontWeight: '600', color: '#0f172a' }}>₹{baseSal.toLocaleString()}</span>
                                                    <button onClick={() => { setEditingTeacher(teacher); setNewSalary(baseSal); }} style={{ padding: '3px 8px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', color: '#475569' }}>✏️ Edit</button>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 18px', color: '#1e293b', fontWeight: '600', fontSize: '14px' }}>
                                                <span style={{ color: '#16a34a' }}>{teacher.daysPresent} days</span>
                                            </td>
                                            <td style={{ padding: '16px 18px', fontSize: '14px' }}>
                                                <span style={{ color: absentDaysCount > 0 ? '#dc2626' : '#16a34a', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                    {absentDaysCount} {absentDaysCount > 0 ? '⚠️' : '✅'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '16px 18px', color: '#475569', fontSize: '13px', fontWeight: '500' }}>{teacher.workingHours || '—'}</td>
                                            <td style={{ padding: '16px 18px', fontSize: '13px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', minWidth: '180px' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#475569', fontSize: '12px' }}>
                                                        <span>Base Salary:</span>
                                                        <span style={{ fontWeight: '600', color: '#0f172a' }}>₹{baseSal.toLocaleString()}</span>
                                                    </div>
                                                    {absentDeduction > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#dc2626', fontSize: '12px' }}>
                                                            <span>Absent ({absentDaysCount}d):</span>
                                                            <span style={{ fontWeight: '600' }}>- ₹{absentDeduction.toLocaleString()}</span>
                                                        </div>
                                                    )}
                                                    {halfDayDeduction > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#d97706', fontSize: '12px' }}>
                                                            <span>Half Day ({halfDaysCount}d):</span>
                                                            <span style={{ fontWeight: '600' }}>- ₹{halfDayDeduction.toLocaleString()}</span>
                                                        </div>
                                                    )}
                                                    {partialHoursDeduction > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#ea580c', fontSize: '12px' }}>
                                                            <span>Short Shift Cut:</span>
                                                            <span style={{ fontWeight: '600' }}>- ₹{partialHoursDeduction.toLocaleString()}</span>
                                                        </div>
                                                    )}
                                                    {latePenalty > 0 && (
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', color: '#b45309', fontSize: '12px' }}>
                                                            <span>Late Penalty:</span>
                                                            <span style={{ fontWeight: '600' }}>- ₹{latePenalty.toLocaleString()}</span>
                                                        </div>
                                                    )}
                                                    <div style={{ borderTop: '1.5px solid #cbd5e1', paddingTop: '4px', marginTop: '2px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <span style={{ fontWeight: '700', fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Net Pay:</span>
                                                        <span style={{ fontWeight: '800', fontSize: '15px', color: netPayout >= baseSal * 0.7 ? '#15803d' : '#b91c1c' }}>₹{netPayout.toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '16px 18px', fontSize: '14px' }}>
                                                <button onClick={() => handlePayoutSalary(teacher)} style={{ padding: '8px 14px', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px', boxShadow: '0 2px 4px rgba(37,99,235,0.2)', transition: 'all 0.15s ease' }}>
                                                    Payout Salary
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: Daily Expenses */}
            {tab === 'daily-expenses' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '30px' }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#1e293b' }}>Log Daily Operational Expense</h3>
                        <form onSubmit={(e) => handleAddExpense(e, 'daily')} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Expense Item Description*</label>
                                <input type="text" placeholder="e.g. Marker Pens, Tea / Milk Pantry, Printing paper" value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} required />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Amount Spent (₹)*</label>
                                    <input type="number" placeholder="₹" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} required />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Date</label>
                                <input type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <button type="submit" style={{ padding: '12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>Record Daily Expense</button>
                        </form>
                    </div>

                    <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Daily Expenses Ledger</h3>
                            <span style={{ fontSize: '14px', color: '#b91c1c', fontWeight: 'bold' }}>Total Spent: ₹{dailyExpensesTotal.toLocaleString()}</span>
                        </div>

                        <div style={{ overflowY: 'auto', maxHeight: '420px' }}>
                            {dateFilteredExpenses.filter(e => e.category === 'daily').length === 0 ? (
                                <div style={{ textShadow: 'none', color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No daily expenses logged.</div>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {dateFilteredExpenses.filter(e => e.category === 'daily').map(exp => (
                                        <li key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f1f5f9', background: '#fafafb', borderRadius: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>{exp.description}</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>Date: {exp.date ? new Date(exp.date).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <span style={{ color: '#b91c1c', fontWeight: '700', fontSize: '14px' }}>-₹{(Number(exp.amount) || 0).toLocaleString()}</span>
                                                <button onClick={() => handleDeleteExpense(exp.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>🗑️</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TAB CONTENT: Monthly Expenses */}
            {tab === 'monthly-expenses' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '30px' }}>
                    <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <h3 style={{ margin: '0 0 20px', fontSize: '18px', color: '#1e293b' }}>Log Monthly Recurring Expense</h3>
                        <form onSubmit={(e) => handleAddExpense(e, 'monthly')} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Expense Title / Description*</label>
                                <input type="text" placeholder="e.g. Rent for Main Building, Salaries, Software Payouts" value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} required />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Amount Spent (₹)*</label>
                                    <input type="number" placeholder="₹" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} required />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', color: '#475569', fontWeight: '600', marginBottom: '6px' }}>Month Recorded</label>
                                <input type="date" value={newExpense.date} onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <button type="submit" style={{ padding: '12px', background: '#991b1b', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}>Record Monthly Payout</button>
                        </form>
                    </div>

                    <div style={{ background: 'white', padding: '24px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>Monthly Expenses Ledger</h3>
                            <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: 'bold' }}>Total Spent: ₹{monthlyExpensesTotal.toLocaleString()}</span>
                        </div>

                        <div style={{ overflowY: 'auto', maxHeight: '420px' }}>
                            {dateFilteredExpenses.filter(e => e.category === 'monthly').length === 0 ? (
                                <div style={{ textShadow: 'none', color: '#94a3b8', fontSize: '13px', textAlign: 'center', padding: '20px' }}>No monthly expenses logged.</div>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    {dateFilteredExpenses.filter(e => e.category === 'monthly').map(exp => (
                                        <li key={exp.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', borderBottom: '1px solid #f1f5f9', background: '#fafafb', borderRadius: '6px', marginBottom: '6px' }}>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>{exp.description}</div>
                                                <div style={{ fontSize: '11px', color: '#94a3b8' }}>Month: {exp.date ? new Date(exp.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}</div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                <span style={{ color: '#991b1b', fontWeight: '700', fontSize: '14px' }}>-₹{(Number(exp.amount) || 0).toLocaleString()}</span>
                                                <button onClick={() => handleDeleteExpense(exp.id)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px' }}>🗑️</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* COLLECT PAYMENT MODAL */}
            {payingStudent && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '420px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ margin: '0 0 10px' }}>Collect Student Fee</h3>
                        <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px' }}>Student: <strong>{payingStudent.name}</strong></p>
                        {modalSuccessMsg ? (
                            <div style={{ background: '#dcfce7', border: '1px solid #86efac', color: '#166534', padding: '16px', borderRadius: '10px', fontWeight: 'bold', fontSize: '14px', textAlign: 'center', margin: '15px 0' }}>
                                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🎉</div>
                                <div>{modalSuccessMsg}</div>
                            </div>
                        ) : (
                        <form onSubmit={handleCollectPayment} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Total Course Fee (₹)</label>
                                <input 
                                    type="number" 
                                    min="1" 
                                    placeholder="Enter total course fee" 
                                    value={totalFeeInput !== '' ? totalFeeInput : (payingStudent.fee || '')} 
                                    onChange={(e) => setTotalFeeInput(e.target.value)} 
                                    style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }} 
                                    required 
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Amount Paid Now (₹)</label>
                                <input type="number" min="1" placeholder="Enter amount to pay" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }} required />
                                {payingStudent && (
                                    <span style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                                        Remaining Due After Payment: ₹{Math.max(0, (Number(totalFeeInput || payingStudent.fee || 0)) - ((Number(payingStudent.amountReceived) || 0) + (Number(paymentAmount) || 0))).toLocaleString()}
                                    </span>
                                )}
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Payment Mode</label>
                                <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }}>
                                    <option value="Cash">Cash</option>
                                    <option value="UPI">UPI / GPay</option>
                                    <option value="Cheque">Cheque</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Receipt / TXN ID (Optional)</label>
                                <input type="text" placeholder="Auto-generated if left blank" value={txnId} onChange={(e) => setTxnId(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setPayingStudent(null)} style={{ padding: '10px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                                <button type="submit" style={{ padding: '10px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Confirm Payment</button>
                            </div>
                        </form>
                        )}
                    </div>
                </div>
            )}

            {/* EDIT BASE SALARY MODAL */}
            {editingTeacher && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ background: 'white', padding: '30px', borderRadius: '12px', width: '400px', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ margin: '0 0 10px' }}>Set Staff Monthly Base Salary</h3>
                        <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: '14px' }}>Staff Name: <strong>{editingTeacher.name}</strong></p>
                        <form onSubmit={handleUpdateSalary} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '6px' }}>Base Salary (₹ / Month)</label>
                                <input type="number" value={newSalary} onChange={(e) => setNewSalary(e.target.value)} style={{ padding: '10px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }} required />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                                <button type="button" onClick={() => setEditingTeacher(null)} style={{ padding: '10px 16px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                                <button type="submit" style={{ padding: '10px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Update Base Salary</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};


function App() {
    const [token, setToken] = useState(sessionStorage.getItem('token'));
    let initialUser = null;
    try { initialUser = JSON.parse(sessionStorage.getItem('user')); } catch (e) { }
    const [user, setUser] = useState(initialUser);

    const login = (t, u) => { 
        sessionStorage.setItem('token', t); 
        sessionStorage.setItem('user', JSON.stringify(u)); 
        setToken(t); 
        setUser(u); 
    };
    const logout = () => { 
        sessionStorage.clear(); 
        localStorage.clear(); 
        setToken(null); 
        setUser(null); 
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated: !!token, user, login, logout }}>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                {!token || !user ? (
                    <Routes>
                        <Route path="*" element={<Login />} />
                    </Routes>
                ) : (
                    <Routes>
                        <Route path="/" element={<ProtectedRoute><Layout logout={logout} user={user}><Dashboard /></Layout></ProtectedRoute>} />
                        <Route path="/attendance" element={<ProtectedRoute><Layout logout={logout} user={user}><AttendancePage /></Layout></ProtectedRoute>} />
                        <Route path="/admissions" element={<ProtectedRoute><Layout logout={logout} user={user}><AdmissionsPage /></Layout></ProtectedRoute>} />
                        <Route path="/bulk-upload" element={<ProtectedRoute><Layout logout={logout} user={user}><BulkUploadPage /></Layout></ProtectedRoute>} />
                        <Route path="/enroll-student" element={<ProtectedRoute><Layout logout={logout} user={user}><EnrollStudentPage /></Layout></ProtectedRoute>} />
                        <Route path="/quick-enroll" element={<ProtectedRoute><Layout logout={logout} user={user}><QuickEnrollPage /></Layout></ProtectedRoute>} />
                        <Route path="/birthdays" element={<ProtectedRoute><Layout logout={logout} user={user}><BirthdaysPage /></Layout></ProtectedRoute>} />
                        <Route path="/teachers" element={<ProtectedRoute><Layout logout={logout} user={user}><TeachersPage /></Layout></ProtectedRoute>} />
                        <Route path="/devices" element={<ProtectedRoute><Layout logout={logout} user={user}><DevicesPage /></Layout></ProtectedRoute>} />
                        <Route path="/simulator" element={<ProtectedRoute><Layout logout={logout} user={user}><Simulator /></Layout></ProtectedRoute>} />
                        <Route path="/enquiries/new" element={<ProtectedRoute><Layout logout={logout} user={user}><NewEnquiryPage /></Layout></ProtectedRoute>} />
                        <Route path="/student/:id" element={<ProtectedRoute><Layout logout={logout} user={user}><StudentProfilePage /></Layout></ProtectedRoute>} />
                        <Route path="/teacher/:id" element={<ProtectedRoute><Layout logout={logout} user={user}><TeacherProfilePage /></Layout></ProtectedRoute>} />
                        <Route path="/finances/cleared-fees" element={<ProtectedRoute><Layout logout={logout} user={user}><FinancesPage activeTab="cleared-fees" /></Layout></ProtectedRoute>} />
                        <Route path="/finances/income" element={<ProtectedRoute><Layout logout={logout} user={user}><FinancesPage activeTab="income" /></Layout></ProtectedRoute>} />
                        <Route path="/finances/salary" element={<ProtectedRoute><Layout logout={logout} user={user}><FinancesPage activeTab="salary" /></Layout></ProtectedRoute>} />
                        <Route path="/finances/daily-expenses" element={<ProtectedRoute><Layout logout={logout} user={user}><FinancesPage activeTab="daily-expenses" /></Layout></ProtectedRoute>} />
                        <Route path="/finances/monthly-expenses" element={<ProtectedRoute><Layout logout={logout} user={user}><FinancesPage activeTab="monthly-expenses" /></Layout></ProtectedRoute>} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                )}
            </Router>
        </AuthContext.Provider>
    );
}
export default App;
